/**
 * ============================================================================
 * BILINGUAL RULES — StoicOs translation dictionary (EN/NL)
 * ============================================================================
 * 1. EVERY key exists in BOTH `en` and `nl`, with a non-empty value. No
 *    exceptions — EN/NL parity is enforced by /app/scripts/check-translations.js
 *    (run `yarn lint:translations`; it MUST pass before every deployment).
 * 2. Never ship user-facing content in one language. DB drafts may be EN-only
 *    (the auto-translate pipeline fills NL), but nothing transitions to
 *    `published` while a required NL field is empty — the backend returns 422.
 * 3. Add a new key here in BOTH `en` and `nl`, then reference it via
 *    t('that.key') in JSX. Do not change the structure of this file
 *    ({ en: {...}, nl: {...} } with flat dot-notation keys).
 * 4. Stoic quotes themselves (Marcus, Seneca, Epictetus) remain in their
 *    original English in both dictionaries — they are philosophical canon;
 *    reflections and practices are translated.
 * 5. In NL mode, DB content that falls back to EN shows the visitor a notice
 *    (TranslationFallbackNotice) — the fallback is never silent.
 * ============================================================================
 */
export const translations = {
  en: {
    // ---------------- NAV / FOOTER ----------------
    "nav.home": "Home", "nav.about": "About", "nav.support": "Support", "nav.privacy": "Privacy",
    "nav.virtueguide": "Virtue Guide", "nav.newsletter": "Newsletter",
    "newsletter.cta.title": "The StoicOs.ai Newsletter",
    "newsletter.cta.intro": "Practical Stoic wisdom, new Living Library articles and Academy insights — thoughtfully delivered. No spam, unsubscribe at any time.",
    "newsletter.cta.button": "Receive the newsletter",
    "newsletter.cta.close": "Close",
    "home.frieze": "KNOW THYSELF",
    "home.my.title": "Create your free My StoicOs",
    "home.my.intro": "My StoicOs is your personal place within the Stoa.",
    "home.my.access": "Your free account gives you access to:",
    "home.my.b1": "your personal learning environment",
    "home.my.b2": "the Academy",
    "home.my.b3": "the Stoic Journal",
    "home.my.b4": "the Living Library",
    "home.my.b5": "the Stoic mentors",
    "home.my.b6": "your saved reflections",
    "home.my.b7": "your learning progress",
    "home.my.b8": "synchronisation between StoicOs.ai and the Virtue Guide app",
    "home.my.secure": "Everything is securely stored so you can continue your journey within the Stoa on any device.",
    "home.my.cta": "Create my free StoicOs",
    "home.discover.title": "Would you like to discover more first?",
    "home.discover.intro1": "Every journey begins differently.",
    "home.discover.intro2": "Perhaps you would first like to discover the history of the Stoa or explore Stoic philosophy in greater depth.",
    "home.discover.agora.title": "I would like to learn more about the Stoics",
    "home.discover.agora.body": "Discover how the Stoa developed over more than 2,300 years, from Zeno of Citium to Marcus Aurelius.",
    "home.discover.agora.cta": "Visit the Agora",
    "home.discover.journal.title": "I would like to read more about Stoic philosophy",
    "home.discover.journal.body": "Explore articles, essays, reflections and practical applications of Stoicism in everyday life.",
    "home.discover.journal.cta": "Open the Stoic Journal",
    "home.cards.eyebrow": "StoicOs Cards",
    "home.cards.title": "Stoicism, one idea at a time.",
    "home.cards.body": "Explore Stoic ideas through visual cards about judgment, virtue, control, character and Inner Freedom.",
    "home.cards.cta": "Explore the StoicOs Cards",
    "agora.next.title": "I would like to take this further",
    "agora.next.p1": "Have the stories and life lessons of Marcus Aurelius, Epictetus, Seneca and Hipparchia awakened your interest?",
    "agora.next.p2": "Perhaps you recognise the feeling: this speaks to me, and I wish to explore it more deeply.",
    "agora.next.p3": "Within My StoicOs you learn, step by step, how to apply the Stoic life lessons in everyday life. You become acquainted with the three classical disciplines of the Stoa: Logic, Physics and Ethics.",
    "agora.next.p4": "Not merely to know more about the philosophy, but to learn to think more clearly, to choose more consciously and to act more steadfastly when life puts you to the test.",
    "nav.download": "Download",
    "nav.journal": "Stoic Journal",
    "nav.stoicJournal": "The Stoic Journal",
    "nav.agoraMain": "Agora",
    "nav.aboutUs": "About Us",
    "nav.mystoicos": "My StoicOs",
    "my.eyebrow": "My StoicOs",
    "my.welcome": "Welcome back",
    "my.traveler": "Traveler",
    "my.subtitle": "Your path of study, practice and reflection.",
    "my.journalCta": "Continue your Stoic Journal",
    "my.recFirst": "Recommended first step",
    "my.startLogic": "Start with Logic",
    "my.logicSub": "Learn to see clearly before you judge, choose and act.",
    "my.beginLogic": "Begin Logic",
    "my.module.library": "Living Library",
    "my.module.library.desc": "The collected wisdom of the Stoa — essays, terms and sources.",
    "my.settings.replayDesc": "Watch the cinematic introduction to the Academy again.",
    "my.aspirant": "Aspirant Member",
    "my.memberSince": "Member Since",
    "my.introDay": "Introduction · Day {day} of 7",
    "my.todaysPractice": "Today's Practice",
    "my.morning": "Morning Reflection",
    "my.practice": "Daily Practice",
    "my.evening": "Evening Reflection",
    "my.freeLabel": "Free Writing",
    "my.done": "Written",
    "my.open": "Open",
    "my.virtueFocus": "Current Virtue Focus",
    "my.virtue.Wisdom": "Wisdom",
    "my.virtue.Courage": "Courage",
    "my.virtue.Justice": "Justice",
    "my.virtue.Temperance": "Temperance",
    "my.streak": "Reflection Streak",
    "my.streakDays": "{n} days",
    "my.streakDay": "1 day",
    "my.daysPracticed": "days practiced",
    "my.inspiration": "Daily Inspiration",
    "my.recent": "Recent Activity",
    "my.noActivity": "Your journal is still empty. Begin with this morning's reflection.",
    "my.recommendations": "Recommended Next",
    "my.rec.logic-lesson-1": "Begin the Logic course",
    "my.rec.library-visit": "Visit the Living Library",
    "my.modules": "Your Study",
    "my.module.journal": "My Journal",
    "my.module.journal.desc": "Morning and evening reflection, daily practice and free writing.",
    "my.module.academy": "My Academy",
    "my.module.academy.desc": "Course progress, completed lessons, certificates and bookmarks.",
    "my.module.journeys": "My Journeys",
    "my.module.journeys.desc": "Current and completed Journeys with personal notes.",
    "my.module.virtues": "My Virtues",
    "my.module.virtues.desc": "Development in Wisdom, Courage, Justice and Temperance.",
    "my.module.mentors": "Mentors",
    "my.module.mentors.desc": "Marcus Aurelius, Epictetus, Seneca and Hipparchia.",
    "my.module.progress": "My Progress",
    "my.module.progress.desc": "Reflection history, statistics, streaks and milestones.",
    "my.module.settings": "Settings",
    "my.module.settings.desc": "Profile, language, notifications, privacy and membership.",
    "my.inPreparation": "In preparation",
    "my.logout": "Sign out",
    "join.eyebrow": "The Academy of Practical Wisdom",
    "join.title": "Join the Journey",
    "join.loginTitle": "Continue Your Journey",
    "join.welcomeNote": "After your free registration you get immediate access to the first lessons of Logic, Ethics and Physics inside My StoicOs.",
    "join.support": "Build character through daily practice, timeless philosophy and the guidance of the great Stoic mentors.",
    "join.name": "Your name",
    "join.email": "Email",
    "join.password": "Password (min. 8 characters)",
    "join.registerCta": "Join the Journey",
    "join.loginCta": "Continue Your Journey",
    "join.haveAccount": "Continue Your Journey",
    "join.newHere": "New here? Join the journey",
    "trial.cta": "Start My Free Introduction",
    "trial.hero.headline": "Start Your Free Introduction",
    "trial.hero.subtitle": "Create a free My StoicOs account and get access to the first lessons of Logic, Ethics and Physics. Discover the Academy of Practical Wisdom and begin your journey.",
    "trial.hero.secondary": "I already have an account",
    "trial.locked.title": "This lesson becomes available once full membership goes live.",
    "trial.locked.body": "You have access to the introduction lessons of Logic, Ethics and Physics. The full curriculum becomes available once membership of the Academy of Practical Wisdom launches.",
    "trial.locked.back": "Back to My StoicOs",
    "trial.locked.previewBody": "The full curriculum preview stays open to everyone. Create your free account to open the introduction lessons, keep your Stoic Journal and follow your progress — or sign in if you are already a student.",
    "trial.academy.note": "The introduction lessons are meant as a first acquaintance with the Academy. The full study programme becomes available once membership goes live.",
    "ceremony.title": "Welcome to the Academy",
    "ceremony.body1": "Welcome, Aspirant Member.",
    "ceremony.body2": "Your membership has been recorded.",
    "ceremony.memberNumber": "Member Number",
    "ceremony.memberSince": "Member Since",
    "ceremony.firstStep": "Today you take your first step toward Inner Freedom.",
    "ceremony.mentorsTitle": "Meet Your Mentors",
    "ceremony.enter": "Enter My StoicOs",
    "ceremony.continueHint": "Tap to continue",
    "onb.scene": "Scene {n} of {total}",
    "onb.next": "Next",
    "onb.previous": "Previous",
    "onb.skip": "Skip",
    "onb.scene6Title": "The Academy Entrance",
    "onb.scene6Note": "This scene is reserved. The entrance of the Academy will be revealed here.",
    "onb.back": "Back",
    "onb.continue": "Continue",
    "onb.ch1.title": "Welcome, Traveler",
    "onb.ch1.desc": "You are welcomed in the Grand Hall of the Academy of Practical Wisdom.",
    "onb.ch2.title": "Marcus Aurelius",
    "onb.ch2.desc": "Hipparchia introduces the philosopher-emperor — he ruled an empire, yet first mastered himself.",
    "onb.ch3.title": "Epictetus",
    "onb.ch3.desc": "Born a slave, he became the freest of men. His staff knows the long road of endurance.",
    "onb.ch4.title": "Seneca",
    "onb.ch4.desc": "Statesman and writer — he turned fortune's storms into letters of wisdom.",
    "onb.ch5.title": "Your Four Mentors",
    "onb.ch5.desc": "Four voices, one path. Your mentors await you in the golden hall.",
    "onb.ch6.title": "Your Journey Begins Here",
    "onb.ch6.desc": "Enter the Academy of Practical Wisdom and begin the practice of becoming who you are capable of being.",
    "onb.enter": "Enter the Academy",
    "onb.skipIntro": "Skip introduction",
    "onb.begin": "Begin the Journey",
    "onb.sound.mute": "Mute narration",
    "onb.sound.unmute": "Unmute narration",
    "onb.replay": "Replay narration",
    "onb.volume": "Volume",
    "my.replayIntro": "Replay Introduction",
    "journal.my.title": "My Journal",
    "journal.my.subtitle": "A daily philosophical practice — the living memory of your development.",
    "journal.my.morningPrompt": "What will today ask of me? Which virtue will I practise deliberately?",
    "journal.my.practicePrompt": "What did I practise today? Where did I act according to my principles — and where not?",
    "journal.my.eveningPrompt": "What did I do well? What could I have done better? What remains undone?",
    "journal.my.free": "Free Writing",
    "journal.my.freeNew": "New page",
    "journal.my.freeTitlePh": "Title (optional)",
    "journal.my.writePh": "Write here…",
    "journal.my.saving": "Saving…",
    "journal.my.saved": "Saved",
    "journal.my.delete": "Delete",
    "journal.my.back": "Back to My StoicOs",
    "journal.my.empty": "No free pages yet.",
    "journal.my.freeLabel": "Free Entry",
    "journal.my.freePrompt": "Write freely — a thought, an observation, a letter to yourself.",
    "my.journal.title": "My Journal",
    "my.journal.savedIn": "Saved to My Journal",
    "my.journal.viewIn": "Open My Journal",
    "my.journal.newFree": "New free entry",
    "my.journal.freeTitlePh": "Title (optional)",
    "my.journal.searchPh": "Search your journal…",
    "my.journal.allDates": "All dates",
    "my.journal.filterAll": "All",
    "my.journal.entriesCount": "entries",
    "my.journal.empty": "No entries yet. Write your first reflection today.",
    "my.journal.deleteConfirm": "Delete this entry?",
    "my.journal.deleteYes": "Delete",
    "my.journal.cancel": "Cancel",
    "my.journal.back": "Back",
    "my.journal.tabToday": "Today",
    "my.journal.tabArchive": "Archive",
    "my.journal.tabMentor": "My Mentor Reviews",
    "my.mentor.title": "My Mentor Reviews",
    "my.mentor.empty": "No mentor reviews received yet.",
    "my.mentor.info": "Future mentor responses will appear here and remain linked to the original entry in My Journal.",
    "my.journal.calendar": "Calendar",
    "my.journal.todayBtn": "Today",
    "join.forgotPassword": "Forgot password?",
    "nav.cards": "Training Cards",
    "nav.insights": "My Insights",
    "nav.books": "My Books",
    "nav.share": "Share Your Thoughts",
    "nav.virtueguide": "Virtue Guide",
    "nav.agora": "The Agora",
    "nav.watch": "Watch",

    // ---------------- SHARE YOUR THOUGHTS ----------------
    "share.eyebrow": "Letters to the Stoa",
    "share.title1": "Share",
    "share.title2": "Your Thoughts",
    "share.headline": "Share Your Thoughts",
    "share.lede": "A modern continuation of the correspondence between Seneca and his friend Lucilius.",
    "share.dropCap": "Q",
    "share.p1": "For nearly two thousand years, Seneca's letters to Lucilius have remained one of the gentlest invitations in philosophy: a quiet, unhurried exchange between two minds searching for the good life.",
    "share.p2": "StoicOs.ai is built in that same spirit. Less a product to be marketed than a conversation to be entered.",
    "share.p3": "If something on these pages sparks a question, a reflection, a memory, or simply a thought you would like to set down, write. I read every letter personally.",
    "share.emailLabel": "Write to the founder",
    "share.reassurance": "Every message is read personally. While I may not be able to respond to every message immediately, your thoughts are always appreciated.",
    "share.signature.name": "Ed Korporaal",
    "share.signature.role": "Founder, StoicOs.ai",
    "share.form.title": "Write a Letter to the Stoa",
    "share.form.subtitle": "A modern correspondence. Your message arrives directly with the founder.",
    "share.form.nameLabel": "Name (optional)",
    "share.form.namePlaceholder": "Lucilius",
    "share.form.emailLabel": "Email (optional)",
    "share.form.emailPlaceholder": "you@example.com",
    "share.form.messageLabel": "Your Thoughts",
    "share.form.messagePlaceholder": "Write freely: a question, a reflection, a suggestion…",
    "share.form.send": "Send Message",
    "share.form.sending": "Sending…",
    "share.form.privacy": "Read by one person. Never shared.",
    "share.form.errEmpty": "Please write a message before sending.",
    "share.form.errSend": "Something interrupted the delivery. Please try again, or write directly to ek@stoicos.ai.",
    "share.form.successTitle": "Thank you. Your message has been sent to Ed at ek@stoicos.ai.",
    "share.form.successBody": "Every letter is read personally. Reply may take a little while, but it will come.",
    "share.form.savedTitle": "Your message has been saved.",
    "share.form.savedBody": "Email delivery is currently unavailable, so I haven't been notified by email yet, but your message is safely stored and will be read. You may also write directly to ek@stoicos.ai.",
    "share.form.deliveryNotice": "Messages are sent directly to:",
    "share.form.writeAgain": "Write another message",
    "footer.tagline": "StoicOs builds tools for the examined life. Virtue Guide is our first companion, an AI-guided dialogue with the wisdom of Rome.",
    "footer.knowThyself": "Know Thyself",
    "footer.company": "Company", "footer.aboutLink": "About StoicOs",
    "footer.product": "Virtue Guide",
    "footer.ios": "Download for iOS", "footer.android": "Download for Android",
    "footer.rights": "All rights reserved.",

    // ---------------- HERO ----------------
    "hero.eyebrow": "StoicOs.ai",
    "hero.title1": "Know", "hero.title2": "Thyself",
    "hero.sub1": "Develop wisdom.",
    "hero.sub2": "Strengthen character.",
    "hero.sub3": "Build your inner citadel.",
    "hero.body": "Virtue Guide is a product of StoicOs.ai. An AI-powered guide for self-knowledge, character development and practical wisdom inspired by the great Stoic thinkers.",
    "hero.bodyLead": "Virtue Guide is a product of",
    "hero.bodyTail": "An AI-powered guide for self-knowledge, character development and practical wisdom inspired by Marcus Aurelius, Seneca and Epictetus.",
    "hero.ctaPrimary": "Begin the Journey",
    "hero.ctaSecondary": "Enter the Agora",
    "hero.alt": "Sunrise over the temple of Delphi inscribed \u0393\u039D\u03A9\u0398\u0399 \u03A3\u0395\u0391\u03A5\u03A4\u039F\u039D, \u201CKnow Thyself\u201D. A lone traveller walks the stone path toward the temple. To the left, a banner of Wisdom (owl) above Justice (scales); to the right, banners of Courage (lion) above Temperance (chalice). Stone busts of Marcus Aurelius, Seneca and Epictetus rise in the clouds above the pediment as the sun rises behind the mountains.",
    "hero.subtitle": "A pocket-sized companion for the examined life. Speak with Marcus Aurelius, Seneca and Epictetus, and rediscover what it means to live well.",
    "hero.statusBeta": "Now in private beta",
    "hero.statusRome": "Designed in Rome",
    "hero.scroll": "Scroll",

    // ---------------- COMING SOON TRAILER ----------------
    "coming.eyebrow": "Now Available",
    "coming.title1": "Virtue Guide",
    "coming.title2": "is Now Available",
    "coming.subtitle": "A new way to explore Stoic philosophy through AI-guided reflection.",
    "coming.description": "Discover how timeless Stoic wisdom meets modern Artificial Intelligence. Virtue Guide is designed to help people reflect, question their judgments, and strengthen their character through conversations with history's greatest Stoic teachers.",
    "coming.video.trailer.eyebrow": "Official Trailer",
    "coming.video.trailer.title": "Virtue Guide: The Official Trailer",
    "coming.video.trailer.caption": "A first glimpse of the journey.",
    "coming.cta.heading": "Be among the first to experience Virtue Guide",
    "coming.cta.text": "Begin your Stoic journey today.\nDownload Virtue Guide now — available on the Apple App Store and Google Play.",
    "coming.available.note": "The film above is our original launch trailer — the launch it announces has since taken place.",
    "coming.available.line": "Virtue Guide is now available on the Apple App Store and Google Play.",
    "coming.comingSoon": "Coming Soon",
    "coming.apple.line1": "On the App Store",
    "coming.apple.line2": "Download",
    "coming.google.line1": "On Google Play",
    "vgs.eyebrow": "Official Launch — The Mobile Companion",
    "vgs.title1": "Your Stoic journey,",
    "vgs.title2": "wherever you are",
    "vgs.body": "My StoicOs is your personal account and learning environment on StoicOs.ai. Virtue Guide is your mobile companion app — daily reflection, guidance and conversations with the great Stoic teachers, wherever life takes you.",
    "vgs.sync": "Everything stays in sync: your progress, journal and reflections travel seamlessly between StoicOs.ai and Virtue Guide.",
    "vgs.trust": "Officially available in the Apple App Store and Google Play Store.",
    "fw.eyebrow": "The First Hundred",
    "fw.title1": "Founders",
    "fw.title2": "Wall of Honor",
    "fw.subtitle": "Every meaningful journey begins with a few people who choose to believe before the destination is visible.",
    "fw.body1": "The first 100 Founding Members of StoicOs.ai will receive a permanent place on this Wall of Honor.",
    "fw.body2": "Founding Membership is completely free. Recognition cannot be purchased.",
    "fw.body3": "It is earned simply by becoming one of the first 100 registered members.",
    "fw.counterLabel": "Founding Members",
    "fw.remaining": "{n} places remaining",
    "fw.empty": "The first names will be inscribed here soon.",
    "fw.cta.heading": "Become one of the First 100 Founding Members",
    "fw.cta.b1": "Free forever",
    "fw.cta.b2": "Permanent recognition",
    "fw.cta.b3": "Privacy protected",
    "fw.cta.b4": "Help build StoicOs.ai from the beginning",
    "fw.cta.button": "Create my free StoicOs account",
    "join.fw.title": "Founders Wall of Honor",
    "join.fw.q": "If you become one of the first 100 Founding Members, may we display you on the public Founders Wall of Honor?",
    "join.fw.yes": "Yes — display my first name and surname initial (for example: Daniel R.)",
    "join.fw.no": "No — do not display my name on the Founders Wall of Honor",
    "fwModal.eyebrow": "Founders Wall of Honor",
    "fwModal.title": "Welcome, Founding Member",
    "fwModal.body": "You are one of the first 100 members of StoicOs.ai. Choose whether you appear on the public Founders Wall of Honor — only your first name and surname initial can ever be shown.",
    "fwModal.save": "Save my choice",
    "fwModal.later": "Decide later",
    "my.fw.title": "Privacy — Founders Wall of Honor",
    "my.fw.badge": "Founding Member {number}",
    "my.fw.desc": "Choose whether your name appears on the public Founders Wall of Honor. Only your first name and surname initial can ever be shown. Your place on the Wall follows the order of registration.",
    "my.fw.saved": "Preference saved.",
    "coming.watchLibrary": "Watch the full library",

    // ---------------- WATCH (Video library) ----------------
    "watch.eyebrow": "The Library",
    "watch.title": "Watch",
    "watch.lede": "A living archive of the StoicOs voice.",
    "watch.intro": "Trailers, voices of the Stoic teachers, tutorials, and quiet product updates. New videos are added as the project grows. There is no schedule, only the work.",
    "watch.browseLabel": "Browse by",
    "watch.cat.all": "All",
    "watch.cat.trailer": "Official Trailer",
    "watch.cat.marcus": "Marcus Aurelius",
    "watch.cat.seneca": "Seneca",
    "watch.cat.epictetus": "Epictetus",
    "watch.cat.cato": "Cato",
    "watch.cat.tutorials": "Tutorials",
    "watch.cat.updates": "Product Updates",
    "watch.empty.eyebrow": "Coming Soon",
    "watch.empty.title": "Nothing here yet.",
    "watch.empty.body": "This shelf of the library is still being filled. Return soon, or browse another category above.",
    "watch.video.trailer.eyebrow": "Official Trailer",
    "watch.video.trailer.title": "Virtue Guide: The Official Trailer",
    "watch.video.trailer.caption": "A first glimpse of the journey. An AI-guided dialogue with the wisdom of Rome, designed for the examined life.",

    // ---------------- MISSION ----------------
    "mission.eyebrow": "Our Mission",
    "mission.headline1": "We are returning philosophy to the place it has always belonged:",
    "mission.headline2": "the quiet hours of an ordinary day.",
    "mission.body": "StoicOs builds operating systems for the inner life. Virtue Guide, our first companion, uses AI to bring you into conversation with the great Stoics, so that wisdom is no longer kept on a shelf, but carried with you, like a coin in your pocket.",

    // ---------------- PHILOSOPHY MISSION (homepage premium panel) ----------------
    // Rendered by MissionSection.jsx between Hero and "Start Your Journey".
    // Editable from this file — each key is a discrete unit of copy so a
    // translator can adjust line breaks per locale.
    "philosophyMission.eyebrow": "Our Philosophy",
    "philosophyMission.headline.lead": "The world doesn\u2019t need more information.",
    "philosophyMission.headline.accent": "It needs more wisdom.",
    "philosophyMission.body.para1": "At StoicOS.ai, our mission is to cultivate wisdom and make it understandable, practical, and accessible to everyone.",
    "philosophyMission.body.para2": "We believe wisdom is not a gift reserved for a few, but a virtue that can be cultivated through reflection, reason, and daily practice.",
    "philosophyMission.body.para3": "By combining timeless philosophy with human-centered AI, we help people strengthen their judgment, build character, and live with greater purpose.",
    "philosophyMission.body.para4": "Because a better world begins with wiser individuals.",
    "philosophyMission.closing.line1": "Know Thyself.",
    "philosophyMission.closing.line2": "Cultivate Wisdom.",
    "philosophyMission.closing.line3": "Live with Virtue.",
    "philosophyMission.cta": "Start Your Journey",

    // ---------------- ACADEMY OF WISDOM ----------------
    "academy.nav": "Academy",
    "academy.hero.eyebrow": "The Academy",
    "academy.hero.title": "The Academy of Wisdom",
    "academy.hero.subtitle.lead": "Life is the Curriculum.",
    "academy.hero.subtitle.accent": "Virtue is the Goal.",
    "academy.hero.cta": "Begin Your Journey",
    "academy.hero.alt": "The entrance to the Academy of Practical Wisdom, StoicOs.ai",
    "academy.intro.title": "The Academy Introduction",
    "academy.intro.sub1": "Before you begin your journey,",
    "academy.intro.sub2": "take two minutes to experience the vision behind the Academy.",
    "academy.intro.video.coming": "Coming soon: The official Academy Introduction",
    "academy.intro.quote1": "Every journey begins with a single step.",
    "academy.intro.quote2": "Every life begins with a single choice.",

    // ---- Living Library (Phase 1 — foundation) ----
    "nav.library": "Living Library",
    "nav.ancientLibrary": "Ancient Library",
    "library.hero.eyebrow": "The Knowledge Foundation",
    "library.hero.title": "The Living Library",
    "library.hero.subtitle": "The world's growing repository of practical wisdom.",
    "library.hero.body1": "Preserving the wisdom of the past.",
    "library.hero.body2": "Connecting it to the present.",
    "library.hero.body3": "Preparing it for the future.",
    "library.hero.cta": "Begin Exploring",
    "library.hero.backAcademy": "Return to the Academy",
    "library.hero.imageAlt": "The Living Library: a monumental marble building on the campus of the Academy of Practical Wisdom",
    "library.hero.lead": "The central knowledge foundation of the Academy of Practical Wisdom.",
    "library.collections.title": "The Collections",
    "library.card.open": "Enter",
    "library.growing": "The Living Library is curated with care. New knowledge is added over time.",
    "library.category.emptyEyebrow": "In Curation",
    "library.category.empty": "This section is currently being curated as part of the Living Library.",
    "library.category.back": "Return to the Living Library",
    "library.search.title": "Search",
    "library.search.sub": "Find anything across the Living Library: philosophers, concepts, virtues, practices and more.",
    "library.search.placeholder": "Search the Living Library…",
    "library.search.button": "Search",
    "library.search.hint": "Enter a term to search the Living Library.",
    "library.search.empty": "No matching entries have been published yet.",
    "library.search.empty2": "The Living Library continues to grow.",
    "library.search.explore": "Explore the Collections",
    "library.seo.description": "Discover the Living Library of StoicOs.ai: a growing repository of practical wisdom, Stoic philosophy, original sources, modern research and lifelong learning.",
    "library.entry.relatedTitle": "Continue Your Journey",
    "library.journey.label": "Journey",
    "library.journey.soon": "In curation. This Journey will open soon.",
    "library.journey.return": "Return to The Living Library",
    "library.journey.wisdom.name": "Wisdom",
    "library.journey.wisdom.invite": "Begin where every virtue begins: with clear judgment.",
    "library.journey.courage.name": "Courage",
    "library.journey.courage.invite": "Transform wise judgment into courageous action.",
    "library.journey.justice.name": "Justice",
    "library.journey.justice.invite": "Learn how wisdom becomes service to others.",
    "library.journey.temperance.name": "Temperance",
    "library.journey.temperance.invite": "Learn how wisdom governs the self.",
    "library.journey.wisdom.greek": "Phronēsis",
    "library.journey.courage.greek": "Andreia",
    "library.journey.justice.greek": "Dikaiosynē",
    "library.journey.temperance.greek": "Sōphrosynē",
    "library.virtues.heroTitle": "The Virtues",
    "library.virtues.heroLine": "Character is not taught by words alone, but by practice.",
    "library.virtues.choose": "Choose a Virtue to Begin Your Journey",
    "library.rel.academy_lessons": "Academy Lessons",
    "library.rel.virtue_guide_conversations": "Virtue Guide Conversations",
    "library.rel.journal_articles": "Journal Articles",
    "library.rel.videos": "Videos",
    "library.rel.original_sources": "Original Sources",
    "library.rel.modern_research": "Modern Research",
    "library.rel.related_concepts": "Related Concepts",
    "library.rel.related_virtues": "Related Virtues",
    "library.rel.related_philosophers": "Related Philosophers",
    "library.rel.exercises": "Exercises",
    "library.rel.books": "Books",
    "library.cats.philosophers.title": "Philosophers",
    "library.cats.philosophers.desc": "The lives, voices and teachings of the Stoic masters.",
    "library.cats.concepts.title": "Concepts",
    "library.cats.concepts.desc": "The core ideas that structure Stoic thought.",
    "library.cats.virtues.title": "Virtues",
    "library.cats.virtues.desc": "Wisdom, justice, courage and temperance: the four pillars.",
    "library.cats.practices.title": "Practices",
    "library.cats.practices.desc": "Daily exercises that turn philosophy into a way of life.",
    "library.cats.sources.title": "Original Sources",
    "library.cats.sources.desc": "The classical texts, in their own words.",
    "library.cats.history.title": "History",
    "library.cats.history.desc": "From the painted porch of Athens to the modern day.",
    "library.cats.research.title": "Modern Research",
    "library.cats.research.desc": "What contemporary science says about ancient practice.",
    "library.cats.leadership.title": "Leadership",
    "library.cats.leadership.desc": "Governing yourself first, then others.",
    "library.cats.relationships.title": "Relationships",
    "library.cats.relationships.desc": "Living wisely with the people entrusted to you.",
    "library.cats.health.title": "Health",
    "library.cats.health.desc": "Care of the body in service of the mind.",
    "library.cats.finance.title": "Finance",
    "library.cats.finance.desc": "Wealth, sufficiency and the wise use of fortune.",
    "library.cats.society.title": "Society",
    "library.cats.society.desc": "The citizen of the world and the common good.",
    "library.cats.glossary.title": "Glossary",
    "library.cats.glossary.desc": "Every term of the Stoic vocabulary, defined.",
    "library.cats.search.title": "Search",
    "library.cats.search.desc": "Find anything across the Living Library.",

    "academy.why.eyebrow": "Why another Academy?",
    "academy.why.p1": "Most education teaches people how to earn a living.",
    "academy.why.p2": "Very little teaches people how to live.",
    "academy.why.p3": "Knowledge is everywhere.",
    "academy.why.p4.lead": "Wisdom is",
    "academy.why.p4.accent": "rare.",
    "academy.why.p5": "The Academy exists to change that.",

    "academy.how.eyebrow": "How it Works",
    "academy.how.title": "One journey, six unfolding movements.",
    "academy.how.body": "The Academy does not teach subjects. It teaches a rhythm: the movement by which a lived moment becomes a virtue, and virtue becomes character.",
    "academy.how.step.experience":    "Experience",
    "academy.how.step.reflection":    "Reflection",
    "academy.how.step.understanding": "Understanding",
    "academy.how.step.virtue":        "Virtue",
    "academy.how.step.character":     "Character",
    "academy.how.step.wisdom":        "Wisdom",

    "academy.map.title": "The Map of Practical Wisdom",
    "academy.map.p1": "Every journey begins with understanding the whole.",
    "academy.map.p2": "The Map of Practical Wisdom offers a visual overview of the Stoic tradition as taught within the Academy of Practical Wisdom.",
    "academy.map.p3": "Each concept is rooted in the surviving classical Stoic sources. The visual organization is an original educational synthesis by StoicOs.ai, created to make Stoicism understandable, practical, and accessible.",
    "academy.map.alt": "The Map of Practical Wisdom: a visual overview of the Stoic tradition as taught within the Academy of Practical Wisdom",
    "academy.map.note.label": "Historical Note",
    "academy.map.note.text": "Every concept on this map is intended to be traceable to the surviving classical Stoic sources. The visual organization is an original educational model created by StoicOs.ai.",


    // ---------------- THE THREE DISCIPLINES OF STOICISM ----------------
    "academy.disc.eyebrow": "The Three Disciplines of Stoicism",
    "academy.disc.title": "One philosophy. Three inseparable disciplines.",
    "academy.disc.body": "The ancient Stoics never taught ethics in isolation. Their philosophy was a single living whole of three disciplines, compared by the old masters to a garden: Logic the wall that protects, Physics the soil that nourishes, Ethics the fruit for which the garden exists. The Traveller who enters this Academy discovers Stoicism exactly as a student entering the ancient Stoa would have discovered it.",
    "academy.disc.concept.logic": "Logic teaches how to think.",
    "academy.disc.concept.physics": "Physics teaches what reality is.",
    "academy.disc.concept.ethics": "Ethics teaches how to live.",
    "academy.disc.concept.close": "Only together do they form Stoicism.",
    "academy.disc.topicsLabel": "Future Learning Journeys",
    "academy.disc.mystoicos.title": "Follow these lessons in My StoicOs",
    "academy.disc.mystoicos.text": "Create your free My StoicOs and gain access to the first lessons in Logic, Physics and Ethics, your personal progress and saved reflections.",
    "academy.disc.start.logic": "Start Logic in My StoicOs",
    "academy.disc.start.physics": "Start Physics in My StoicOs",
    "academy.disc.start.ethics": "Start Ethics in My StoicOs",
    "academy.disc.freeAccount": "Free account required",

    "academy.disc.logic.title": "Stoic Logic",
    "academy.disc.logic.subtitle": "How We Come to Truth",
    "academy.disc.logic.classical": "Logikē",
    "academy.disc.logic.body": "Before a Stoic asks how to live, they ask how to know. Stoic Logic is the discipline of thinking itself: how an impression arrives, how judgment examines it, and when a mind may rightly say “true”. It teaches how a human being should think before acting.",
    "academy.disc.logic.topics": "Impressions (Phantasiai)|Assent (Synkatathesis)|Judgment|Reason|Knowledge|Truth|Wisdom versus Opinion|The Discipline of Thinking",
    "academy.disc.status.open": "Now Open",
    "academy.nav.switcherLabel": "Switch discipline",
    "academy.nav.lessonNavLabel": "Lesson navigation",
    "academy.nav.goTo.logic": "Go to Logic",
    "academy.nav.goTo.physics": "Go to Physics",
    "academy.nav.goTo.ethics": "Go to Ethics",
    "academy.nav.name.logic": "Logic",
    "academy.nav.name.physics": "Physics",
    "academy.nav.name.ethics": "Ethics",
    "academy.nav.active": "Active",
    "academy.nav.comingSoon": "Coming soon",
    "academy.nav.prevLesson": "Previous lesson",
    "academy.nav.nextLesson": "Next lesson",
    "academy.nav.lessonN": "Lesson {n}",
    "academy.nav.position": "Phase {phase} \u00b7 Lesson {lesson} of {total}",
    "academy.nav.backToLesson": "Back to lesson",
    "academy.nav.backToPhase": "Back to phase overview",
    "academy.lesson.virtueSeal": "Test your judgment by the four virtues.",
    "academy.nav.backToCourse": "Back to course overview",
    "academy.nav.resume": "Continue where I left off",
    "academy.nav.openCourseFirst": "Open course",
    "academy.nav.reflectionSaved": "Reflection saved",
    "academy.nav.lessonCompleted": "Lesson completed",
    "academy.nav.status.notStarted": "Not started",
    "academy.nav.status.inProgress": "In progress",
    "academy.nav.status.completed": "Completed",
    "academy.nav.lessonsCompleted": "{x} of {y} lessons completed",
    "academy.nav.openCourse": "Open course",
    "academy.nav.openPhase": "Open phase",
    "academy.nav.openLesson": "Open lesson",
    "academy.nav.completePhase": "Complete phase {n}",
    "academy.nav.continueChamber": "Continue to the Chamber",
    "academy.nav.continueToLesson": "Continue to lesson {n}",
    "academy.nav.viewProgress": "View my progress",
    "academy.nav.viewPhase": "View phase overview",
    "academy.nav.viewPath": "View my path of development",
    "academy.nav.unsavedWarning": "You have unsaved text. Leave anyway? A draft has been kept where possible.",
    "academy.nav.myWork": "View My Work",
    "academy.mywork.title": "My Work",
    "academy.mywork.subtitle": "Drafts, reflections and completed exercises",
    "academy.mywork.empty": "Nothing saved yet. Your drafts, reflections and completed exercises will gather here.",
    "academy.mywork.sort": "Most recently saved first",
    "academy.mywork.filter.discipline": "Discipline",
    "academy.mywork.filter.status": "Status",
    "academy.mywork.filter.type": "Type",
    "academy.mywork.filter.all": "All",
    "academy.mywork.status.draft": "Draft",
    "academy.mywork.status.inProgress": "In progress",
    "academy.mywork.status.completed": "Completed",
    "academy.mywork.status.archived": "Archived",
    "academy.mywork.type.lesson": "Lesson work",
    "academy.mywork.type.record": "Record",
    "academy.mywork.type.practice": "Practice log",
    "academy.mywork.type.evidence": "Demonstration",
    "academy.mywork.type.chamber": "Chamber",
    "academy.mywork.action.continue": "Continue editing",
    "academy.mywork.action.view": "View",
    "academy.mywork.action.gotoLesson": "Go to related lesson",
    "academy.mywork.action.delete": "Delete",
    "academy.mywork.deleteConfirm": "Delete this saved work? This cannot be undone.",
    "academy.mywork.writtenIn.nl": "Written in Dutch",
    "academy.mywork.writtenIn.en": "Written in English",
    "academy.mywork.createdAt": "Created",
    "academy.mywork.savedAt": "Last saved",
    "academy.mywork.completedAt": "Completed on",
    "academy.mywork.lesson": "Lesson",
    "academy.mywork.phase": "Phase",
    "academy.mywork.storageNote": "Your work is stored on the server, linked to an anonymous ID kept in this browser. It is not yet linked to an account: it does not follow you to other devices, and clearing browser data breaks the link.",
    "academy.save.saving": "Saving changes\u2026",
    "academy.save.saved": "✓ Saved to your StoicOs account",
    "academy.save.failed": "Save failed \u2014 try again",
    "academy.save.lastSaved": "Last saved on {date} at {time}",
    "academy.save.draftBtn": "Save draft",
    "academy.save.completeBtn": "Save and complete lesson",
    "academy.save.completedNote": "Lesson completed",
    "academy.save.keepWriting": "Keep writing",
    "academy.save.backToLesson": "Back to lesson",
    "academy.disc.status.preparation": "In Preparation",
    "academy.disc.logic.cta": "Enter the Hall of Clear Judgment",

    "academy.disc.physics.title": "Stoic Physics",
    "academy.disc.physics.subtitle": "Understanding Reality",
    "academy.disc.physics.classical": "Physikē",
    "academy.disc.physics.body": "Not physics in the modern sense, but the understanding of Nature, Reality and the Cosmos: the ordered whole of which we are parts. The Stoics held that ethics can only truly be understood after understanding reality itself. One of the deepest wings of the Academy.",
    "academy.disc.physics.topics": "The Cosmos|Logos|Physis|Pneuma|Sympatheia|Providence|Fate (Heimarmenē)|Causality|The Human Soul|Hegemonikon|Ekpyrosis|Living According to Nature",

    "academy.disc.ethics.title": "Stoic Ethics",
    "academy.disc.ethics.subtitle": "The Art of Living",
    "academy.disc.ethics.classical": "Ēthikē",
    "academy.disc.ethics.body": "Ethics is where Logic and Physics become a life: the practical application of clear thinking and understood reality. Here the Academy trains the Four Cardinal Virtues, the passions and their healthy counterparts, and the daily formation of character on the road to eudaimonia.",
    "academy.disc.ethics.topics": "The Four Cardinal Virtues|Appropriate Actions (Kathēkonta)|Passions (Pathē)|Healthy Emotions (Eupatheiai)|Character Formation|Daily Practice|The Prokopton|Eudaimonia",

    "academy.curriculum.eyebrow": "The First Curriculum",
    "academy.curriculum.title": "Thirteen doors into a lifetime of practice.",
    "academy.curriculum.body": "Each card opens a complete path of readings, reflections, dialogues with Virtue Guide, and daily exercises. Move at your own pace. The Curriculum waits.",
    // ---------------- CURRICULUM CARDS (13 doors × 3 lines each) ----------------
    // Structure per door:
    //   {id}          — modern English title (level 1)
    //   {id}Classical — Greek/Latin philosophical foundation (level 2)
    //   {id}Sub       — one-sentence promise of what will be learned (level 3)
    // The classical term is universal — its value is identical across
    // EN and NL blocks (a philosophical foundation, not a translation).
    "academy.card.welcome":            "Welcome, Student",
    "academy.card.welcomeClassical":   "Prooimion",
    "academy.card.welcomeSub":         "Enter the Stoa. Meet your mentor.",

    "academy.card.lifeCurriculum":         "Life is the Curriculum",
    "academy.card.lifeCurriculumClassical":"Askēsis",
    "academy.card.lifeCurriculumSub":      "Turn every ordinary day into a lesson in virtue.",

    "academy.card.knowThyself":         "Know Thyself",
    "academy.card.knowThyselfClassical":"Gnōthi Seauton",
    "academy.card.knowThyselfSub":      "Discover who you are before deciding who you want to become.",

    "academy.card.fourVirtues":         "The Four Virtues",
    "academy.card.fourVirtuesClassical":"Aretē",
    "academy.card.fourVirtuesSub":      "Build the character that guides every decision.",

    "academy.card.emotions":            "Emotions",
    "academy.card.emotionsClassical":   "Pathē",
    "academy.card.emotionsSub":         "Learn to feel deeply without losing your center.",

    "academy.card.judgement":           "Judgement",
    "academy.card.judgementClassical":  "Prohairesis",
    "academy.card.judgementSub":        "Learn to see reality clearly before you choose.",

    "academy.card.selfMastery":         "Self Mastery",
    "academy.card.selfMasteryClassical":"Enkrateia",
    "academy.card.selfMasterySub":      "Master the will that governs every other choice.",

    // Door 08 — Fortuna. Not a finance course. The Stoic relationship
    // with Fortune: wealth, loss, success, failure, generosity, legacy,
    // enough. Seneca's Ep. 4.7, 8, 91, 98 sit at the heart of this door.
    "academy.card.fortuna":             "Fortuna",
    "academy.card.fortunaClassical":    "Fortuna",
    "academy.card.fortunaSub":          "Master wealth, success and loss without becoming their servant.",

    "academy.card.relationships":         "Relationships",
    "academy.card.relationshipsClassical":"Philia",
    "academy.card.relationshipsSub":      "Cultivate meaningful bonds through wisdom, trust and virtue.",

    "academy.card.purpose":            "Purpose",
    "academy.card.purposeClassical":   "Telos",
    "academy.card.purposeSub":         "Discover a life directed by meaning instead of distraction.",

    "academy.card.death":              "Death",
    "academy.card.deathClassical":     "Memento Mori",
    "academy.card.deathSub":           "Let mortality teach you how to live fully.",

    "academy.card.resilience":         "Resilience",
    "academy.card.resilienceClassical":"Amor Fati",
    "academy.card.resilienceSub":      "Transform adversity into strength and character.",

    "academy.card.leadership":         "Leadership",
    "academy.card.leadershipClassical":"Hēgemonikon",
    "academy.card.leadershipSub":      "Lead yourself first, then serve others with integrity.",

    "academy.card.openPath":       "Enter the path",

    // ---------------- CHAPTER I · THE GATE ----------------
    // Localized Academy prose. "StoicOs.ai Academy" is never translated;
    // "VIRTUS · DISCIPLINA · SAPIENTIA" stays Latin (rendered inside the
    // distant temple silhouette, not through this table). The tablet
    // lines below are the Academy's own voice — not attributed to any
    // philosopher.

    "academy.mentor.eyebrow": "Meet Your Mentor",
    "academy.mentor.title.lead": "Your mentor",
    "academy.mentor.title.accent": "never sleeps.",
    "academy.mentor.body1": "Virtue Guide is not a chatbot. It is your personal mentor within the Academy: always aware of the curriculum, always aware of your progress, always ready.",
    "academy.mentor.body2": "Ask questions. Reflect. Practice. Grow.",
    "academy.mentor.body3": "Virtue Guide accompanies you through every lesson.",
    "academy.mentor.always1": "24 hours a day.",
    "academy.mentor.always2": "365 days a year.",
    "academy.mentor.cta": "Speak with Virtue Guide",

    "academy.future.eyebrow": "The Future Academy",
    "academy.future.title": "This is only the beginning.",
    "academy.future.body": "The First Curriculum is one path. In time, the Academy will open new lifelong paths, each a subject that a human life can never exhaust.",
    "academy.future.wisdom":     "Wisdom",
    "academy.future.justice":    "Justice",
    "academy.future.courage":    "Courage",
    "academy.future.temperance": "Temperance",
    "academy.future.purpose":    "Purpose",
    "academy.future.love":       "Love",
    "academy.future.leadership": "Leadership",
    "academy.future.finance":    "Finance",
    "academy.future.parenthood": "Parenthood",
    "academy.future.health":     "Health",
    "academy.future.legacy":     "Legacy",
    "academy.future.death":      "Death",
    "academy.future.meaning":    "Meaning",
    "academy.future.modernLife": "Modern Life",
    "academy.future.footer": "Every subject becomes a lifelong learning path.",

    "academy.closing.line1": "You do not leave life to study philosophy.",
    "academy.closing.line2": "You study philosophy through life itself.",
    "academy.closing.cta":   "Begin Your Journey",

    // ---------------- HOMEPAGE — ACADEMY CTA CARDS ----------------
    // Two elegant minimal invitations placed on the homepage:
    //  · homeAcademy.primary — right after the Hero (first invitation)
    //  · homeAcademy.secondary — mid-page, after Inner Citadel (deeper invitation)
    "homeAcademy.primary.eyebrow":  "The Academy of Wisdom",
    "homeAcademy.primary.headline": "Life is the curriculum.",
    "homeAcademy.primary.accent":   "Virtue is the goal.",
    "homeAcademy.primary.body":     "A living school for the examined life. Thirteen doors, one lifetime of practice, guided by Marcus, Seneca and Epictetus, and by a mentor that never sleeps.",
    "homeAcademy.primary.cta":      "Enter the Academy",

    "homeAcademy.secondary.eyebrow":  "Begin the practice",
    "homeAcademy.secondary.headline": "You do not leave life to study philosophy.",
    "homeAcademy.secondary.accent":   "You study it through life itself.",
    "homeAcademy.secondary.body":     "The Academy is where reflection becomes discipline, and discipline becomes character. Move at your own pace. The curriculum waits.",
    "homeAcademy.secondary.cta":      "Open the Academy",

    // ---------------- GUIDES ----------------
    "guides.eyebrow": "Featured Guides",
    "guides.headline1": "Three voices.", "guides.headline2": "Two thousand years.",
    "guides.intro": "Each guide brings their own temperament: the emperor, the senator, the slave. All wrote on the same questions you carry today.",
    "guides.marcusRole": "The Philosopher Emperor", "guides.marcusMeta": "Roman Emperor · 121–180 AD",
    "guides.marcusDesc": "Author of Meditations. Speaks with calm imperial gravity, reflective and inward-facing.",
    "guides.senecaRole": "The Statesman & Letter-Writer", "guides.senecaMeta": "Roman Statesman · c. 4 BC – 65 AD",
    "guides.senecaDesc": "Author of Letters from a Stoic. Eloquent, warm, like a wise older friend.",
    "guides.epictetusRole": "The Freed Slave", "guides.epictetusMeta": "Stoic Philosopher · c. 50–135 AD",
    "guides.epictetusDesc": "Author of the Discourses and Enchiridion. Speaks plainly, directly, with disciplined fire.",
    "guides.guidePrefix": "Guide",

    // ---------------- FEATURES ----------------
    "features.eyebrow": "The App",
    "features.headline1": "Not another wellness app.", "features.headline2": "A discipline.",
    "features.dialogue.title": "AI-Guided Dialogue",
    "features.dialogue.copy": "Converse with voices shaped by the meditations of Aurelius, the letters of Seneca, and the Discourses of Epictetus.",
    "features.reflection.title": "Daily Reflection",
    "features.reflection.copy": "A single virtue to consider each morning. A single question to sit with each night.",
    "features.journal.title": "Private Journal",
    "features.journal.copy": "An end-to-end encrypted notebook for your examined life. Yours alone.",
    "features.goldenHour.title": "The Golden Hour Mode",
    "features.goldenHour.copy": "Slow, warm sessions designed for the contemplative parts of the day.",
    "features.compass.title": "Virtue Compass",
    "features.compass.copy": "Track wisdom, courage, justice, and temperance, the cardinal virtues, as a quiet practice.",
    "features.premium.title": "Stoic Guidance",
    "features.premium.copy": "Models tuned on primary Stoic texts. No platitudes, no productivity hacks. Only philosophy.",

    // ---------------- PREVIEW BAND ----------------
    "preview.eyebrow": "A Quiet Companion",
    "preview.headline1": "Designed for the", "preview.headline2": "golden hour.",
    "preview.body": "Virtue Guide is built to be used slowly. Five minutes at sunrise. Ten minutes before sleep. Just long enough to hear yourself think.",
    "preview.stat1": "Cardinal Virtues", "preview.stat2": "Conversations",
    "preview.stat3": "Daily Reflections", "preview.stat4": "Notifications, ever",

    // ---------------- FINAL CTA ----------------
    "cta.eyebrow": "Begin",
    "cta.headline1": "The unexamined life,", "cta.headline2": "is not worth living.",
    "cta.body": "Download Virtue Guide. Speak with the Stoics. Begin the practice today.",
    "cta.available": "Officially available in the Apple App Store and Google Play Store.",

    // ---------------- BUILDING IN PUBLIC (homepage closing section) ----------------
    "buildingInPublic.eyebrow": "A Living Project",
    "buildingInPublic.title": "Building in Public",
    "buildingInPublic.intro": "You are watching StoicOs.ai and Virtue Guide being built, just as Rome once was.",
    "buildingInPublic.line1": "Stone by stone.",
    "buildingInPublic.line2": "Lesson by lesson.",
    "buildingInPublic.line3": "Virtue by virtue.",
    "buildingInPublic.body1": "Just as Rome was not built in a day, neither are wisdom, character, or the tools that help us cultivate them.",
    "buildingInPublic.body2": "StoicOs.ai and Virtue Guide are evolving continuously. Every update, every lesson, every reflection, and every feature is another stone laid with care.",
    "buildingInPublic.outro": "We hope you enjoy the journey as much as we do.",
    "buildingInPublic.thanks": "Thank you for building the future with us.",

    // ---------------- ROTATE HINT (mobile-only) ----------------
    "rotateHint.title": "Turn your device.",
    "rotateHint.body": "Discover the full 16:3 cinematic Stoa.",

    // ---------------- FOUNDER STORY ----------------
    "founder.eyebrow": "Founder Story",
    "founder.headline1": "Why", "founder.headline2": "exists.",
    "founder.role": "Founder, StoicOs", "founder.tagline": "Creator of Virtue Guide",
    "founder.quote1": "\u201CThe app is not the goal. The app is the tool.\u201D",
    "founder.toggleOpen": "Read Founder Story", "founder.toggleClose": "Hide Founder Story",
    "founder.letter": "A personal letter from the founder",
    "founder.p1": "y motivation is actually quite simple.",
    "founder.p2": "Over the years I have studied Stoicism, self-knowledge, and practical wisdom. These ideas helped me navigate difficult periods in my own life, and I believe they can help others as well.",
    "founder.p3": "Virtue Guide was created to make that wisdom more accessible.",
    "founder.p4": "Not everyone has the time, energy, or desire to read philosophical books. Yet many people are searching for guidance, reflection, and a little more clarity in their daily lives.",
    "founder.p5a": "The app is not the goal.", "founder.p5b": "The app is the tool.",
    "founder.p6": "The goal is to help people become a little wiser, a little calmer, and a little more conscious of how they live.",
    "founder.p8": "Profit is not the purpose.",
    "founder.p9": "The purpose is to make practical wisdom available when people need it.",
    "founder.quote2": "Knowledge becomes wisdom only when it is lived.",
    "founder.p10": "That is why Virtue Guide is not about collecting quotes or memorizing philosophy.",
    "founder.p11": "It is about bringing wisdom into daily life.",
    "founder.cadence1": "One reflection.", "founder.cadence2": "One conversation.",
    "founder.cadence3": "One decision.", "founder.cadence4": "One day at a time.",
    "founder.p12": "The Stoics taught that philosophy is not something to admire.",
    "founder.p13": "It is something to practice.",
    "founder.p14": "Until one day we are returned to Nature and to the Logos from which we came.",
    "founder.closingMain": "Know Thyself.", "founder.closingSub": "Understanding Is Freedom.",

    // ---------------- FREE QUOTE MODAL ----------------
    "freeQuote.ctaOverline": "Free for you",
    "freeQuote.ctaLabel": "Your Stoic Reflection",
    "freeQuote.modalEyebrow": "Your Stoic Reflection",
    "freeQuote.modalHeadline1": "From quotes", "freeQuote.modalHeadline2": "to practice.",
    "freeQuote.reflection": "Reflection",
    "freeQuote.practice": "Practice for today",
    "freeQuote.another": "Another quote",
    "freeQuote.comingSoon": "Virtue Guide is now available.",
    "freeQuote.comingSoonBody": "A pocket companion for daily reflection, guided by Marcus, Seneca, and Epictetus.",
    "freeQuote.emailPlaceholder": "you@example.com",
    "freeQuote.notify": "Notify Me",
    "freeQuote.sending": "Sending\u2026",
    "freeQuote.successOverline": "You\u2019re on the list",
    "freeQuote.successThanks": "Thank you.",
    "freeQuote.successBody": "Until Virtue Guide arrives: know thyself, and live well today.",
    "freeQuote.footerTag": "Knowledge becomes wisdom only when it is lived.",

    // ---------------- NOTIFY MODAL ----------------
    "notify.overline": "Coming Soon",
    "notify.headline1": "Something new is", "notify.headline2": "on its way.",
    "notify.body": "This part of StoicOs.ai is being prepared with care. Leave your email and we will notify you the moment it opens.",
    "notify.storeApple": "Apple App Store",
    "notify.storeGoogle": "Google Play Store",
    "notify.storeBoth": "the App Store and Google Play Store",
    "notify.emailLabel": "Email address",
    "notify.button": "Notify Me",
    "notify.sending": "Sending\u2026",
    "notify.noSpam": "No spam. One email, when the app is ready.",
    "notify.successOverline": "You\u2019re on the list",
    "notify.thanks": "Thank you.",
    "notify.successBody": "We will write to you the moment it opens. Until then:",
    "notify.knowThyself": "know thyself.",
    "notify.close": "Close",
    "notify.errInvalid": "Please enter a valid email address.",
    "notify.errGeneric": "Something went wrong. Please try again or email ek@stoicos.ai.",

    // ---------------- STORE BADGES ----------------
    "store.appstoreLine1": "Download on the", "store.appstoreLine2": "App Store",
    "store.playLine1": "Get it on", "store.playLine2": "Google Play",

    // ---------------- ABOUT ----------------
    "about.eyebrow": "About StoicOs",
    "about.headline1": "An operating system", "about.headline2": "for the inner life.",
    "about.p1": "toicOs is a small studio building software for the examined life. We believe that the most important technology of the next decade will not be productivity tools. It will be the tools we use to live more wisely.",
    "about.p2_pre": "Our first product, ", "about.p2_em": "Virtue Guide",
    "about.p2_post": ", is a companion app rooted in Stoic philosophy. It does not optimize you. It does not gamify you. It asks you to sit, briefly, with a single question, and to consider how an emperor, a senator, or a freed slave who lived two thousand years ago might have answered it.",
    "about.p3": "We are based between Rome and the open internet. We take our cues from the marble and the golden hour, from the discipline of the colonnade and the calm of the cypress at dusk. We build slowly. We ship rarely. We hope it shows.",
    "about.statFounded": "Founded", "about.statBased": "Based", "about.statApp": "First app",
    "about.basedValue": "Rome / Web",
    "about.contactLink": "Speak with us",
    "about.nav.aria": "About StoicOs.ai",
    "about.nav.why": "Why StoicOs.ai",
    "about.nav.founders": "Founder's Speech",
    "about.nav.stoa": "The Digital Stoa",
    "about.nav.disciplines": "Logic · Physics · Ethics",
    "about.nav.lifelong": "Never too old to learn",
    "about.stoa.title": "The Digital Stoa",
    "about.stoa.body": "StoicOs.ai is built as a digital Stoa: a quiet place where the classical teachers — Marcus Aurelius, Epictetus, Seneca and Hipparchia — guide you in the practice of practical wisdom. Not a course factory, but a school in the original sense: a place to practise, to examine and to grow, at your own pace.",
    "about.disc.title": "Logic, Physics and Ethics",
    "about.disc.logic.title": "Logic",
    "about.disc.logic.body": "Learning to perceive and judge clearly. You examine your impressions, recognise hasty conclusions and learn when to assent to a first impression — and when not to.",
    "about.disc.physics.title": "Physics",
    "about.disc.physics.body": "Accepting reality as it is. You learn to distinguish what lies within and beyond your control, and find peace in the order of nature.",
    "about.disc.ethics.title": "Ethics",
    "about.disc.ethics.body": "Turning wisdom into action. You practise the four virtues — wisdom, courage, justice and temperance — in the choices of every day.",
    "about.lifelong.title": "You are never too old to learn.",
    "about.lifelong.body": "Wisdom knows no age. Whether you are at the start of your adult life, in the middle of your career or looking back on many years of experience: as long as you are willing to examine yourself honestly, you can keep learning. Not to become someone else. But to become more aware of who you are, how you judge, and who you choose to be from today onwards.",
    "about.lifelong.quote": "Waste no more time arguing about what a good man should be. Be one.",
    "about.lifelong.cite": "— Marcus Aurelius, Meditations 10.16",
    "about.lifelong.p1": "The Stoa is not merely something to read about. It asks to be practised.",
    "about.lifelong.line1": "Not merely talking about wisdom, but choosing more wisely.",
    "about.lifelong.line2": "Not merely speaking of courage, but acting courageously.",
    "about.lifelong.line3": "Not merely admiring justice, but living justly.",
    "about.lifelong.line4": "Not merely understanding temperance, but practising it daily.",
    "about.lifelong.p3": "Your practice begins with the very next choice you make.",
    "about.closing": "Inner Freedom is the destination.",
    "join.innerFreedom": "Inner Freedom is the Destination",

    // ---------------- PRIVACY ----------------
    "privacy.eyebrow": "Legal",
    "privacy.headline1": "Privacy", "privacy.headline2": "Policy.",
    "privacy.effective": "Effective: January 2026",
    "privacy.s1.title": "1. Our Commitment",
    "privacy.s1.body": "StoicOs builds software for the inner life. We treat your data with the same discretion we would expect for our own. We collect the minimum necessary to operate Virtue Guide, and we do not sell or rent personal information to anyone. Ever.",
    "privacy.s2.title": "2. Information We Collect",
    "privacy.s2.body": "Account information (email, optional name) when you create an account. Usage information (sessions, features used) to improve the app. Conversation content you choose to save to your private journal, encrypted at rest and accessible only to you. We do not collect contacts, location, or device identifiers beyond what is strictly required.",
    "privacy.s3.title": "3. How We Use Information",
    "privacy.s3.body": "To provide and maintain Virtue Guide; to respond to your support requests; to detect and prevent abuse; and to improve the app over time in aggregate, anonymized ways. We do not use your journal entries to train AI models.",
    "privacy.s4.title": "4. AI Conversations",
    "privacy.s4.body": "Conversations with the Guide are routed through large language model providers under strict processing agreements. We do not retain conversation transcripts on our servers unless you explicitly save them. Saved content is encrypted on the device and synced via end-to-end encrypted channels.",
    "privacy.s5.title": "5. Data Sharing",
    "privacy.s5.body": "We share data only with the service providers required to operate the app (hosting, analytics, AI inference) and only under the terms of strict data-processing agreements. We will disclose information when required by law, after careful review.",
    "privacy.s6.title": "6. Your Rights",
    "privacy.s6.body": "You may request access to, correction of, or deletion of your data at any time by writing to ek@stoicos.ai. We honor all rights granted under GDPR, CCPA, and similar regimes, regardless of where you reside.",
    "privacy.s7.title": "7. Children",
    "privacy.s7.body": "Virtue Guide is intended for users aged 16 and older. We do not knowingly collect information from children under 16.",
    "privacy.s8.title": "8. Changes",
    "privacy.s8.body": "We may update this policy from time to time. Material changes will be announced in-app and on stoicos.ai/privacy. Continued use of the app after such updates constitutes acceptance.",
    "privacy.s9.title": "9. Contact",
    "privacy.s9.body": "Questions, requests, or comments may be sent to ek@stoicos.ai. We aim to respond within 7 business days.",
    "privacy.quote": "\u201CBegin at once to live, and count each separate day as a separate life.\u201D",

    // ---------------- SUPPORT ----------------
    "support.eyebrow": "Support",
    "support.headline1": "How can we", "support.headline2": "help?",
    "support.intro": "We're a small team and we read every message ourselves. Expect a reply within two business days, usually much sooner.",
    "support.emailLabel": "Email us", "support.pressLabel": "Press & Media", "support.readLabel": "Read our",
    "support.privacyValue": "Privacy Policy",
    "footer.termsLink": "Terms of Use",
    "support.writeTitle": "Write to us.",
    "support.writeSub": "No tickets, no chatbots. Just a thoughtful reply.",
    "support.fieldName": "Your name", "support.fieldEmail": "Email",
    "support.fieldTopic": "Topic", "support.fieldMessage": "Your message",
    "support.topicGeneral": "General question", "support.topicAccount": "Account",
    "support.topicBug": "A bug to report", "support.topicPress": "Press & media", "support.topicOther": "Other",
    "support.placeholderName": "Marcus", "support.placeholderMessage": "Tell us what's on your mind...",
    "support.submit": "Send Message",
    "support.sentTitle": "Your message is on its way.",
    "support.sentBody": "If your email client did not open, write to ",
    "support.faqTitle": "Frequently asked.",
    "support.faq1q": "When will Virtue Guide be available?",
    "support.faq1a": "Virtue Guide is available now on both the Apple App Store and Google Play. Download the free Basic Edition and begin your Stoic journey today.",
    "support.faq2q": "Is it free?",
    "support.faq2a": "Pricing details will be shared in the app at launch.",
    "support.faq3q": "How do you handle my journal entries?",
    "support.faq3a": "Journal entries are encrypted on your device before they are synced. We cannot read them, not even in support cases. See our Privacy Policy for the full detail.",
    "support.faq4q": "Which philosophers can I talk with?",
    "support.faq4a": "At launch: Marcus Aurelius, Seneca, and Epictetus. We're carefully exploring additional voices: Musonius Rufus, Hierocles, and the Hellenistic schools beyond.",

    // ---------------- 404 ----------------
    "404.eyebrow": "404",
    "404.headline1": "The path is", "404.headline2": "lost.",
    "404.body": "\u201CIf you are distressed by anything external, the pain is not due to the thing itself, but to your estimate of it.\u201D (Marcus Aurelius)",
    "404.return": "Return home",

    // ---------------- LANGUAGE TOGGLE ----------------
    "lang.switchTo": "Switch language",

    // ---------------- HERO (SEO H2 + new subtitle) ----------------
    "hero.h2a": "Develop wisdom. Strengthen character.",
    "hero.h2b": "Build your Inner Citadel.",
    "hero.subtitleSeo": "Virtue Guide is an AI-powered Stoic wisdom coach inspired by Marcus Aurelius, Seneca and Epictetus, helping you develop better judgment, emotional resilience and self-knowledge.",

    // ---------------- MISSION (new closing quote) ----------------
    "mission.quoteLead": "Knowledge is cheap.",
    "mission.quoteAccent": "Wisdom is priceless.",

    // ---------------- MEET THE SAGES (relabel) ----------------
    "sages.eyebrow": "Meet the Sages",
    "sages.introExtra": "The app is not about Stoicism. It is about self-knowledge. Know thyself. The sages are guides.",
    "sages.meetCta": "Meet {name}",

    // ---------------- WHAT IS VIRTUE GUIDE ----------------
    "wivg.eyebrow": "What is Virtue Guide?",
    "wivg.h2a": "An AI Stoic coach",
    "wivg.h2b": "for the examined life.",
    "wivg.intro": "Virtue Guide is the first product from StoicOs. It is not a Stoicism reader, a quote feed, or a meditation app. It is a small, deliberate daily practice for becoming wiser, calmer, and more yourself, with three sages, your own private journal, and an AI life coach bounded by virtue.",
    "wivg.col1.title": "What it does",
    "wivg.col1.copy": "Virtue Guide is an AI-powered Stoic wisdom coach. You converse with guides inspired by Marcus Aurelius, Seneca and Epictetus, sages who help you reflect on the day, examine a decision, and notice where you can choose more wisely.",
    "wivg.col2.title": "How it works",
    "wivg.col2.copy": "Each day brings one short reflection and an optional dialogue. The AI uses our Common Sense by Virtue™ framework (Impression, Reasoning, Virtue Test, Assent, Judgment, Choice, Action, Reflection) to help you turn impressions into examined judgments.",
    "wivg.col3.title": "Why it is different",
    "wivg.col3.copy": "Most apps deliver content. Virtue Guide is a practice. The goal is not more information. It is self-knowledge, better judgment, emotional resilience and character. Knowledge is cheap. Wisdom is priceless.",

    // ---------------- COMMON SENSE BY VIRTUE ----------------
    "csbv.eyebrow": "Common Sense by Virtue™",
    "csbv.h2a": "A framework for",
    "csbv.h2b": "wiser decisions.",
    "csbv.intro": "Most poor decisions don't come from a lack of intelligence. They come from skipping steps: we assent to a first impression before we've examined it. Common Sense by Virtue™ is the eight-step Stoic decision process Virtue Guide walks you through, until it becomes second nature.",
    "csbv.step1.label": "Impression",
    "csbv.step1.desc": "Notice the raw first thought, before you call it true.",
    "csbv.step2.label": "Reasoning",
    "csbv.step2.desc": "Examine it. What is it really saying? Is it accurate?",
    "csbv.step3.label": "Virtue Test",
    "csbv.step3.desc": "Wisdom · Courage · Justice · Temperance. Does it hold?",
    "csbv.step4.label": "Assent",
    "csbv.step4.desc": "Only now do you agree (or refuse) to take it as your own.",
    "csbv.step5.label": "Judgment",
    "csbv.step5.desc": "What is the most accurate, honest reading of the situation?",
    "csbv.step6.label": "Choice",
    "csbv.step6.desc": "From judgment, the next right move becomes visible.",
    "csbv.step7.label": "Action",
    "csbv.step7.desc": "Execute calmly. Effort is yours; outcomes are not.",
    "csbv.step8.label": "Reflection",
    "csbv.step8.desc": "At day's end, examine what you did. Adjust. Repeat.",
    "csbv.footQuote": "\u201CBetween stimulus and response there is a space. In that space lies our power to choose our response.\u201D",
    "csbv.footAttrib": "The Stoic intuition",

    // ---------------- INNER CITADEL ----------------
    "citadel.eyebrow": "The Inner Citadel",
    "citadel.h2a": "Build the part of you",
    "citadel.h2b": "nothing outside can reach.",
    "citadel.intro": "Marcus Aurelius wrote of an inner citadel: the considered judgment of a person that no insult, loss, or fortune can breach without consent. Virtue Guide is built around fortifying that citadel. Every reflection, every Stoic question, every conversation is in service of one quiet outcome: an unshakeable interior life.",
    "citadel.quote": "\u201CYou have power over your mind, not outside events. Realize this, and you will find strength.\u201D",
    "citadel.quoteAttrib": "Marcus Aurelius",
    "citadel.pillar1.title": "Inner Strength",
    "citadel.pillar1.copy": "The settled core that doesn't flinch when life arrives unannounced.",
    "citadel.pillar2.title": "Emotional Resilience",
    "citadel.pillar2.copy": "Not the absence of feeling, but the discipline of not being ruled by it.",
    "citadel.pillar3.title": "Character",
    "citadel.pillar3.copy": "What you are when no one is watching, and when everyone is.",
    "citadel.pillar4.title": "Stoic Stability",
    "citadel.pillar4.copy": "An equanimity that holds whether the day is excellent, ordinary or hard.",
    "citadel.pillar5.title": "Self-Mastery",
    "citadel.pillar5.copy": "Command of your own attention, appetites, and assent: the only real power.",

    // ---------------- HOW DIFFERENT ----------------
    "diff.eyebrow": "How Virtue Guide is Different",
    "diff.h2a": "Not another wellness app.",
    "diff.h2b": "A wisdom practice.",
    "diff.intro": "There are many excellent Stoic and mindfulness apps. We respect them. Virtue Guide sits in a different place: it is a coach for wisdom, character and judgment, not a content library, mood tracker, journaling tool, or meditation timer. The comparison below is honest about that difference.",
    "diff.colApp": "App",
    "diff.colThem": "What they do",
    "diff.colUs": "What Virtue Guide does",
    "diff.row1.cat": "Stoic journaling app",
    "diff.row1.them": "Daily journaling prompts based on Stoic themes.",
    "diff.row1.us": "Journal + AI Stoic dialogue + Common Sense by Virtue™ decision framework.",
    "diff.row2.cat": "Stoic content + tracker",
    "diff.row2.them": "Quote feeds, mood tracking, journaling, breathwork.",
    "diff.row2.us": "A coach, not a content library. AI-guided reflection that examines your actual decisions.",
    "diff.row3.cat": "Stoic newsletter & courses",
    "diff.row3.them": "Curated quotes and lessons, read and absorb.",
    "diff.row3.us": "Not reading about Stoicism, but practicing it, with three sages as your conversational guides.",
    "diff.row4.cat": "Meditation & secular philosophy",
    "diff.row4.them": "Guided meditation and reflective talks.",
    "diff.row4.us": "Less sitting in silence, more thinking clearly. Built for the decisions of your actual day.",
    "diff.row5.cat": "Mindfulness & wellness",
    "diff.row5.them": "Calm, sleep, mindfulness exercises.",
    "diff.row5.us": "Not wellness. A discipline. Designed to develop wisdom and character, not just relaxation.",
    "diff.disclaimer": "Apps and brands are referenced for conceptual comparison only. All trademarks belong to their respective owners.",

    // ---------------- FAQ PREVIEW + PAGE chrome ----------------
    "faq.eyebrow": "Frequently asked",
    "faq.h2a": "Questions",
    "faq.h2b": "worth asking.",
    "faq.allLink": "All questions",
    "faq.readMore": "Read more",
    "faq.pageIntro": "Everything you might want to know about Virtue Guide: the AI Stoic coach, the framework behind it, the Inner Citadel, and how a small daily practice changes the shape of a life.",
    "faq.cta.eyebrow": "Begin the practice",
    "faq.cta.h2a": "Start practicing with ",
    "faq.cta.h2b": "Virtue Guide.",
    "faq.cta.body": "Virtue Guide is now available in the App Store and Google Play.",
    "faq.cta.aboutLink": "About StoicOs",

    // ---------------- SUBLANDING (shared chrome) ----------------
    "sub.backAria": "Back to StoicOs Virtue Guide home",
    "sub.commonQ": "Common questions",
    "sub.peopleAsk": "People also ask.",
    "sub.seeAllFaq": "See all questions",
    "sub.cta.eyebrow": "Begin the practice",
    "sub.cta.h2a": "Start practicing with ",
    "sub.cta.h2b": "Virtue Guide.",
    "sub.cta.body": "Virtue Guide is now available in the App Store and Google Play.",

    // ---------------- STOIC COMPASS ----------------
    "compass.eyebrow": "The Stoic Compass",
    "compass.headlineLead": "Curious which Stoic guide",
    "compass.headlineAccent": "would advise you today?",
    "compass.subheadline": "Answer four short questions and discover which Stoic master best matches the challenge you are facing right now.",
    "compass.startButton": "Take the Stoic Compass",
    "compass.meta": "Four questions · About one minute · Private to you",
    "compass.progressLabel": "Question {n} of {total}",
    "compass.restartLabel": "Restart",
    "compass.takeAgain": "Take the compass again",
    "compass.reflectionLabel": "Reflection",
    "compass.downloadEyebrow": "Begin the practice",
    "compass.downloadHeadline": "Meet your guide in StoicOs.",

    // ---- Questions ----
    "compass.q1.title": "When life feels difficult, what do you seek first?",
    "compass.q1.a": "Direction",
    "compass.q1.b": "Calm",
    "compass.q1.c": "Strength",

    "compass.q2.title": "What frustrates you most?",
    "compass.q2.a": "Lack of control",
    "compass.q2.b": "Lack of clarity",
    "compass.q2.c": "Acting below my values",

    "compass.q3.title": "Which quality do you admire most?",
    "compass.q3.a": "Self-discipline",
    "compass.q3.b": "Wisdom",
    "compass.q3.c": "Courage",

    "compass.q4.title": "What do you need most today?",
    "compass.q4.a": "Perspective",
    "compass.q4.b": "Discipline",
    "compass.q4.c": "Inner peace",

    // ---- Sage bust labels (positioned around the compass) ----
    "compass.bust.marcus": "Marcus Aurelius",
    "compass.bust.epictetus": "Epictetus",
    "compass.bust.seneca": "Seneca",
    "compass.quoteLabel": "A Stoic counsel",

    // ---- Result chrome ----
    "compass.result.eyebrow": "Your Stoic guide",

    // ---- Result: Marcus ----
    "compass.result.marcus.name": "Marcus Aurelius",
    "compass.result.marcus.body": "Your compass points to Marcus Aurelius, the philosopher emperor. You seek perspective and calm strength, the steady mind that holds firm when responsibility presses in.",
    "compass.result.marcus.quote": "You have power over your mind, not outside events. Realize this, and you will find strength.",
    "compass.result.marcus.quoteAttrib": "Marcus Aurelius, Meditations",
    "compass.result.marcus.cta": "Begin your practice with Marcus",

    // ---- Result: Seneca ----
    "compass.result.seneca.name": "Seneca",
    "compass.result.seneca.body": "Your compass points to Seneca, the statesman and letter-writer. You seek direction, discipline and the wisdom to turn intention into honest action.",
    "compass.result.seneca.quote": "It is not that we have a short time to live, but that we waste much of it.",
    "compass.result.seneca.quoteAttrib": "Seneca, Letters from a Stoic",
    "compass.result.seneca.cta": "Begin your practice with Seneca",

    // ---- Result: Epictetus ----
    "compass.result.epictetus.name": "Epictetus",
    "compass.result.epictetus.body": "Your compass points to Epictetus, the freed slave turned teacher. You seek the inner freedom that comes from focusing only on what is truly in your control.",
    "compass.result.epictetus.quote": "Some things are in our control and others are not.",
    "compass.result.epictetus.quoteAttrib": "Epictetus, Enchiridion",
    "compass.result.epictetus.cta": "Begin your practice with Epictetus",

    // ---------------- GETTING STARTED (collapsible homepage card) ----------------
    "gs.title": "Getting Started",
    "gs.eyebrow": "A short tour",
    "gs.intro": "Six doorways into the digital Stoa. Begin wherever you feel drawn.",
    "gs.open": "Open guide",
    "gs.close": "Close guide",
    "gs.virtueguide.title": "Virtue Guide",
    "gs.virtueguide.desc": "Talk with Marcus, Seneca and Epictetus.",
    "gs.journal.title": "The Stoic Journal",
    "gs.journal.desc": "Read articles, reflections and practical wisdom.",
    "gs.cards.title": "Stoic Training Cards",
    "gs.cards.desc": "Practice Stoic exercises for daily life.",
    "gs.insights.title": "My Insights",
    "gs.insights.desc": "Explore personal reflections and lessons learned.",
    "gs.books.title": "My Books",
    "gs.books.desc": "Discover the books behind the journey.",
    "gs.about.title": "About StoicOs.ai",
    "gs.about.desc": "Learn about the mission and founder.",

    // ---------------- THE STOIC JOURNAL ----------------
    "journal.eyebrow": "The Stoic Journal",
    "journal.title": "The Stoic Journal",
    "journal.subA": "Ancient Texts.",
    "journal.subB": "Modern Reflections.",
    "journal.subC": "Timeless Questions.",
    "journal.intro": "A small editorial library of articles, essays and reflections. New entries are added as the practice deepens.",
    "journal.comingSoon": "The first articles are being written. Return soon, or subscribe below to receive a quiet note when the first essays are published.",
    "journal.categoriesLabel": "Categories",
    "journal.cat.all": "All",
    "journal.cat.marcus": "Marcus Aurelius",
    "journal.cat.epictetus": "Epictetus",
    "journal.cat.seneca": "Seneca",
    "journal.cat.stoicPractice": "Stoic Practice",
    "journal.cat.virtues": "Virtues",
    "journal.cat.modernLife": "Modern Life",
    "journal.cat.stoicismSociety": "Stoicism & Society",
    "journal.cat.virtueGuide": "Virtue Guide",
    // legacy keys — kept for the seeded article's back-compat, safe to remove later
    "journal.cat.stoicism": "Stoicism",
    "journal.cat.selfKnowledge": "Self-Knowledge",
    "journal.cat.reflections": "Reflections",
    "journal.featuredLabel": "Featured",
    "journal.featuredTitle": "How Ancient Wisdom Helped Me Through a Difficult Period",
    "journal.featuredAuthor": "Eduard Korporaal",
    "journal.featuredRole": "Founder, StoicOs.ai",
    "journal.featuredTeaser": "A personal account of returning to the Stoics in a season when little else held. An essay on the slow, unhurried work of becoming a wiser person.",
    "journal.featuredReadCta": "Read the article",
    "journal.browseLabel": "Browse",
    "journal.search.placeholder": "Search the Stoic Library…",
    "journal.hero.label": "Stoic Journal",
    "journal.hero.title": "Ancient Texts. Modern Reflections. Timeless Questions.",
    "journal.hero.tagline": "Ancient Texts. Modern Reflections. Timeless Questions.",
    "journal.hero.lede": "An editorial library of articles, essays and reflections for modern life.",
    "journal.hero.cta": "Explore the Journal",
    "journal.search.results": "results",
    "journal.tags.philosophers": "Philosophers",
    "journal.tags.themes": "Themes",
    "journal.tags.virtues": "Virtues",
    "journal.tags.practices": "Practices",
    "journal.readingTime": "min read",
    "journal.readMore": "Continue reading",
    "journal.empty.title": "No articles in this category yet.",
    "journal.empty.body": "More writing is on the way. Browse another category, or wait quietly. The work continues.",
    "journal.backToIndex": "Back to The Stoic Journal",
    "journal.article.publishedOn": "Published",
    "journal.article.readOther": "Other articles",
    "journal.article.relatedTitle": "Related articles",
    "journal.article.tagsLabel": "Tags",
    "journal.cta.eyebrow": "Continue your practice",
    "journal.cta.title": "Continue your practice in Virtue Guide.",
    "journal.cta.body": "The Free Basic Edition is yours to use. Begin your Stoic journey today.",
    "journal.cta.download": "Free download",

    // ---------------- STOIC TRAINING CARDS ----------------
    "cards.eyebrow": "Stoic Training Cards",
    "cards.title": "Stoic Training Cards",
    "cards.subtitle": "For Daily Life",
    "cards.lede": "Wisdom you can carry in your pocket.",
    "cards.intro": "Short Stoic exercises, drawn from the classics and adapted for the way we live now. Practical, repeatable, made to be carried into the day.",
    "cards.comingSoon": "The first 23 cards are being polished. Return soon to read, download and share them.",
    "cards.browseLabel": "Browse by",
    "cards.shown": "cards shown",
    "cards.cat.all": "All",
    "cards.cat.virtue": "Virtue",
    "cards.cat.emotion": "Emotion",
    "cards.cat.philosophy": "Philosophy",
    "cards.cat.wisdom": "Wisdom",
    "cards.cat.courage": "Courage",
    "cards.cat.justice": "Justice",
    "cards.cat.temperance": "Temperance",
    "cards.cat.relationships": "Relationships",
    "cards.cat.work": "Work",
    "cards.cat.selfKnowledge": "Self-Knowledge",
    "cards.cat.difficultTimes": "Difficult Times",
    "cards.downloadCard": "Download Card",
    "cards.shareCard": "Share Card",
    "cards.empty.title": "No cards in this category yet.",
    "cards.empty.body": "The library grows in season. Try another category, or check back soon.",
    "cards.modal.close": "Close",
    "cards.share.download": "Download PNG",
    "cards.share.downloaded": "Downloaded",
    "cards.share.native": "Share…",
    "cards.share.shareTo": "Share to",
    "cards.share.copyLink": "Copy link",
    "cards.share.linkCopied": "Link copied",
    "cards.share.instagramHint": "Card saved. Open Instagram and post the image from your photos.",

    // ---------------- MY INSIGHTS ----------------
    "insights.eyebrow": "My Insights",
    "insights.title": "My Insights",
    "insights.subtitle": "Reflections from a modern Stoic.",
    "insights.intro": "Personal reflections and lessons learned. Not Stoic quotes, but small, honest notes from a life spent trying to live them.",
    "insights.byline": "By Ed Korporaal",
    "insights.comingSoon": "The first insights are being written by hand. New entries will be added quietly as they arrive.",
    "insights.shareLabel": "Share",

    // ---------------- MY BOOKS ----------------
    "books.eyebrow": "My Books",
    "books.title": "My Books",
    "books.subtitle": "A personal journey through ancient wisdom.",
    "books.intro": "The library behind the practice: ancient editions held in hand, modern translations read late into the night, public-domain texts free to anyone, and a few rare books still on the quest.",
    "books.comingSoon": "Each book is being photographed and annotated. The first shelves will appear here shortly.",
    "books.s1.title": "Ancient Works",
    "books.s1.desc": "Old editions held in hand, small windows into how earlier readers met these texts.",
    "books.s1.b1.title": "Enchiridion",
    "books.s1.b1.year": "1670 / 1680",
    "books.s1.b1.note": "An early printed Enchiridion of Epictetus, the small handbook that has guided readers for nearly two millennia.",
    "books.s1.b2.title": "Stoicos Apatheias",
    "books.s1.b2.year": "Historic edition",
    "books.s1.b2.note": "An historic edition tracing the Stoic doctrine of apatheia: freedom from being ruled by the passions.",
    "books.s2.title": "Modern Stoicism",
    "books.s2.desc": "The books currently owned and studied. An expanding collection.",
    "books.s2.b1": "Meditations · Marcus Aurelius",
    "books.s2.b2": "Letters from a Stoic · Seneca",
    "books.s2.b3": "The Practicing Stoic · Ward Farnsworth",
    "books.s2.b4": "How to Think Like a Roman Emperor · Donald Robertson",
    "books.s2.b5": "Other titles from Ed's personal collection",
    "books.s3.title": "Essential Stoic Texts",
    "books.s3.desc": "Public-domain works available to anyone. A good starting point for the practice.",
    "books.s3.whyMatters": "Why it matters",
    "books.s3.readOnline": "Read online",
    "books.s3.b1.title": "Meditations",
    "books.s3.b1.author": "Marcus Aurelius",
    "books.s3.b1.why": "The private notebook of an emperor, written for himself, not for us. The clearest entry point into living Stoicism.",
    "books.s3.b1.url": "https://www.gutenberg.org/ebooks/2680",
    "books.s3.b2.title": "Enchiridion",
    "books.s3.b2.author": "Epictetus",
    "books.s3.b2.why": "A compact handbook on what is in our power and what is not. The shortest path into Stoic discipline.",
    "books.s3.b2.url": "https://www.gutenberg.org/ebooks/45109",
    "books.s3.b3.title": "Letters to Lucilius",
    "books.s3.b3.author": "Seneca",
    "books.s3.b3.why": "A long correspondence on how to live: warm, eloquent, and as close to a wise older friend as a book can be.",
    "books.s3.b3.url": "https://www.gutenberg.org/ebooks/56075",
    "books.s4.title": "On My Quest",
    "books.s4.desc": "Books Ed still hopes to acquire, a lifelong search for editions that mark the path of Stoicism through history.",
    "books.s4.b1": "Rare Marcus Aurelius editions",
    "books.s4.b2": "Rare Seneca editions",
    "books.s4.b3": "Works of Justus Lipsius",
    "books.s4.b4": "Other historical Stoic texts",

    // ---------------- SHARED PLACEHOLDER ----------------
    "placeholder.notifyTitle": "Be quietly notified",
    "placeholder.notifyBody": "Leave your email and we will write to you once when this section opens. No noise.",
    "placeholder.notifyButton": "Notify me",
    "placeholder.exploreOther": "Explore the rest of the Stoa",

    // ================ THE AGORA ================
    "agora.hero.eyebrow": "Stoic Os · The Agora",
    "agora.hero.title": "Enter the Stoa",
    "agora.hero.subtitle": "Before you learn the Stoa, you must feel it.",
    "agora.hero.line1": "Learn Wisdom.",
    "agora.hero.line2": "Practice Virtue.",
    "agora.hero.line3": "Live Well.",
    "agora.hero.cta": "Begin the Journey",
    "agora.hero.scroll": "Walk in",
    "agora.hero.alt": "Sunlit panorama of ancient Athens: marble colonnade, citizens in the agora, the Acropolis crowning the city in golden hour light.",

    "agora.intro": "Before you learn the Stoa, you must feel it. Walk slowly. There is no curriculum here. There is only a long room of light, and a question that has been waiting for you since Athens: how should I live?",
    "agora.continueJourney": "Continue the Journey",
    "agora.readMore": "Read More",

    "agora.placeholder": "Coming soon. The place is being built. The wisdom will follow.",

    // I — The Greek Dawn
    "agora.s1.numeral": "II",
    "agora.s1.title": "The Greek Dawn",
    "agora.s1.dates": "334 – 206 BC",
    "agora.s1.figures": "Zeno of Citium · Cleanthes · Chrysippus",
    "agora.s1.atmosphere": "Morning light over Athens. Olive trees. Marble columns. The beginning of the Stoa.",
    "agora.s1.body": "A shipwrecked merchant walks into a bookshop in Athens and never leaves. The school he founds is named after the painted porch where he teaches in the open air, the stoa poikilē. Anyone may listen. The first philosophy of the everyday is born under a roof of olive light.",
    "agora.s1.alt": "Zeno of Citium teaching seated students on marble flagstones under a colonnade. The Acropolis rises at sunset, framed by olive branches.",

    // II — The Stoa Poikile (the painted porch, school of reason)
    "agora.stoa.numeral": "III",
    "agora.stoa.title": "The Stoa Poikile",
    "agora.stoa.subtitle": "The School of Reason",
    "agora.stoa.atmosphere": "Beneath the painted porch. Frescoes warm with afternoon light. Voices, then quieter voices, then a question.",
    "agora.stoa.q1": "No sacred mysteries.",
    "agora.stoa.q2": "No hidden doctrines.",
    "agora.stoa.q3": "Only observation.",
    "agora.stoa.q4": "Only dialogue.",
    "agora.stoa.q5": "Only reason.",
    "agora.stoa.p1": "Beneath the painted porch of Athens, people gathered not to worship, but to think. Merchants sat beside students. Citizens debated with travellers. Questions were welcomed. Arguments were tested. Ideas were challenged.",
    "agora.stoa.p2": "The Stoa was not built upon revelation or authority. It was built upon reason.",
    "agora.stoa.p3": "Here, wisdom was not handed down. It was discovered together.",
    "agora.stoa.alt": "The painted porch of ancient Athens at golden hour. Citizens, students, travellers and an elder philosopher in animated dialogue beneath frescoed walls and marble columns; the Acropolis visible beyond.",

    // III — Cleanthes (discipline and devotion)
    "agora.cleanthes.numeral": "IV",
    "agora.cleanthes.dates": "331 – 232 BC",
    "agora.cleanthes.title": "Cleanthes",
    "agora.cleanthes.subtitle": "Discipline and Devotion",
    "agora.cleanthes.virtues": "Discipline · Endurance · Humility · Devotion",
    "agora.cleanthes.q1": "Where Zeno opened the door,",
    "agora.cleanthes.q2": "Cleanthes remained when others left.",
    "agora.cleanthes.p1": "After Zeno came the test of endurance.",
    "agora.cleanthes.p2": "Cleanthes was not born into wealth. He worked by night carrying water through the streets of Athens and studied philosophy by day.",
    "agora.cleanthes.p3": "Others admired wisdom. Cleanthes practiced it. He believed that character was built through discipline, effort and loyalty to what is right.",
    "agora.cleanthes.p4": "Under his guidance the Stoa became more than an idea. It became a way of life.",
    "agora.cleanthes.alt": "Cleanthes addressing his disciples beneath the colonnade of the Stoa. Earth-tone toga, marble columns, frescoed wall, the Acropolis visible through the columns in golden afternoon light.",

    // V — Chrysippus (the architect of Stoic logic)
    "agora.chrysippus.numeral": "V",
    "agora.chrysippus.dates": "279 – 206 BC",
    "agora.chrysippus.title": "Chrysippus",
    "agora.chrysippus.subtitle": "The System of Reason",
    "agora.chrysippus.note": "Chrysippus of Soli · Third head of the Stoic school · Often called \u201CThe Second Founder of Stoicism\u201D",
    "agora.chrysippus.concepts": "Impression · Reasoning · Assent · Judgment · Action",
    "agora.chrysippus.q1": "To live well,",
    "agora.chrysippus.q2": "one must first learn to think clearly.",
    "agora.chrysippus.p1": "Athens was full of opinions. The harbor was full of merchants, sailors, politicians and philosophers. Everyone had a view. Not every view was true.",
    "agora.chrysippus.p2": "Chrysippus believed wisdom begins when impressions are examined instead of blindly accepted. At crowded tables and beneath the noise of the city, arguments were tested. Questions were challenged. Assumptions were exposed.",
    "agora.chrysippus.p3": "Reason became a tool for freedom. What matters is not what appears to be true. What matters is what survives examination.",
    "agora.chrysippus.p4": "Through Chrysippus, Stoicism gained its structure. Through reason, philosophy became practical.",
    "agora.chrysippus.alt": "An elder philosopher in pale toga gestures mid-argument at a wooden table by the harbor. Three companions lean in to listen. Behind them: docked ships, a teeming marketplace, and the Acropolis crowning the city in golden hour light.",

    // VI — The Bridge to Rome (Panaetius)
    "agora.bridge.numeral": "VI",
    "agora.bridge.dates": "c. 185 – 110 BC",
    "agora.bridge.title": "The Bridge to Rome",
    "agora.bridge.subtitle": "When Greek Wisdom Met Roman Power",
    "agora.bridge.note": "Panaetius of Rhodes · c. 185 – 110 BC · Stoic philosopher · Teacher and advisor to influential Roman statesmen · Widely regarded as the philosopher who introduced Stoicism to the Roman elite",
    "agora.bridge.concepts": "Wisdom · Character · Duty · Culture · Civilization",
    "agora.bridge.q1": "Rome conquered Greece with legions.",
    "agora.bridge.q2": "Greece conquered Rome with ideas.",
    "agora.bridge.p1": "The Mediterranean now belonged to Rome. Its legions guarded the ports. Its senators governed an expanding republic. Its banners flew across the known world. Power had chosen its capital.",
    "agora.bridge.p2": "Yet in this age of conquest, something unexpected happened. The conquerors became students. Roman statesmen looked beyond wealth, beyond military victory and beyond politics. They looked toward Greece, not for land, not for gold, but for wisdom.",
    "agora.bridge.p3": "Panaetius of Rhodes stood between two civilizations. Behind him lay Athens. Before him stood Rome. He carried no army. No fleet. No authority. Only ideas: about character, about duty, about reason, about virtue.",
    "agora.bridge.p4": "Through Panaetius, Stoicism crossed the sea. The philosophy born beneath the painted porch of Athens began its journey into the heart of Rome. Under the watchful eyes of soldiers and senators, a different kind of power emerged: the power of an examined life. The sword had conquered territories. Ideas would conquer centuries.",
    "agora.bridge.alt": "A Greek philosopher in pale toga addresses a circle of Roman senators and a centurion at a stone table by the harbor at sunset. Roman warships fly an eagle banner; columns and statues line the quay; the city of Rome rises beyond.",

    // VII — Seneca (wisdom amid power)
    "agora.seneca.numeral": "VII",
    "agora.seneca.dates": "4 BC – AD 65",
    "agora.seneca.title": "Seneca",
    "agora.seneca.subtitle": "Wisdom Amid Power",
    "agora.seneca.note": "Lucius Annaeus Seneca · 4 BC – AD 65 · Roman Stoic philosopher · Statesman · Author of the Letters to Lucilius · Advisor to Emperor Nero · One of the most influential voices in Stoicism",
    "agora.seneca.concepts": "Virtue · Fortune · Character · Mortality · Inner Freedom",
    "agora.seneca.q1": "It is not the man who has too little,",
    "agora.seneca.q2": "but the man who craves more,",
    "agora.seneca.q3": "who is poor.",
    "agora.seneca.qattr": "Seneca",
    "agora.seneca.p1": "Rome was now the center of the world. Its roads crossed continents. Its armies guarded an empire. Its rulers commanded immense power. Yet beneath the marble grandeur of Rome, another struggle unfolded: the struggle within.",
    "agora.seneca.p2": "Seneca lived close to power. Closer than most philosophers ever dared. He advised emperors. He accumulated wealth. He moved through palaces and courts. Yet his writings returned again and again to the same question: how can a person remain free?",
    "agora.seneca.p3": "In his Letters to Lucilius, Seneca spoke not as a distant teacher but as a companion. He wrote about fear. About anger. About fortune. About death. About the daily practice of philosophy. His wisdom was not meant for schools. It was meant for life.",
    "agora.seneca.p4": "As Nero's reign darkened, Seneca gradually withdrew from public life. After the Pisonian conspiracy of AD 65, he was accused of involvement. Whether guilty or innocent remains uncertain. Nero ordered him to end his own life. Seneca obeyed with composure. The philosopher who had written so often about mortality now faced it himself.",
    "agora.seneca.p5": "His final lesson was not written. It was lived.",
    "agora.seneca.alt": "Seneca writing at a stone table in his villa garden at sunset. Cypress trees, ivy-clad colonnade, marble statues, a reflecting pool, and the city of Rome rising in the distance under golden hour light.",

    // VIII — Epictetus (the freedom within)
    "agora.epictetus.numeral": "VIII",
    "agora.epictetus.dates": "c. AD 50 – 135",
    "agora.epictetus.title": "Epictetus",
    "agora.epictetus.subtitle": "The Freedom Within",
    "agora.epictetus.note": "Epictetus · c. AD 50 – 135 · Born a slave · Later one of the most influential Stoic teachers in history · His teachings survive through the Discourses and the Enchiridion recorded by Arrian",
    "agora.epictetus.concepts": "Freedom · Self-Mastery · Discipline · Acceptance · Character",
    "agora.epictetus.q1": "No man is free who is not master of himself.",
    "agora.epictetus.qattr": "Epictetus",
    "agora.epictetus.p1": "Epictetus was born a slave. He owned nothing. His future belonged to others. His body could be commanded. His movements controlled. His life directed by another man's will. Yet even as a slave, he discovered something no master could take away: the freedom of the mind.",
    "agora.epictetus.p2": "After gaining his freedom, Epictetus became a teacher. Not in palaces. Not in the Senate. Not among the wealthy. But among ordinary people seeking a better way to live.",
    "agora.epictetus.p3": "His message was simple. Some things are within our control. Some things are not. Confusion begins when we mistake one for the other. Peace begins when we learn the difference.",
    "agora.epictetus.p4": "Students traveled long distances to hear him speak. They did not come for theories. They came for clarity. For courage. For practical wisdom. For a way to remain steady in an uncertain world.",
    "agora.epictetus.p5": "Epictetus taught that freedom does not come from wealth. Nor from status. Nor from power. It comes from governing oneself. The person who masters his judgments becomes unconquerable.",
    "agora.epictetus.p6": "His student Arrian recorded his teachings. Through those writings, the voice of a former slave would echo through centuries, eventually reaching an emperor named Marcus Aurelius.",
    "agora.epictetus.alt": "Epictetus, weathered and grey-bearded, sits before a low wooden table piled with scrolls and small wooden tablets, raising one finger as he teaches. His students lean in around him in earth-tone togas. Iron chains hang from the cracked wall behind him; through the open arch, the temple of an ancient mountain town glows in golden hour light.",

    // IX — Marcus Aurelius (the young philosopher)
    "agora.marcus.numeral": "IX",
    "agora.marcus.dates": "AD 121 – 180",
    "agora.marcus.title": "Marcus Aurelius",
    "agora.marcus.subtitle": "The Philosopher",
    "agora.marcus.note": "Marcus Aurelius · AD 121 – 180 · Roman Emperor · Student of Stoicism · Adopted heir of Antoninus Pius · Author of the Meditations · Widely regarded as one of the wisest rulers in history",
    "agora.marcus.concepts": "Character · Duty · Humility · Philosophy · Service",
    "agora.marcus.q1": "Waste no more time arguing what a good man should be.",
    "agora.marcus.q2": "Be one.",
    "agora.marcus.qattr": "Marcus Aurelius",
    "agora.marcus.p1": "Marcus Aurelius was born in AD 121 as Marcus Annius Verus. From an early age, those around him noticed something unusual. A seriousness. A sincerity. A quiet devotion to duty.",
    "agora.marcus.p2": "The Emperor Hadrian saw these qualities as well. He reportedly gave the boy a nickname: Verissimus, \u201CThe Most Truthful,\u201D \u201CThe Most Upright.\u201D Not because of ambition. Not because of status. But because of character.",
    "agora.marcus.p3": "The future of Marcus changed when Hadrian adopted Antoninus Pius and required him to adopt two young heirs: Marcus Aurelius and Lucius Verus. The path toward the throne was chosen for him. Yet Marcus never viewed the imperial office as a reward. He viewed it as a responsibility.",
    "agora.marcus.p4": "As a young man he immersed himself in philosophy. Among all his teachers, one stood above the rest: Junius Rusticus. Marcus would later thank Rusticus in his Meditations for introducing him to the teachings of Epictetus, and for showing him the value of simplicity, humility and self-examination.",
    "agora.marcus.p5": "Other teachers followed. Apollonius of Chalcedon. Sextus of Chaeronea. Fronto, the great rhetorician. Each shaped his mind. But it was Stoicism that shaped his character.",
    "agora.marcus.p6": "Standing above Rome, Marcus looked upon the greatest city in the world. Yet his attention was drawn elsewhere. Not to power. Not to glory. Not to wealth. But to a question that would guide his entire life: how should a human being live?",
    "agora.marcus.alt": "A young Marcus Aurelius, dark curly hair, in cream tunic and deep purple cloak embroidered with gold, stands at a marble balcony overlooking the city of Rome at sunset. Domes, columns and rooftops stretch to the horizon under a vast golden sky.",

    "agora.marcusWar.numeral": "X",
    "agora.marcusWar.dates": "AD 161 – 180 · The Marcomannic Wars",
    "agora.marcusWar.title": "Marcus Aurelius",
    "agora.marcusWar.subtitle": "The Philosopher in War",
    "agora.marcusWar.note": "Marcus Aurelius · AD 121 – 180 · Roman Emperor · Stoic philosopher · Commander of the Roman legions on the Danube frontier · Author of the Meditations · Widely regarded as one of the wisest rulers in history.",
    "agora.marcusWar.concepts": "War · Reflection · Reason · Discipline · Virtue",
    "agora.marcusWar.q1": "The impediment to action advances action.",
    "agora.marcusWar.q2": "What stands in the way becomes the way.",
    "agora.marcusWar.qattr": "Marcus Aurelius",
    "agora.marcusWar.p1": "The empire was at war. Along the Danube frontier, Roman legions faced relentless pressure from tribes beyond the river. Winter was harsh. Supplies were uncertain. Thousands depended on the decisions of one man. That man was Marcus Aurelius.",
    "agora.marcusWar.p2": "By day he commanded armies. By night he commanded himself. In the quiet of his command tent, illuminated only by oil lamps and the glow of distant campfires, Marcus wrote private reflections. Not for publication. Not for history. Not for fame. Only for himself.",
    "agora.marcusWar.p3": "These notes would become known as the Meditations. One of the most remarkable books ever written. Not because it teaches how to rule an empire, but because it teaches how to rule oneself.",
    "agora.marcusWar.p4": "Outside stood the armies of Rome. Inside sat a man trying to remain good.",
    "agora.marcusWar.p5": "The greatest battle of Marcus Aurelius was not fought against the Marcomanni. It was fought within.",
    "agora.marcusWar.alt": "Marcus Aurelius, in bronze armour and crimson cloak, writes by lamplight inside his command tent on the Danube frontier at dusk. Beyond the open tent flap, Roman legions stand in formation along the river under a burning orange sky; maps and standards hang on the tent wall behind him.",

    "agora.marcusEnd.numeral": "XI",
    "agora.marcusEnd.dates": "AD 180 · The Last Day",
    "agora.marcusEnd.title": "Marcus Aurelius",
    "agora.marcusEnd.subtitle": "The Last Sunset",
    "agora.marcusEnd.note": "Marcus Aurelius · AD 121 – 180 · Roman Emperor · Stoic philosopher · Author of the Meditations. He died during the Danubian campaigns in AD 180. The final words presented here belong to later historical tradition and reflect the Stoic spirit for which Marcus Aurelius became known.",
    "agora.marcusEnd.concepts": "Amor Fati · Acceptance · Nature · Logos · Mortality",
    "agora.marcusEnd.q1": "Do not act as if you were going to live ten thousand years.",
    "agora.marcusEnd.q2": "Death hangs over you.",
    "agora.marcusEnd.q3": "While you live, while it is in your power, be good.",
    "agora.marcusEnd.qattr": "Marcus Aurelius",
    "agora.marcusEnd.p1": "The wars were ending. The campaigns were nearly complete. The burdens of empire had been carried for many years. Marcus Aurelius had spent a lifetime serving something greater than himself. Now another duty awaited him.",
    "agora.marcusEnd.p2": "Age had weakened his body. Loss had visited him often. He had endured plague, famine, war, political intrigue, and the deaths of many of those he loved. Yet through it all he returned to the same principles. Reason. Duty. Virtue. Acceptance.",
    "agora.marcusEnd.p3": "As dawn approached, Marcus rested in his chamber. Beyond the balcony the first light touched the horizon. The empire was waking. A new day was beginning.",
    "agora.marcusEnd.p4": "According to later tradition, Marcus called for a Praetorian guard. The young soldier entered. Marcus looked toward the rising sun, then toward the man whose life still stretched before him. And he spoke words that have echoed through the centuries.",
    "agora.marcusEnd.p5": "\u201CGo now. For you, the sun is rising. For me, it is setting.\u201D",
    "agora.marcusEnd.p6": "There was no bitterness. No fear. No resistance. Only acceptance: the acceptance of a man who understood that everything borrowed from Nature must one day be returned.",
    "agora.marcusEnd.p7": "For Marcus, death was not an enemy. It was part of the same order that had given him life. The same Logos. The same universe. The same eternal process of becoming and passing away.",
    "agora.marcusEnd.p8": "The philosopher who had spent a lifetime preparing for this moment now met it calmly. As he had lived, so he departed.",
    "agora.marcusEnd.closing": "The sun rises. The sun sets. The Logos remains.",
    "agora.marcusEnd.alt": "An elder Marcus Aurelius reclines on a silk-draped imperial couch in a marble chamber at sunset. He gestures with an open hand toward a Praetorian guard standing by the open balcony. Beyond the columns, Rome stretches to the horizon under a vast golden sky; on a low table rest a lit oil lamp and a leather codex inscribed \u2018Meditationes · Marcus Aurelius\u2019.",

    // ORPHAN — old typographic chapter (kept to avoid key churn)
    "agora.s2.numeral": "IV",
    "agora.s2.title": "The System of Reason",
    "agora.s2.figures": "Chrysippus",
    "agora.s2.themes": "Impressions · Reasoning · Judgment · Logic",
    "agora.s2.atmosphere": "Scrolls. Maps. Ancient texts.",
    "agora.s2.body": "If Zeno opened the door, Chrysippus built the house. Hundreds of treatises, almost all lost, on how an impression becomes a thought, and a thought a deed. Stoicism becomes a complete way of seeing, a logic of how to be alive.",

    // V — The Bridge to Rome
    "agora.s3.numeral": "VI",
    "agora.s3.title": "The Bridge to Rome",
    "agora.s3.dates": "185 – 51 BC",
    "agora.s3.figures": "Panaetius · Posidonius",
    "agora.s3.atmosphere": "Sails on the Mediterranean. Cargo of ideas heavier than grain.",
    "agora.s3.body": "Stoicism crosses the water. In Rhodes and Rome it meets Roman law, Roman duty, Roman temperament. It softens, broadens, becomes practical. By the time it reaches the senate floor, it is no longer just Greek. It is a way of being a citizen.",

    // VI — Seneca's Courtyard
    "agora.s4.numeral": "VII",
    "agora.s4.title": "Seneca's Courtyard",
    "agora.s4.dates": "4 BC – 65 AD",
    "agora.s4.themes": "Time · Wealth · Friendship · Mortality",
    "agora.s4.atmosphere": "A Roman villa garden. A fountain. Olive trees. A letter half-written on the table.",
    "agora.s4.body": "Tutor to an emperor, advisor at the center of power, and finally condemned by it. Seneca writes as a man who knows he will be asked to die before the year is out. His letters to Lucilius are warm, almost domestic, and unflinching. They remain the closest thing philosophy has to a wise older friend.",

    // VII — Epictetus
    "agora.s5.numeral": "VIII",
    "agora.s5.title": "Epictetus",
    "agora.s5.dates": "50 – 135 AD",
    "agora.s5.themes": "Freedom · Control · Prohairesis · The Inner Citadel",
    "agora.s5.atmosphere": "A simple room. A lamp. A single bench. Nothing else is needed.",
    "agora.s5.body": "Born a slave, lame for life, exiled by an emperor, and yet the freest mind of his age. Epictetus teaches that no one outside us controls what is most ours: the use we make of our impressions. The doctrine of prohairesis, the inner faculty of choice, becomes the heart of the practice.",

    // VIII — Marcus at the Danube
    "agora.s6.numeral": "IX",
    "agora.s6.title": "Marcus at the Danube",
    "agora.s6.dates": "121 – 180 AD",
    "agora.s6.subtitle": "The Emperor Who Wrote to Himself",
    "agora.s6.atmosphere": "A military tent on the Danube frontier. Golden hour. A campfire outside. An oil lamp inside. The empire's weight, and a single notebook open on a wooden table.",
    "agora.s6.body": "He never meant for us to read these pages. The Meditations are private exercises: a man practicing, by candlelight, how to remain just under unjust pressure, how to keep an open hand when the world demands a closed fist. The most powerful man in the world, on most nights, was simply trying to be a better one.",
    "agora.s6.pull": "Begin the morning by saying to thyself, I shall meet with the busy-body, the ungrateful…",

    // IX — The Library
    "agora.s7.numeral": "XII",
    "agora.s7.title": "The Library",
    "agora.s7.atmosphere": "A long classical hall. Bookshelves to the vaulted ceiling. Reading tables of dark wood. Quiet.",
    "agora.s7.body": "A future home for texts, courses, exercises, reflections, and the books that shaped the practice. For now, the shelves are being filled.",
    "agora.s7.shelf1": "Texts",
    "agora.s7.shelf2": "Courses",
    "agora.s7.shelf3": "Exercises",
    "agora.s7.shelf4": "Reflections",
    "agora.s7.shelf5": "Book Recommendations",

    // X — The Hall of Light
    "agora.s8.numeral": "XIII",
    "agora.s8.title": "The Hall of Light",
    "agora.s8.atmosphere": "An open room. White stone. High windows. Long shafts of golden afternoon light cutting across the floor.",
    "agora.s8.line1": "Knowledge can be stored.",
    "agora.s8.line2": "Information can be transmitted.",
    "agora.s8.line3": "Intelligence can be simulated.",
    "agora.s8.line4": "But wisdom must be lived.",
    "agora.s8.line5": "Character has sustained us.",
    "agora.s8.line6": "Now it is time for wisdom.",
    "agora.s8.line7": "Knowledge is cheap.",
    "agora.s8.line8": "Wisdom is priceless.",

    // XI — The Modern Stoa
    "agora.s9.numeral": "XIV",
    "agora.s9.title": "The Modern Stoa",
    "agora.s9.atmosphere": "Glass. Stone. Linen. Quiet light. The architecture of a calm life.",
    "agora.s9.body": "An ancient way, met by a modern life. The questions are no longer asked in a marketplace in Athens. They are asked in a kitchen at six in the morning, in a meeting at two, in a hospital corridor, on the train home. Stoicism is brought back into rooms it never left.",
    "agora.s9.topicsLabel": "Subjects",
    "agora.s9.t1": "Relationships",
    "agora.s9.t2": "Health",
    "agora.s9.t3": "Work",
    "agora.s9.t4": "Leadership",
    "agora.s9.t5": "Politics",
    "agora.s9.t6": "Social Media",
    "agora.s9.t7": "Artificial Intelligence",
    "agora.s9.t8": "Meaning",

    // XII — Virtue Guide
    "agora.s10.numeral": "XV",
    "agora.s10.title": "Virtue Guide",
    "agora.s10.body": "The lesson ends. The practice begins.",
    "agora.s10.cta": "Open Virtue Guide",
    "agora.s10.note": "The Guides Are Always With You.",

    // XIII — The Exit
    "agora.s11.numeral": "XVI",
    "agora.s11.title": "The Exit",
    "agora.s11.atmosphere": "A stone pathway leaving the colonnade. The sun low. The day quieter than it began.",
    "agora.s11.line1": "The Guides Are Always With You.",
    "agora.s11.line2": "Until You Return to Nature and the Logos.",

    // figures used in expandable containers
    "agora.figures.label": "Future chapters",
    "agora.figures.note": "Each figure below has its own room in the Agora. The rooms exist now; the words will arrive in time.",
    "agora.fig.zeno": "Zeno of Citium",
    "agora.fig.cleanthes": "Cleanthes",
    "agora.fig.chrysippus": "Chrysippus",
    "agora.fig.panaetius": "Panaetius",
    "agora.fig.posidonius": "Posidonius",
    "agora.fig.seneca": "Seneca",
    "agora.fig.musonius": "Musonius Rufus",
    "agora.fig.epictetus": "Epictetus",
    "agora.fig.marcus": "Marcus Aurelius",
    "agora.fig.modern": "The Modern Stoa",

    "agora.philosophy.title": "Why the Agora exists",
    "agora.philosophy.body": "To preserve, cultivate, and pass on wisdom. Technology serves wisdom, not the other way around. AI may become humanity's greatest library, but wisdom remains humanity's highest achievement.",
  },

  nl: {
    // ---------------- NAV / FOOTER ----------------
    "nav.home": "Home", "nav.about": "Over", "nav.support": "Support", "nav.privacy": "Privacy",
    "nav.virtueguide": "Virtue Guide", "nav.newsletter": "Nieuwsbrief",
    "newsletter.cta.title": "De StoicOs.ai Nieuwsbrief",
    "newsletter.cta.intro": "Praktische stoïcijnse wijsheid, nieuwe Living Library-artikelen en Academy-inzichten — met zorg bezorgd. Geen spam, u kunt zich altijd uitschrijven.",
    "newsletter.cta.button": "Ontvang de nieuwsbrief",
    "newsletter.cta.close": "Sluiten",
    "home.frieze": "KEN UZELF",
    "home.my.title": "Maak gratis uw My StoicOs aan",
    "home.my.intro": "My StoicOs is uw persoonlijke plaats binnen de Stoa.",
    "home.my.access": "Na uw gratis inschrijving krijgt u toegang tot:",
    "home.my.b1": "uw persoonlijke leeromgeving",
    "home.my.b2": "de Academy",
    "home.my.b3": "het Stoic Journal",
    "home.my.b4": "de Living Library",
    "home.my.b5": "de stoïcijnse mentoren",
    "home.my.b6": "uw opgeslagen reflecties",
    "home.my.b7": "uw studievoortgang",
    "home.my.b8": "synchronisatie tussen StoicOs.ai en de Virtue Guide-app",
    "home.my.secure": "Alles wordt veilig opgeslagen zodat u uw reis binnen de Stoa op ieder apparaat kunt voortzetten.",
    "home.my.cta": "Maak gratis mijn StoicOs aan",
    "home.discover.title": "Wilt u eerst meer ontdekken?",
    "home.discover.intro1": "Iedere reis begint anders.",
    "home.discover.intro2": "Misschien wilt u eerst kennismaken met de geschiedenis van de Stoa of meer lezen over de stoïcijnse levensfilosofie.",
    "home.discover.agora.title": "Ik wil meer weten over de stoïcijnen",
    "home.discover.agora.body": "Ontdek hoe de Stoa zich gedurende meer dan 2300 jaar ontwikkelde, van Zeno van Citium tot Marcus Aurelius.",
    "home.discover.agora.cta": "Bezoek de Agora",
    "home.discover.journal.title": "Ik wil meer lezen over de stoïcijnse levensfilosofie",
    "home.discover.journal.body": "Lees artikelen, essays, praktische oefeningen en reflecties waarin de stoïcijnse filosofie wordt toegepast op het dagelijks leven.",
    "home.discover.journal.cta": "Open the Stoic Journal",
    "home.cards.eyebrow": "StoicOs Cards",
    "home.cards.title": "Stoïcisme, één idee per keer.",
    "home.cards.body": "Verken Stoïcijnse ideeën via visuele kaarten over oordeel, deugd, controle, karakter en Innerlijke Vrijheid.",
    "home.cards.cta": "Ontdek de StoicOs Cards",
    "agora.next.title": "Dit wil ik verder leren",
    "agora.next.p1": "Hebben de verhalen en levenslessen van Marcus Aurelius, Epictetus, Seneca en Hipparchia uw belangstelling gewekt?",
    "agora.next.p2": "Misschien herkent u het gevoel: dit vind ik interessant en hierin wil ik mij verder verdiepen.",
    "agora.next.p3": "Binnen My StoicOs leert u stap voor stap hoe u de stoïcijnse levenslessen kunt toepassen in het dagelijks leven. U maakt kennis met de drie klassieke disciplines van de Stoa: Logica, Fysica en Ethica.",
    "agora.next.p4": "Niet alleen om meer over de filosofie te weten, maar om helderder te leren denken, bewuster te kiezen en standvastiger te handelen wanneer het leven u op de proef stelt.",
    "nav.download": "Download",
    "nav.journal": "Stoic Journal",
    "nav.stoicJournal": "The Stoic Journal",
    "nav.agoraMain": "Agora",
    "nav.aboutUs": "Over ons",
    "nav.mystoicos": "My StoicOs",
    "my.eyebrow": "My StoicOs",
    "my.welcome": "Welkom terug",
    "my.traveler": "Reiziger",
    "my.subtitle": "Jouw pad van studie, oefening en reflectie.",
    "my.journalCta": "Ga verder met je Stoic Journal",
    "my.recFirst": "Aanbevolen eerste stap",
    "my.startLogic": "Begin met Logic",
    "my.logicSub": "Leer helder zien voordat je oordeelt, kiest en handelt.",
    "my.beginLogic": "Begin Logic",
    "my.module.library": "Living Library",
    "my.module.library.desc": "De verzamelde wijsheid van de Stoa — essays, begrippen en bronnen.",
    "my.settings.replayDesc": "Bekijk de filmische introductie van de Academy opnieuw.",
    "my.aspirant": "Aspirant Member",
    "my.memberSince": "Member Since",
    "my.introDay": "Introductie · Dag {day} van 7",
    "my.todaysPractice": "De praktijk van vandaag",
    "my.morning": "Ochtendreflectie",
    "my.practice": "Dagelijkse Oefening",
    "my.evening": "Avondreflectie",
    "my.freeLabel": "Vrij Schrijven",
    "my.done": "Geschreven",
    "my.open": "Open",
    "my.virtueFocus": "Deugd van de week",
    "my.virtue.Wisdom": "Wijsheid",
    "my.virtue.Courage": "Moed",
    "my.virtue.Justice": "Rechtvaardigheid",
    "my.virtue.Temperance": "Matigheid",
    "my.streak": "Reflectiereeks",
    "my.streakDays": "{n} dagen",
    "my.streakDay": "1 dag",
    "my.daysPracticed": "dagen geoefend",
    "my.inspiration": "Dagelijkse Inspiratie",
    "my.recent": "Recente Activiteit",
    "my.noActivity": "Je journal is nog leeg. Begin met de ochtendreflectie van vandaag.",
    "my.recommendations": "Aanbevolen vervolg",
    "my.rec.logic-lesson-1": "Begin de leergang Logica",
    "my.rec.library-visit": "Bezoek de Living Library",
    "my.modules": "Jouw Studeerkamer",
    "my.module.journal": "My Journal",
    "my.module.journal.desc": "Ochtend- en avondreflectie, dagelijkse oefening en vrij schrijven.",
    "my.module.academy": "My Academy",
    "my.module.academy.desc": "Voortgang, afgeronde lessen, certificaten en bladwijzers.",
    "my.module.journeys": "My Journeys",
    "my.module.journeys.desc": "Huidige en voltooide Journeys met persoonlijke notities.",
    "my.module.virtues": "My Virtues",
    "my.module.virtues.desc": "Ontwikkeling in Wijsheid, Moed, Rechtvaardigheid en Matigheid.",
    "my.module.mentors": "Mentors",
    "my.module.mentors.desc": "Marcus Aurelius, Epictetus, Seneca en Hipparchia.",
    "my.module.progress": "My Progress",
    "my.module.progress.desc": "Reflectiegeschiedenis, statistieken, reeksen en mijlpalen.",
    "my.module.settings": "Settings",
    "my.module.settings.desc": "Profiel, taal, meldingen, privacy en membership.",
    "my.inPreparation": "In voorbereiding",
    "my.logout": "Uitloggen",
    "join.eyebrow": "The Academy of Practical Wisdom",
    "join.title": "Begin de Reis",
    "join.loginTitle": "Vervolg je Reis",
    "join.welcomeNote": "Na je gratis registratie krijg je direct toegang tot de eerste lessen van Logica, Ethica en Fysica binnen My StoicOs.",
    "join.support": "Bouw karakter door dagelijkse oefening, tijdloze filosofie en de begeleiding van de grote stoïcijnse mentoren.",
    "join.name": "Je naam",
    "join.email": "E-mail",
    "join.password": "Wachtwoord (min. 8 tekens)",
    "join.registerCta": "Begin de Reis",
    "join.loginCta": "Vervolg je Reis",
    "join.haveAccount": "Vervolg je Reis",
    "join.newHere": "Nieuw hier? Begin de reis",
    "trial.cta": "Start mijn gratis kennismaking",
    "trial.hero.headline": "Start je gratis kennismaking",
    "trial.hero.subtitle": "Maak gratis een My StoicOs-account aan en krijg toegang tot de eerste lessen van Logica, Ethica en Fysica. Ontdek de Academy of Practical Wisdom en begin jouw reis.",
    "trial.hero.secondary": "Ik heb al een account",
    "trial.locked.title": "Deze les wordt beschikbaar zodra het volledige lidmaatschap live is.",
    "trial.locked.body": "Je hebt toegang tot de introductielessen van Logica, Ethica en Fysica. Het volledige curriculum wordt beschikbaar zodra het lidmaatschap van de Academy of Practical Wisdom wordt gelanceerd.",
    "trial.locked.back": "Terug naar Mijn StoicOs",
    "trial.locked.previewBody": "De volledige curriculum-preview blijft voor iedereen open. Maak gratis een account aan om de introductielessen te openen, houd je Stoic Journal bij en volg je voortgang — of log in als je al student bent.",
    "trial.academy.note": "De introductielessen zijn bedoeld als eerste kennismaking met de Academy. Het volledige studieprogramma wordt beschikbaar zodra het lidmaatschap live gaat.",
    "ceremony.title": "Welkom in de Academy",
    "ceremony.body1": "Welkom, Aspirant Member.",
    "ceremony.body2": "Je membership is vastgelegd.",
    "ceremony.memberNumber": "Member Number",
    "ceremony.memberSince": "Member Since",
    "ceremony.firstStep": "Vandaag zet je je eerste stap naar Innerlijke Vrijheid.",
    "ceremony.mentorsTitle": "Ontmoet je Mentoren",
    "ceremony.enter": "Betreed My StoicOs",
    "ceremony.continueHint": "Tik om verder te gaan",
    "onb.scene": "Scène {n} van {total}",
    "onb.next": "Volgende",
    "onb.previous": "Vorige",
    "onb.skip": "Overslaan",
    "onb.scene6Title": "De Entree van de Academy",
    "onb.scene6Note": "Deze scène is gereserveerd. Hier wordt straks de entree van de Academy onthuld.",
    "onb.back": "Terug",
    "onb.continue": "Verder",
    "onb.ch1.title": "Welkom, Reiziger",
    "onb.ch1.desc": "Je wordt welkom geheten in de Grote Hal van de Academie voor Praktische Wijsheid.",
    "onb.ch2.title": "Marcus Aurelius",
    "onb.ch2.desc": "Hipparchia stelt de filosoof-keizer voor — hij regeerde een rijk, maar beheerste eerst zichzelf.",
    "onb.ch3.title": "Epictetus",
    "onb.ch3.desc": "Geboren als slaaf werd hij de vrijste van alle mensen. Zijn staf kent de lange weg van volharding.",
    "onb.ch4.title": "Seneca",
    "onb.ch4.desc": "Staatsman en schrijver — hij maakte van de stormen van het lot brieven vol wijsheid.",
    "onb.ch5.title": "Jouw Vier Mentoren",
    "onb.ch5.desc": "Vier stemmen, één pad. Je mentoren wachten op je in de gouden hal.",
    "onb.ch6.title": "Jouw Reis Begint Hier",
    "onb.ch6.desc": "Betreed de Academy of Practical Wisdom en begin de oefening om te worden wie je in staat bent te zijn.",
    "onb.enter": "Betreed de Academy",
    "onb.skipIntro": "Introductie overslaan",
    "onb.begin": "Begin de Reis",
    "onb.sound.mute": "Vertelling dempen",
    "onb.sound.unmute": "Vertelling aanzetten",
    "onb.replay": "Vertelling opnieuw afspelen",
    "onb.volume": "Volume",
    "my.replayIntro": "Intro opnieuw tonen",
    "journal.my.title": "My Journal",
    "journal.my.subtitle": "Een dagelijkse filosofische praktijk — het levende geheugen van je ontwikkeling.",
    "journal.my.morningPrompt": "Wat zal vandaag van mij vragen? Welke deugd oefen ik vandaag bewust?",
    "journal.my.practicePrompt": "Wat heb ik vandaag geoefend? Waar handelde ik naar mijn principes — en waar niet?",
    "journal.my.eveningPrompt": "Wat deed ik goed? Wat had beter gekund? Wat bleef ongedaan?",
    "journal.my.free": "Vrij Schrijven",
    "journal.my.freeNew": "Nieuwe pagina",
    "journal.my.freeTitlePh": "Titel (optioneel)",
    "journal.my.writePh": "Schrijf hier…",
    "journal.my.saving": "Opslaan…",
    "journal.my.saved": "Opgeslagen",
    "journal.my.delete": "Verwijderen",
    "journal.my.back": "Terug naar My StoicOs",
    "journal.my.empty": "Nog geen vrije pagina's.",
    "journal.my.freeLabel": "Vrije Notitie",
    "journal.my.freePrompt": "Schrijf vrijuit — een gedachte, een observatie, een brief aan jezelf.",
    "my.journal.title": "My Journal",
    "my.journal.savedIn": "Opgeslagen in My Journal",
    "my.journal.viewIn": "Open My Journal",
    "my.journal.newFree": "Nieuwe vrije notitie",
    "my.journal.freeTitlePh": "Titel (optioneel)",
    "my.journal.searchPh": "Zoek in je journal…",
    "my.journal.allDates": "Alle datums",
    "my.journal.filterAll": "Alles",
    "my.journal.entriesCount": "notities",
    "my.journal.empty": "Nog geen notities. Schrijf vandaag je eerste reflectie.",
    "my.journal.deleteConfirm": "Deze notitie verwijderen?",
    "my.journal.deleteYes": "Verwijderen",
    "my.journal.cancel": "Annuleren",
    "my.journal.back": "Terug",
    "my.journal.tabToday": "Vandaag",
    "my.journal.tabArchive": "Archief",
    "my.journal.tabMentor": "My Mentor Reviews",
    "my.mentor.title": "My Mentor Reviews",
    "my.mentor.empty": "Nog geen mentorbeoordelingen ontvangen.",
    "my.mentor.info": "Toekomstige mentorreacties verschijnen hier en blijven gekoppeld aan de originele notitie in My Journal.",
    "my.journal.calendar": "Kalender",
    "my.journal.todayBtn": "Vandaag",
    "join.forgotPassword": "Wachtwoord vergeten?",
    "nav.cards": "Oefenkaarten",
    "nav.insights": "Mijn Inzichten",
    "nav.books": "Mijn Boeken",
    "nav.share": "Deel je Gedachten",
    "nav.virtueguide": "Virtue Guide",
    "nav.agora": "De Agora",
    "nav.watch": "Bekijk",

    // ---------------- SHARE YOUR THOUGHTS ----------------
    "share.eyebrow": "Brieven aan de Stoa",
    "share.title1": "Deel je",
    "share.title2": "Gedachten",
    "share.headline": "Deel je Gedachten",
    "share.lede": "Een hedendaagse voortzetting van de briefwisseling tussen Seneca en zijn vriend Lucilius.",
    "share.dropCap": "V",
    "share.p1": "Bijna tweeduizend jaar lang zijn Seneca's brieven aan Lucilius een van de zachtste uitnodigingen in de filosofie gebleven: een rustige, onhaastige uitwisseling tussen twee geesten op zoek naar het goede leven.",
    "share.p2": "StoicOs.ai is in diezelfde geest gebouwd. Eerder een gesprek om binnen te treden dan een product om aan te prijzen.",
    "share.p3": "Roept iets op deze pagina een vraag op, een reflectie, een herinnering, of simpelweg een gedachte die je wilt vastleggen, schrijf. Ik lees iedere brief persoonlijk.",
    "share.emailLabel": "Schrijf de oprichter",
    "share.reassurance": "Elke boodschap wordt persoonlijk gelezen. Hoewel ik misschien niet direct op ieder bericht kan reageren, wordt jouw bijdrage altijd gewaardeerd.",
    "share.signature.name": "Ed Korporaal",
    "share.signature.role": "Oprichter, StoicOs.ai",
    "share.form.title": "Schrijf een Brief aan de Stoa",
    "share.form.subtitle": "Een moderne correspondentie. Je bericht komt rechtstreeks bij de oprichter aan.",
    "share.form.nameLabel": "Naam (optioneel)",
    "share.form.namePlaceholder": "Lucilius",
    "share.form.emailLabel": "E-mail (optioneel)",
    "share.form.emailPlaceholder": "jij@voorbeeld.nl",
    "share.form.messageLabel": "Jouw gedachten",
    "share.form.messagePlaceholder": "Schrijf vrijuit: een vraag, een reflectie, een suggestie…",
    "share.form.send": "Verstuur Bericht",
    "share.form.sending": "Verzenden…",
    "share.form.privacy": "Gelezen door één mens. Nooit gedeeld.",
    "share.form.errEmpty": "Schrijf een bericht voordat je verstuurt.",
    "share.form.errSend": "De bezorging is onderbroken. Probeer opnieuw, of schrijf rechtstreeks naar ek@stoicos.ai.",
    "share.form.successTitle": "Dank je. Je bericht is verzonden naar Ed op ek@stoicos.ai.",
    "share.form.successBody": "Elke brief wordt persoonlijk gelezen. Een antwoord kan even duren, maar het komt.",
    "share.form.savedTitle": "Je bericht is opgeslagen.",
    "share.form.savedBody": "E-mailbezorging is op dit moment niet beschikbaar, dus ik heb nog geen melding per e-mail ontvangen, maar je bericht is veilig bewaard en wordt gelezen. Je kunt ook rechtstreeks schrijven naar ek@stoicos.ai.",
    "share.form.deliveryNotice": "Berichten worden rechtstreeks verzonden naar:",
    "share.form.writeAgain": "Schrijf nog een bericht",
    "footer.tagline": "StoicOs ontwikkelt instrumenten voor het onderzochte leven. Virtue Guide is onze eerste metgezel, een door AI begeleide dialoog met de wijsheid van Rome.",
    "footer.knowThyself": "Ken Uzelf",
    "footer.company": "Bedrijf", "footer.aboutLink": "Over StoicOs",
    "footer.product": "Virtue Guide",
    "footer.ios": "Download voor iOS", "footer.android": "Download voor Android",
    "footer.rights": "Alle rechten voorbehouden.",

    // ---------------- HERO ----------------
    "hero.eyebrow": "StoicOs.ai",
    "hero.title1": "Ken", "hero.title2": "Uzelf",
    "hero.sub1": "Ontwikkel wijsheid.",
    "hero.sub2": "Versterk karakter.",
    "hero.sub3": "Bouw uw innerlijke citadel.",
    "hero.body": "Virtue Guide is een product van StoicOs.ai. Een AI-gestuurde gids voor zelfkennis, karaktervorming en praktische wijsheid, geïnspireerd door de grote stoïcijnse denkers.",
    "hero.bodyLead": "Virtue Guide is een product van",
    "hero.bodyTail": "Een AI-gestuurde gids voor zelfkennis, karaktervorming en praktische wijsheid, geïnspireerd door Marcus Aurelius, Seneca en Epictetus.",
    "hero.ctaPrimary": "Begin de Reis",
    "hero.ctaSecondary": "Betreed de Agora",
    "hero.alt": "Zonsopgang boven de tempel van Delphi met de inscriptie \u0393\u039D\u03A9\u0398\u0399 \u03A3\u0395\u0391\u03A5\u03A4\u039F\u039D, \u201CKen Uzelf\u201D. Een eenzame reiziger loopt over het stenen pad naar de tempel. Links een banier van Wijsheid (uil) boven Rechtvaardigheid (weegschaal); rechts banieren van Moed (leeuw) boven Matigheid (kelk). Stenen bustes van Marcus Aurelius, Seneca en Epictetus verrijzen in de wolken boven de tympaan terwijl de zon opkomt achter de bergen.",
    "hero.subtitle": "Een zakformaat metgezel voor het onderzochte leven. Spreek met Marcus Aurelius, Seneca en Epictetus, en herontdek wat het betekent om goed te leven.",
    "hero.statusBeta": "Nu in besloten bèta",
    "hero.statusRome": "Ontworpen in Rome",
    "hero.scroll": "Scrol",

    // ---------------- COMING SOON TRAILER ----------------
    "coming.eyebrow": "Nu Beschikbaar",
    "coming.title1": "Virtue Guide",
    "coming.title2": "is nu beschikbaar",
    "coming.subtitle": "Een nieuwe manier om de Stoïcijnse filosofie te verkennen, geleid door AI-reflectie.",
    "coming.description": "Ontdek hoe tijdloze Stoïcijnse wijsheid samenkomt met moderne kunstmatige intelligentie. Virtue Guide is ontworpen om mensen te helpen reflecteren, hun oordelen in twijfel te trekken en hun karakter te versterken via gesprekken met de grootste Stoïcijnse leermeesters uit de geschiedenis.",
    "coming.video.trailer.eyebrow": "Officiële Trailer",
    "coming.video.trailer.title": "Virtue Guide: De Officiële Trailer",
    "coming.video.trailer.caption": "Een eerste blik op de reis.",
    "coming.cta.heading": "Wees een van de eersten die Virtue Guide ervaart",
    "coming.cta.text": "Begin vandaag je Stoïcijnse reis.\nDownload Virtue Guide nu — beschikbaar in de Apple App Store en op Google Play.",
    "coming.available.note": "De film hierboven is onze oorspronkelijke lanceringstrailer — de lancering die hij aankondigt heeft inmiddels plaatsgevonden.",
    "coming.available.line": "Virtue Guide is nu beschikbaar in de Apple App Store en op Google Play.",
    "coming.comingSoon": "Binnenkort",
    "coming.apple.line1": "In de App Store",
    "coming.apple.line2": "Downloaden",
    "coming.google.line1": "Op Google Play",
    "vgs.eyebrow": "Officiële lancering — De mobiele metgezel",
    "vgs.title1": "Uw stoïcijnse reis,",
    "vgs.title2": "waar u ook bent",
    "vgs.body": "My StoicOs is uw persoonlijke account en leeromgeving op StoicOs.ai. Virtue Guide is uw mobiele metgezel — dagelijkse reflectie, begeleiding en gesprekken met de grote stoïcijnse leermeesters, waar het leven u ook brengt.",
    "vgs.sync": "Alles blijft gesynchroniseerd: uw voortgang, dagboek en reflecties reizen naadloos mee tussen StoicOs.ai en Virtue Guide.",
    "vgs.trust": "Officieel beschikbaar in de Apple App Store en de Google Play Store.",
    "fw.eyebrow": "De Eerste Honderd",
    "fw.title1": "Founders",
    "fw.title2": "Wall of Honor",
    "fw.subtitle": "Elke betekenisvolle reis begint met enkele mensen die durven te geloven voordat de bestemming zichtbaar is.",
    "fw.body1": "De eerste 100 Founding Members van StoicOs.ai krijgen een permanente plaats op deze Wall of Honor.",
    "fw.body2": "Founding Membership is volledig gratis. Erkenning is niet te koop.",
    "fw.body3": "U verdient haar eenvoudigweg door een van de eerste 100 geregistreerde leden te worden.",
    "fw.counterLabel": "Founding Members",
    "fw.remaining": "{n} plaatsen beschikbaar",
    "fw.empty": "De eerste namen worden hier binnenkort bijgeschreven.",
    "fw.cta.heading": "Word een van de eerste 100 Founding Members",
    "fw.cta.b1": "Voor altijd gratis",
    "fw.cta.b2": "Permanente erkenning",
    "fw.cta.b3": "Privacy beschermd",
    "fw.cta.b4": "Help StoicOs.ai vanaf het begin op te bouwen",
    "fw.cta.button": "Maak mijn gratis StoicOs-account aan",
    "join.fw.title": "Founders Wall of Honor",
    "join.fw.q": "Wordt u een van de eerste 100 Founding Members, mogen wij u dan tonen op de publieke Founders Wall of Honor?",
    "join.fw.yes": "Ja — toon mijn voornaam en de eerste letter van mijn achternaam (bijvoorbeeld: Daniel R.)",
    "join.fw.no": "Nee — toon mijn naam niet op de Founders Wall of Honor",
    "fwModal.eyebrow": "Founders Wall of Honor",
    "fwModal.title": "Welkom, Founding Member",
    "fwModal.body": "U behoort tot de eerste 100 leden van StoicOs.ai. Kies of u op de publieke Founders Wall of Honor verschijnt — alleen uw voornaam en de eerste letter van uw achternaam kunnen ooit worden getoond.",
    "fwModal.save": "Mijn keuze opslaan",
    "fwModal.later": "Later beslissen",
    "my.fw.title": "Privacy — Founders Wall of Honor",
    "my.fw.badge": "Founding Member {number}",
    "my.fw.desc": "Kies of uw naam op de publieke Founders Wall of Honor verschijnt. Alleen uw voornaam en de eerste letter van uw achternaam kunnen ooit worden getoond. Uw plaats op de Wall volgt de volgorde van registratie.",
    "my.fw.saved": "Voorkeur opgeslagen.",
    "coming.watchLibrary": "Bekijk de volledige bibliotheek",

    // ---------------- WATCH (Videobibliotheek) ----------------
    "watch.eyebrow": "De Bibliotheek",
    "watch.title": "Bekijk",
    "watch.lede": "Een levend archief van de stem van StoicOs.",
    "watch.intro": "Trailers, stemmen van de Stoïcijnse leermeesters, tutorials en rustige productupdates. Nieuwe video's worden toegevoegd naarmate het project groeit. Er is geen schema, alleen het werk.",
    "watch.browseLabel": "Bladeren op",
    "watch.cat.all": "Alle",
    "watch.cat.trailer": "Officiële Trailer",
    "watch.cat.marcus": "Marcus Aurelius",
    "watch.cat.seneca": "Seneca",
    "watch.cat.epictetus": "Epictetus",
    "watch.cat.cato": "Cato",
    "watch.cat.tutorials": "Tutorials",
    "watch.cat.updates": "Productupdates",
    "watch.empty.eyebrow": "Binnenkort",
    "watch.empty.title": "Hier staat nog niets.",
    "watch.empty.body": "Deze plank van de bibliotheek wordt nog gevuld. Kom binnenkort terug, of blader hierboven door een andere categorie.",
    "watch.video.trailer.eyebrow": "Officiële Trailer",
    "watch.video.trailer.title": "Virtue Guide: De Officiële Trailer",
    "watch.video.trailer.caption": "Een eerste blik op de reis. Een door AI begeleide dialoog met de wijsheid van Rome, gemaakt voor het onderzochte leven.",

    // ---------------- MISSION ----------------
    "mission.eyebrow": "Onze Missie",
    "mission.headline1": "Wij brengen filosofie terug naar waar ze altijd thuishoorde:",
    "mission.headline2": "de stille uren van een gewone dag.",
    "mission.body": "StoicOs bouwt besturingssystemen voor het innerlijk leven. Virtue Guide, onze eerste metgezel, gebruikt AI om u in gesprek te brengen met de grote Stoïcijnen, zodat wijsheid niet langer op een plank blijft staan, maar met u meegaat, als een muntstuk in uw zak.",

    // ---------------- PHILOSOPHY MISSION (homepage premium panel) ----------------
    "philosophyMission.eyebrow": "Onze Filosofie",
    "philosophyMission.headline.lead": "De wereld heeft geen behoefte aan meer informatie.",
    "philosophyMission.headline.accent": "Wel aan meer wijsheid.",
    "philosophyMission.body.para1": "Bij StoicOS.ai is het onze missie om wijsheid te cultiveren en haar begrijpelijk, praktisch en toegankelijk te maken voor iedereen.",
    "philosophyMission.body.para2": "We geloven dat wijsheid geen gave is voorbehouden aan enkelen, maar een deugd die gecultiveerd kan worden door reflectie, rede en dagelijkse praktijk.",
    "philosophyMission.body.para3": "Door tijdloze filosofie te verbinden met mensgerichte AI helpen wij mensen hun oordeel te versterken, karakter op te bouwen en te leven met meer doel.",
    "philosophyMission.body.para4": "Want een betere wereld begint bij wijzere mensen.",
    "philosophyMission.closing.line1": "Ken Uzelf.",
    "philosophyMission.closing.line2": "Cultiveer Wijsheid.",
    "philosophyMission.closing.line3": "Leef met Deugd.",
    "philosophyMission.cta": "Begin Uw Reis",

    // ---------------- ACADEMY OF WISDOM ----------------
    "academy.nav": "Academie",
    "academy.hero.eyebrow": "De Academie",
    "academy.hero.title": "De Academie van Wijsheid",
    "academy.hero.subtitle.lead": "Het leven is het curriculum.",
    "academy.hero.subtitle.accent": "Deugd is het doel.",
    "academy.hero.cta": "Begin Uw Reis",
    "academy.hero.alt": "De ingang van de Academie van Praktische Wijsheid, StoicOs.ai",
    "academy.intro.title": "De Introductie van de Academie",
    "academy.intro.sub1": "Voordat u aan uw reis begint,",
    "academy.intro.sub2": "neem twee minuten om de visie achter de Academie te ervaren.",
    "academy.intro.video.coming": "Binnenkort: de officiële introductie van de Academie",
    "academy.intro.quote1": "Elke reis begint met één enkele stap.",
    "academy.intro.quote2": "Elk leven begint met één enkele keuze.",

    // ---- Living Library (Fase 1 — fundering) ----
    "nav.library": "Living Library",
    "library.hero.eyebrow": "Het Kennisfundament",
    "library.hero.title": "De Living Library",
    "library.hero.subtitle": "De groeiende wereldschat aan praktische wijsheid.",
    "library.hero.body1": "De wijsheid van het verleden bewaren.",
    "library.hero.body2": "Haar verbinden met het heden.",
    "library.hero.body3": "Haar voorbereiden op de toekomst.",
    "library.hero.cta": "Begin met Verkennen",
    "library.hero.backAcademy": "Terug naar de Academie",
    "library.hero.imageAlt": "De Living Library: een monumentaal marmeren gebouw op de campus van de Academie van Praktische Wijsheid",
    "library.hero.lead": "Het centrale kennisfundament van de Academie van Praktische Wijsheid.",
    "library.collections.title": "De Collecties",
    "library.card.open": "Betreed",
    "library.growing": "De Living Library wordt met zorg samengesteld. Nieuwe kennis wordt gaandeweg toegevoegd.",
    "library.category.emptyEyebrow": "In Curatie",
    "library.category.empty": "Deze sectie wordt momenteel samengesteld als onderdeel van de Living Library.",
    "library.category.back": "Terug naar de Living Library",
    "library.search.title": "Zoeken",
    "library.search.sub": "Vind alles in de Living Library: filosofen, concepten, deugden, praktijken en meer.",
    "library.search.placeholder": "Doorzoek de Living Library…",
    "library.search.button": "Zoeken",
    "library.search.hint": "Voer een zoekterm in om de Living Library te doorzoeken.",
    "library.search.empty": "Er zijn nog geen passende entries gepubliceerd.",
    "library.search.empty2": "De Living Library blijft groeien.",
    "library.search.explore": "Verken de Collecties",
    "library.seo.description": "Ontdek de Living Library van StoicOs.ai: een groeiende schatkamer van praktische wijsheid, stoïcijnse filosofie, originele bronnen, modern onderzoek en levenslang leren.",
    "library.entry.relatedTitle": "Vervolg Uw Reis",
    "library.journey.label": "Journey",
    "library.journey.soon": "In curatie. Deze Journey opent binnenkort.",
    "library.journey.return": "Terug naar The Living Library",
    "library.journey.wisdom.name": "Wisdom",
    "library.journey.wisdom.invite": "Begin waar elke deugd begint: bij helder oordeel.",
    "library.journey.courage.name": "Courage",
    "library.journey.courage.invite": "Zet wijs oordeel om in moedig handelen.",
    "library.journey.justice.name": "Justice",
    "library.journey.justice.invite": "Ontdek hoe wijsheid dienstbaarheid aan anderen wordt.",
    "library.journey.temperance.name": "Temperance",
    "library.journey.temperance.invite": "Ontdek hoe wijsheid het zelf bestuurt.",
    "library.journey.wisdom.greek": "Phronēsis",
    "library.journey.courage.greek": "Andreia",
    "library.journey.justice.greek": "Dikaiosynē",
    "library.journey.temperance.greek": "Sōphrosynē",
    "library.virtues.heroTitle": "De Deugden",
    "library.virtues.heroLine": "Karakter wordt niet door woorden alleen onderwezen, maar door oefening.",
    "library.virtues.choose": "Kies een deugd om uw Journey te beginnen",
    "library.rel.academy_lessons": "Academy-lessen",
    "library.rel.virtue_guide_conversations": "Virtue Guide-gesprekken",
    "library.rel.journal_articles": "Journal-artikelen",
    "library.rel.videos": "Video's",
    "library.rel.original_sources": "Originele Bronnen",
    "library.rel.modern_research": "Modern Onderzoek",
    "library.rel.related_concepts": "Gerelateerde Concepten",
    "library.rel.related_virtues": "Gerelateerde Deugden",
    "library.rel.related_philosophers": "Gerelateerde Filosofen",
    "library.rel.exercises": "Oefeningen",
    "library.rel.books": "Boeken",
    "library.cats.philosophers.title": "Filosofen",
    "library.cats.philosophers.desc": "De levens, stemmen en leringen van de stoïcijnse meesters.",
    "library.cats.concepts.title": "Concepten",
    "library.cats.concepts.desc": "De kernideeën die het stoïcijnse denken vormgeven.",
    "library.cats.virtues.title": "Deugden",
    "library.cats.virtues.desc": "Wijsheid, rechtvaardigheid, moed en matigheid: de vier pijlers.",
    "library.cats.practices.title": "Praktijken",
    "library.cats.practices.desc": "Dagelijkse oefeningen die filosofie omzetten in een manier van leven.",
    "library.cats.sources.title": "Originele Bronnen",
    "library.cats.sources.desc": "De klassieke teksten, in hun eigen woorden.",
    "library.cats.history.title": "Geschiedenis",
    "library.cats.history.desc": "Van de beschilderde zuilengang van Athene tot vandaag.",
    "library.cats.research.title": "Modern Onderzoek",
    "library.cats.research.desc": "Wat hedendaagse wetenschap zegt over de klassieke praktijk.",
    "library.cats.leadership.title": "Leiderschap",
    "library.cats.leadership.desc": "Eerst uzelf besturen, dan anderen.",
    "library.cats.relationships.title": "Relaties",
    "library.cats.relationships.desc": "Wijs leven met de mensen die u zijn toevertrouwd.",
    "library.cats.health.title": "Gezondheid",
    "library.cats.health.desc": "Zorg voor het lichaam in dienst van de geest.",
    "library.cats.finance.title": "Financiën",
    "library.cats.finance.desc": "Rijkdom, genoeg hebben en het wijze gebruik van fortuin.",
    "library.cats.society.title": "Samenleving",
    "library.cats.society.desc": "De wereldburger en het algemeen belang.",
    "library.cats.glossary.title": "Woordenlijst",
    "library.cats.glossary.desc": "Elk begrip uit het stoïcijnse vocabulaire, gedefinieerd.",
    "library.cats.search.title": "Zoeken",
    "library.cats.search.desc": "Vind alles in de Living Library.",

    "academy.why.eyebrow": "Waarom nog een Academie?",
    "academy.why.p1": "De meeste opleidingen leren mensen hoe ze de kost verdienen.",
    "academy.why.p2": "Zeer weinig leert hen hoe ze moeten leven.",
    "academy.why.p3": "Kennis is overal.",
    "academy.why.p4.lead": "Wijsheid is",
    "academy.why.p4.accent": "zeldzaam.",
    "academy.why.p5": "De Academie bestaat om daar verandering in te brengen.",

    "academy.how.eyebrow": "Hoe het werkt",
    "academy.how.title": "Eén reis, zes opeenvolgende bewegingen.",
    "academy.how.body": "De Academie leert geen vakken. Ze leert een ritme: de beweging waardoor een geleefd moment een deugd wordt, en deugd karakter.",
    "academy.how.step.experience":    "Ervaring",
    "academy.how.step.reflection":    "Reflectie",
    "academy.how.step.understanding": "Inzicht",
    "academy.how.step.virtue":        "Deugd",
    "academy.how.step.character":     "Karakter",
    "academy.how.step.wisdom":        "Wijsheid",

    "academy.map.title": "De Kaart van Praktische Wijsheid",
    "academy.map.p1": "Elke reis begint met inzicht in het geheel.",
    "academy.map.p2": "De Kaart van Praktische Wijsheid biedt een visueel overzicht van de Stoïcijnse traditie zoals deze binnen de Academy of Practical Wisdom wordt onderwezen.",
    "academy.map.p3": "Elk begrip is terug te voeren op de overgeleverde klassieke Stoïcijnse bronnen. De visuele indeling is een oorspronkelijke educatieve synthese van StoicOs.ai, ontwikkeld om het Stoïcisme begrijpelijk, praktisch en toegankelijk te maken.",
    "academy.map.alt": "De Kaart van Praktische Wijsheid: een visueel overzicht van de Stoïcijnse traditie zoals onderwezen binnen de Academy of Practical Wisdom",
    "academy.map.note.label": "Historische toelichting",
    "academy.map.note.text": "Elk begrip op deze kaart is bedoeld om terug te voeren te zijn op de overgeleverde klassieke Stoïcijnse bronnen. De visuele indeling is een oorspronkelijk onderwijsmodel van StoicOs.ai.",


    // ---------------- DE DRIE DISCIPLINES VAN HET STOÏCISME ----------------
    "academy.disc.eyebrow": "De Drie Disciplines van het Stoïcisme",
    "academy.disc.title": "Eén filosofie. Drie onafscheidelijke disciplines.",
    "academy.disc.body": "De oude Stoïcijnen onderwezen de ethiek nooit op zichzelf. Hun filosofie was één levend geheel van drie disciplines, door de oude meesters vergeleken met een tuin: de Logica de muur die beschermt, de Fysica de grond die voedt, de Ethiek de vrucht waarvoor de tuin bestaat. De Reiziger die deze Academie betreedt, ontdekt het Stoïcisme precies zoals een student die de antieke Stoa binnentrad het zou hebben ontdekt.",
    "academy.disc.concept.logic": "Logica leert hoe te denken.",
    "academy.disc.concept.physics": "Fysica leert wat de werkelijkheid is.",
    "academy.disc.concept.ethics": "Ethiek leert hoe te leven.",
    "academy.disc.concept.close": "Alleen samen vormen zij het Stoïcisme.",
    "academy.disc.topicsLabel": "Toekomstige Learning Journeys",
    "academy.disc.mystoicos.title": "Volg deze lessen in My StoicOs",
    "academy.disc.mystoicos.text": "Maak gratis uw My StoicOs aan en krijg toegang tot de eerste lessen van Logica, Fysica en Ethica, uw persoonlijke voortgang en uw opgeslagen reflecties.",
    "academy.disc.start.logic": "Start Logica in My StoicOs",
    "academy.disc.start.physics": "Start Fysica in My StoicOs",
    "academy.disc.start.ethics": "Start Ethica in My StoicOs",
    "academy.disc.freeAccount": "Gratis account vereist",

    "academy.disc.logic.title": "Stoïsche Logica",
    "academy.disc.logic.subtitle": "Hoe Wij tot Waarheid Komen",
    "academy.disc.logic.classical": "Logikē",
    "academy.disc.logic.body": "Voordat een Stoïcijn vraagt hoe te leven, vraagt hij hoe te weten. De Stoïsche Logica is de discipline van het denken zelf: hoe een indruk binnenkomt, hoe het oordeel haar onderzoekt, en wanneer een geest met recht “waar” mag zeggen. Zij leert hoe een mens behoort te denken alvorens te handelen.",
    "academy.disc.logic.topics": "Indrukken (Phantasiai)|Instemming (Synkatathesis)|Oordeel|Rede|Kennis|Waarheid|Wijsheid versus Mening|De Discipline van het Denken",
    "academy.disc.status.open": "Nu Geopend",
    "academy.nav.switcherLabel": "Wissel van discipline",
    "academy.nav.lessonNavLabel": "Lesnavigatie",
    "academy.nav.goTo.logic": "Ga naar Logica",
    "academy.nav.goTo.physics": "Ga naar Fysica",
    "academy.nav.goTo.ethics": "Ga naar Ethica",
    "academy.nav.name.logic": "Logica",
    "academy.nav.name.physics": "Fysica",
    "academy.nav.name.ethics": "Ethica",
    "academy.nav.active": "Actief",
    "academy.nav.comingSoon": "Binnenkort beschikbaar",
    "academy.nav.prevLesson": "Vorige les",
    "academy.nav.nextLesson": "Volgende les",
    "academy.nav.lessonN": "Les {n}",
    "academy.nav.position": "Fase {phase} \u00b7 Les {lesson} van {total}",
    "academy.nav.backToLesson": "Terug naar les",
    "academy.nav.backToPhase": "Terug naar faseoverzicht",
    "academy.lesson.virtueSeal": "Toets je oordeel aan de vier deugden.",
    "academy.nav.backToCourse": "Terug naar leergangsoverzicht",
    "academy.nav.resume": "Ga verder waar ik gebleven was",
    "academy.nav.openCourseFirst": "Open leergang",
    "academy.nav.reflectionSaved": "Reflectie bewaard",
    "academy.nav.lessonCompleted": "Les voltooid",
    "academy.nav.status.notStarted": "Niet gestart",
    "academy.nav.status.inProgress": "Bezig",
    "academy.nav.status.completed": "Voltooid",
    "academy.nav.lessonsCompleted": "{x} van {y} lessen voltooid",
    "academy.nav.openCourse": "Open leergang",
    "academy.nav.openPhase": "Open fase",
    "academy.nav.openLesson": "Open les",
    "academy.nav.completePhase": "Rond fase {n} af",
    "academy.nav.continueChamber": "Ga verder naar de Chamber",
    "academy.nav.continueToLesson": "Ga verder met les {n}",
    "academy.nav.viewProgress": "Bekijk mijn voortgang",
    "academy.nav.viewPhase": "Bekijk faseoverzicht",
    "academy.nav.viewPath": "Bekijk mijn ontwikkelpad",
    "academy.nav.unsavedWarning": "Je hebt niet-opgeslagen tekst. Toch vertrekken? Waar mogelijk is een concept bewaard.",
    "academy.nav.myWork": "Bekijk Mijn werk",
    "academy.mywork.title": "Mijn werk",
    "academy.mywork.subtitle": "Concepten, reflecties en voltooide oefeningen",
    "academy.mywork.empty": "Nog niets opgeslagen. Je concepten, reflecties en voltooide oefeningen verzamelen zich hier.",
    "academy.mywork.sort": "Meest recent opgeslagen eerst",
    "academy.mywork.filter.discipline": "Discipline",
    "academy.mywork.filter.status": "Status",
    "academy.mywork.filter.type": "Type",
    "academy.mywork.filter.all": "Alles",
    "academy.mywork.status.draft": "Concept",
    "academy.mywork.status.inProgress": "Bezig",
    "academy.mywork.status.completed": "Voltooid",
    "academy.mywork.status.archived": "Gearchiveerd",
    "academy.mywork.type.lesson": "Leswerk",
    "academy.mywork.type.record": "Record",
    "academy.mywork.type.practice": "Praktijklog",
    "academy.mywork.type.evidence": "Demonstratie",
    "academy.mywork.type.chamber": "Chamber",
    "academy.mywork.action.continue": "Verder bewerken",
    "academy.mywork.action.view": "Bekijken",
    "academy.mywork.action.gotoLesson": "Ga naar bijbehorende les",
    "academy.mywork.action.delete": "Verwijderen",
    "academy.mywork.deleteConfirm": "Dit opgeslagen werk verwijderen? Dit kan niet ongedaan worden gemaakt.",
    "academy.mywork.writtenIn.nl": "Geschreven in het Nederlands",
    "academy.mywork.writtenIn.en": "Geschreven in het Engels",
    "academy.mywork.createdAt": "Aangemaakt",
    "academy.mywork.savedAt": "Laatst opgeslagen",
    "academy.mywork.completedAt": "Voltooid op",
    "academy.mywork.lesson": "Les",
    "academy.mywork.phase": "Fase",
    "academy.mywork.storageNote": "Je werk wordt op de server bewaard, gekoppeld aan een anoniem ID in deze browser. Het is nog niet aan een account gekoppeld: het reist niet mee naar andere apparaten, en het wissen van browsergegevens verbreekt de koppeling.",
    "academy.save.saving": "Wijzigingen opslaan\u2026",
    "academy.save.saved": "✓ Opgeslagen in je StoicOs-account",
    "academy.save.failed": "Opslaan mislukt \u2014 probeer opnieuw",
    "academy.save.lastSaved": "Laatst opgeslagen op {date} om {time}",
    "academy.save.draftBtn": "Concept opslaan",
    "academy.save.completeBtn": "Opslaan en les voltooien",
    "academy.save.completedNote": "Les voltooid",
    "academy.save.keepWriting": "Verder schrijven",
    "academy.save.backToLesson": "Terug naar les",
    "academy.disc.status.preparation": "In Voorbereiding",
    "academy.disc.logic.cta": "Betreed de Hal van het Heldere Oordeel",

    "academy.disc.physics.title": "Stoïsche Fysica",
    "academy.disc.physics.subtitle": "De Werkelijkheid Begrijpen",
    "academy.disc.physics.classical": "Physikē",
    "academy.disc.physics.body": "Geen fysica in de moderne zin, maar het begrijpen van de Natuur, de Werkelijkheid en de Kosmos: het geordende geheel waarvan wij delen zijn. De Stoïcijnen hielden vol dat de ethiek pas werkelijk begrepen kan worden nadat de werkelijkheid zelf is begrepen. Een van de diepste vleugels van de Academie.",
    "academy.disc.physics.topics": "De Kosmos|Logos|Physis|Pneuma|Sympatheia|Voorzienigheid|Het Lot (Heimarmenē)|Causaliteit|De Menselijke Ziel|Hēgemonikon|Ekpyrosis|Leven in Overeenstemming met de Natuur",

    "academy.disc.ethics.title": "Stoïsche Ethiek",
    "academy.disc.ethics.subtitle": "De Kunst van het Leven",
    "academy.disc.ethics.classical": "Ēthikē",
    "academy.disc.ethics.body": "De Ethiek is waar Logica en Fysica een leven worden: de praktische toepassing van helder denken en begrepen werkelijkheid. Hier traint de Academie de Vier Kardinale Deugden, de passies en hun gezonde tegenhangers, en de dagelijkse vorming van karakter op de weg naar eudaimonia.",
    "academy.disc.ethics.topics": "De Vier Kardinale Deugden|Passende Handelingen (Kathēkonta)|Passies (Pathē)|Gezonde Emoties (Eupatheiai)|Karaktervorming|Dagelijkse Praktijk|De Prokoptōn|Eudaimonia",

    "academy.curriculum.eyebrow": "Het Eerste Curriculum",
    "academy.curriculum.title": "Dertien poorten naar een leven van praktijk.",
    "academy.curriculum.body": "Elke kaart opent een volledig pad van lezingen, reflecties, dialogen met Virtue Guide en dagelijkse oefeningen. Ga in uw eigen tempo. Het Curriculum wacht.",
    // ---------------- CURRICULUM CARDS (13 doors × 3 lines) ----------------
    // Classical foundations are universal (Greek/Latin) — same value
    // in EN and NL. Only titles and promises are translated.
    "academy.card.welcome":            "Welkom, Student",
    "academy.card.welcomeClassical":   "Prooimion",
    "academy.card.welcomeSub":         "Betreed de Stoa. Ontmoet uw mentor.",

    "academy.card.lifeCurriculum":         "Het Leven is het Curriculum",
    "academy.card.lifeCurriculumClassical":"Askēsis",
    "academy.card.lifeCurriculumSub":      "Maak van elke gewone dag een les in deugd.",

    "academy.card.knowThyself":         "Ken Uzelf",
    "academy.card.knowThyselfClassical":"Gnōthi Seauton",
    "academy.card.knowThyselfSub":      "Ontdek wie u bent voordat u besluit wie u wilt worden.",

    "academy.card.fourVirtues":         "De Vier Deugden",
    "academy.card.fourVirtuesClassical":"Aretē",
    "academy.card.fourVirtuesSub":      "Bouw het karakter dat elke beslissing leidt.",

    "academy.card.emotions":            "Emoties",
    "academy.card.emotionsClassical":   "Pathē",
    "academy.card.emotionsSub":         "Leer diep te voelen zonder uw kern te verliezen.",

    "academy.card.judgement":           "Oordeel",
    "academy.card.judgementClassical":  "Prohairesis",
    "academy.card.judgementSub":        "Leer de werkelijkheid helder te zien voordat u kiest.",

    "academy.card.selfMastery":         "Zelfbeheersing",
    "academy.card.selfMasteryClassical":"Enkrateia",
    "academy.card.selfMasterySub":      "Beheers de wil die elke andere keuze bestuurt.",

    // Deur 08 — Fortuna. Geen financiële cursus. De stoïcijnse omgang
    // met Fortuin: welvaart, verlies, succes, tegenspoed, vrijgevigheid,
    // nalatenschap, genoeg. Seneca (Ep. 4.7, 8, 91, 98) staat centraal.
    "academy.card.fortuna":             "Fortuna",
    "academy.card.fortunaClassical":    "Fortuna",
    "academy.card.fortunaSub":          "Beheers welvaart, succes en verlies zonder hun dienaar te worden.",

    "academy.card.relationships":         "Relaties",
    "academy.card.relationshipsClassical":"Philia",
    "academy.card.relationshipsSub":      "Cultiveer betekenisvolle banden door wijsheid, vertrouwen en deugd.",

    "academy.card.purpose":            "Bestemming",
    "academy.card.purposeClassical":   "Telos",
    "academy.card.purposeSub":         "Ontdek een leven geleid door zin in plaats van afleiding.",

    "academy.card.death":              "Dood",
    "academy.card.deathClassical":     "Memento Mori",
    "academy.card.deathSub":           "Laat sterfelijkheid u leren hoe u ten volle leeft.",

    "academy.card.resilience":         "Veerkracht",
    "academy.card.resilienceClassical":"Amor Fati",
    "academy.card.resilienceSub":      "Vorm tegenspoed om tot kracht en karakter.",

    "academy.card.leadership":         "Leiderschap",
    "academy.card.leadershipClassical":"Hēgemonikon",
    "academy.card.leadershipSub":      "Leid eerst uzelf, dien dan anderen met integriteit.",

    "academy.card.openPath":        "Betreed het pad",

    // ---------------- HOOFDSTUK I · DE POORT ----------------

    "academy.mentor.eyebrow": "Ontmoet Uw Mentor",
    "academy.mentor.title.lead": "Uw mentor",
    "academy.mentor.title.accent": "slaapt nooit.",
    "academy.mentor.body1": "Virtue Guide is geen chatbot. Het is uw persoonlijke mentor binnen de Academie: altijd bewust van het curriculum, altijd bewust van uw voortgang, altijd bereid.",
    "academy.mentor.body2": "Stel vragen. Reflecteer. Oefen. Groei.",
    "academy.mentor.body3": "Virtue Guide begeleidt u door elke les.",
    "academy.mentor.always1": "24 uur per dag.",
    "academy.mentor.always2": "365 dagen per jaar.",
    "academy.mentor.cta": "Spreek met Virtue Guide",

    "academy.future.eyebrow": "De Toekomstige Academie",
    "academy.future.title": "Dit is nog maar het begin.",
    "academy.future.body": "Het Eerste Curriculum is één pad. Na verloop van tijd opent de Academie nieuwe levenslange paden, elk een onderwerp dat een mensenleven nooit uitput.",
    "academy.future.wisdom":     "Wisdom",
    "academy.future.justice":    "Justice",
    "academy.future.courage":    "Courage",
    "academy.future.temperance": "Matigheid",
    "academy.future.purpose":    "Bestemming",
    "academy.future.love":       "Liefde",
    "academy.future.leadership": "Leiderschap",
    "academy.future.finance":    "Financiën",
    "academy.future.parenthood": "Ouderschap",
    "academy.future.health":     "Gezondheid",
    "academy.future.legacy":     "Nalatenschap",
    "academy.future.death":      "Dood",
    "academy.future.meaning":    "Zingeving",
    "academy.future.modernLife": "Modern Leven",
    "academy.future.footer": "Elk vak wordt een levenslang leerpad.",

    "academy.closing.line1": "U verlaat het leven niet om filosofie te studeren.",
    "academy.closing.line2": "U studeert filosofie dóór het leven zelf.",
    "academy.closing.cta":   "Begin Uw Reis",

    // ---------------- HOMEPAGE — ACADEMY CTA CARDS ----------------
    "homeAcademy.primary.eyebrow":  "De Academie van Wijsheid",
    "homeAcademy.primary.headline": "Het leven is het curriculum.",
    "homeAcademy.primary.accent":   "Deugd is het doel.",
    "homeAcademy.primary.body":     "Een levende school voor het onderzochte leven. Dertien poorten, één leven van praktijk, geleid door Marcus, Seneca en Epictetus, en door een mentor die nooit slaapt.",
    "homeAcademy.primary.cta":      "Betreed de Academie",

    "homeAcademy.secondary.eyebrow":  "Begin de praktijk",
    "homeAcademy.secondary.headline": "U verlaat het leven niet om filosofie te studeren.",
    "homeAcademy.secondary.accent":   "U studeert het dóór het leven zelf.",
    "homeAcademy.secondary.body":     "De Academie is waar reflectie discipline wordt, en discipline karakter. Ga in uw eigen tempo. Het curriculum wacht.",
    "homeAcademy.secondary.cta":      "Open de Academie",

    // ---------------- GUIDES ----------------
    "guides.eyebrow": "Onze Gidsen",
    "guides.headline1": "Drie stemmen.", "guides.headline2": "Tweeduizend jaar.",
    "guides.intro": "Elke gids brengt zijn eigen temperament: de keizer, de senator, de slaaf. Allen schreven over dezelfde vragen die u vandaag draagt.",
    "guides.marcusRole": "De Filosoof-Keizer", "guides.marcusMeta": "Romeins Keizer · 121–180 n.Chr.",
    "guides.marcusDesc": "Auteur van Overpeinzingen. Spreekt met kalme keizerlijke ernst, beschouwend en naar binnen gekeerd.",
    "guides.senecaRole": "De Staatsman & Brievenschrijver", "guides.senecaMeta": "Romeins Staatsman · ca. 4 v.Chr. – 65 n.Chr.",
    "guides.senecaDesc": "Auteur van Brieven aan Lucilius. Welbespraakt, warm, als een wijze oudere vriend.",
    "guides.epictetusRole": "De Vrijgelaten Slaaf", "guides.epictetusMeta": "Stoïcijns Filosoof · ca. 50–135 n.Chr.",
    "guides.epictetusDesc": "Auteur van de Diatriben en het Handboekje. Spreekt eenvoudig, direct, met gedisciplineerd vuur.",
    "guides.guidePrefix": "Gids",

    // ---------------- FEATURES ----------------
    "features.eyebrow": "De App",
    "features.headline1": "Geen zoveelste wellness-app.", "features.headline2": "Een oefening.",
    "features.dialogue.title": "AI-begeleide Dialoog",
    "features.dialogue.copy": "Voer een gesprek met stemmen gevormd door de overpeinzingen van Aurelius, de brieven van Seneca en de Diatriben van Epictetus.",
    "features.reflection.title": "Dagelijkse Reflectie",
    "features.reflection.copy": "Eén deugd om \u2019s ochtends te overwegen. Eén vraag om \u2019s avonds bij stil te staan.",
    "features.journal.title": "Privé Dagboek",
    "features.journal.copy": "Een end-to-end versleuteld notitieboek voor uw onderzochte leven. Alleen van u.",
    "features.goldenHour.title": "Het Gouden Uur",
    "features.goldenHour.copy": "Trage, warme sessies ontworpen voor de beschouwende delen van de dag.",
    "features.compass.title": "Deugd-Kompas",
    "features.compass.copy": "Volg wijsheid, moed, rechtvaardigheid en gematigdheid, de kardinale deugden, als een stille oefening.",
    "features.premium.title": "Stoïcijnse Begeleiding",
    "features.premium.copy": "Modellen afgestemd op de Stoïcijnse primaire teksten. Geen gemeenplaatsen, geen productiviteitstrucs. Alleen filosofie.",

    // ---------------- PREVIEW BAND ----------------
    "preview.eyebrow": "Een Stille Metgezel",
    "preview.headline1": "Ontworpen voor het", "preview.headline2": "gouden uur.",
    "preview.body": "Virtue Guide is gemaakt om langzaam gebruikt te worden. Vijf minuten bij zonsopkomst. Tien minuten voor het slapen. Net lang genoeg om uzelf te horen denken.",
    "preview.stat1": "Kardinale Deugden", "preview.stat2": "Gesprekken",
    "preview.stat3": "Dagelijkse Reflecties", "preview.stat4": "Notificaties, ooit",

    // ---------------- FINAL CTA ----------------
    "cta.eyebrow": "Begin",
    "cta.headline1": "Het onbeproefde leven", "cta.headline2": "is niet waard geleefd te worden.",
    "cta.body": "Download Virtue Guide. Spreek met de Stoïcijnen. Begin vandaag met de oefening.",
    "cta.available": "Officieel beschikbaar in de Apple App Store en de Google Play Store.",

    // ---------------- BUILDING IN PUBLIC (afsluitende sectie homepage) ----------------
    "buildingInPublic.eyebrow": "Een Levend Project",
    "buildingInPublic.title": "Bouwen in het Openbaar",
    "buildingInPublic.intro": "Je bent getuige van hoe StoicOs.ai en Virtue Guide worden gebouwd, net zoals Rome ooit gebouwd werd.",
    "buildingInPublic.line1": "Steen voor steen.",
    "buildingInPublic.line2": "Les voor les.",
    "buildingInPublic.line3": "Deugd voor deugd.",
    "buildingInPublic.body1": "Zoals Rome niet in één dag werd gebouwd, zo worden ook wijsheid, karakter en de werktuigen die ons helpen deze te cultiveren niet in één dag gevormd.",
    "buildingInPublic.body2": "StoicOs.ai en Virtue Guide evolueren voortdurend. Elke update, elke les, elke reflectie en elk onderdeel is een nieuwe steen, met zorg gelegd.",
    "buildingInPublic.outro": "We hopen dat je net zoveel van deze reis geniet als wij.",
    "buildingInPublic.thanks": "Dank dat je samen met ons aan de toekomst bouwt.",

    // ---------------- ROTATE HINT (alleen op mobiel) ----------------
    "rotateHint.title": "Draai je toestel.",
    "rotateHint.body": "Ontdek de volledige 16:3 cinematische Stoa.",

    // ---------------- FOUNDER STORY ----------------
    "founder.eyebrow": "Verhaal van de Oprichter",
    "founder.headline1": "Waarom", "founder.headline2": "bestaat.",
    "founder.role": "Oprichter, StoicOs", "founder.tagline": "Maker van Virtue Guide",
    "founder.quote1": "\u201CDe app is niet het doel. De app is het instrument.\u201D",
    "founder.toggleOpen": "Lees het Verhaal", "founder.toggleClose": "Verberg het Verhaal",
    "founder.letter": "Een persoonlijke brief van de oprichter",
    "founder.p1": "ijn motivatie is eigenlijk heel eenvoudig.",
    "founder.p2": "In de loop der jaren heb ik Stoïcisme, zelfkennis en praktische wijsheid bestudeerd. Deze ideeën hielpen mij door moeilijke perioden in mijn eigen leven, en ik geloof dat ze ook anderen kunnen helpen.",
    "founder.p3": "Virtue Guide is gemaakt om die wijsheid toegankelijker te maken.",
    "founder.p4": "Niet iedereen heeft de tijd, energie of behoefte om filosofische boeken te lezen. Toch zijn veel mensen op zoek naar begeleiding, reflectie en wat meer helderheid in hun dagelijks leven.",
    "founder.p5a": "De app is niet het doel.", "founder.p5b": "De app is het instrument.",
    "founder.p6": "Het doel is om mensen te helpen iets wijzer, iets rustiger en iets bewuster te leven.",
    "founder.p8": "Winst is niet het doel.",
    "founder.p9": "Het doel is om praktische wijsheid beschikbaar te maken wanneer mensen die nodig hebben.",
    "founder.quote2": "Kennis wordt pas wijsheid wanneer ze geleefd wordt.",
    "founder.p10": "Daarom gaat Virtue Guide niet over het verzamelen van citaten of het uit het hoofd leren van filosofie.",
    "founder.p11": "Het gaat erom wijsheid in het dagelijks leven te brengen.",
    "founder.cadence1": "Eén reflectie.", "founder.cadence2": "Eén gesprek.",
    "founder.cadence3": "Eén beslissing.", "founder.cadence4": "Eén dag per keer.",
    "founder.p12": "De Stoïcijnen leerden dat filosofie niet iets is om te bewonderen.",
    "founder.p13": "Het is iets om te beoefenen.",
    "founder.p14": "Tot wij op een dag worden teruggegeven aan de Natuur en aan de Logos waaruit wij voortkwamen.",
    "founder.closingMain": "Ken Uzelf.", "founder.closingSub": "Begrip Is Vrijheid.",

    // ---------------- FREE QUOTE MODAL ----------------
    "freeQuote.ctaOverline": "Gratis voor u",
    "freeQuote.ctaLabel": "Uw Stoïcijnse Reflectie",
    "freeQuote.modalEyebrow": "Uw Stoïcijnse Reflectie",
    "freeQuote.modalHeadline1": "Van citaten", "freeQuote.modalHeadline2": "naar praktijk.",
    "freeQuote.reflection": "Reflectie",
    "freeQuote.practice": "Oefening voor vandaag",
    "freeQuote.another": "Andere reflectie",
    "freeQuote.comingSoon": "Virtue Guide is nu beschikbaar.",
    "freeQuote.comingSoonBody": "Een metgezel voor dagelijkse reflectie, begeleid door Marcus, Seneca en Epictetus.",
    "freeQuote.emailPlaceholder": "u@voorbeeld.nl",
    "freeQuote.notify": "Hou Me Op De Hoogte",
    "freeQuote.sending": "Versturen\u2026",
    "freeQuote.successOverline": "U staat op de lijst",
    "freeQuote.successThanks": "Dank u.",
    "freeQuote.successBody": "Tot Virtue Guide er is: ken uzelf, en leef vandaag goed.",
    "freeQuote.footerTag": "Kennis wordt pas wijsheid wanneer ze geleefd wordt.",

    // ---------------- NOTIFY MODAL ----------------
    "notify.overline": "Binnenkort",
    "notify.headline1": "Iets nieuws is", "notify.headline2": "onderweg.",
    "notify.body": "Dit deel van StoicOs.ai wordt met zorg voorbereid. Laat uw e-mailadres achter en wij berichten u zodra het opent.",
    "notify.storeApple": "de Apple App Store",
    "notify.storeGoogle": "de Google Play Store",
    "notify.storeBoth": "de App Store en Google Play",
    "notify.emailLabel": "E-mailadres",
    "notify.button": "Hou Me Op De Hoogte",
    "notify.sending": "Versturen\u2026",
    "notify.noSpam": "Geen spam. Eén bericht, zodra de app klaar is.",
    "notify.successOverline": "U staat op de lijst",
    "notify.thanks": "Dank u.",
    "notify.successBody": "Wij schrijven u zodra het opent. Tot die tijd:",
    "notify.knowThyself": "ken uzelf.",
    "notify.close": "Sluiten",
    "notify.errInvalid": "Voer een geldig e-mailadres in.",
    "notify.errGeneric": "Er ging iets mis. Probeer het opnieuw of mail naar ek@stoicos.ai.",

    // ---------------- STORE BADGES ----------------
    "store.appstoreLine1": "Download in de", "store.appstoreLine2": "App Store",
    "store.playLine1": "Verkrijgbaar in", "store.playLine2": "Google Play",

    // ---------------- ABOUT ----------------
    "about.eyebrow": "Over StoicOs",
    "about.headline1": "Een besturingssysteem", "about.headline2": "voor het innerlijk leven.",
    "about.p1": "toicOs is een kleine studio die software bouwt voor het onderzochte leven. Wij geloven dat de belangrijkste technologie van het komende decennium geen productiviteitsinstrument zal zijn, maar het instrument waarmee wij wijzer leren leven.",
    "about.p2_pre": "Ons eerste product, ", "about.p2_em": "Virtue Guide",
    "about.p2_post": ", is een metgezel-app geworteld in de Stoïcijnse filosofie. Hij optimaliseert u niet. Hij maakt er geen spel van. Hij vraagt u om kort stil te staan bij één vraag, en te overwegen hoe een keizer, een senator of een vrijgelaten slaaf die tweeduizend jaar geleden leefde, die vraag had kunnen beantwoorden.",
    "about.p3": "Wij werken tussen Rome en het open internet. Wij laten ons leiden door het marmer en het gouden uur, door de discipline van de zuilengang en de rust van de cipres bij schemering. Wij bouwen langzaam. Wij brengen zelden iets uit. Wij hopen dat dit voelbaar is.",
    "about.statFounded": "Opgericht", "about.statBased": "Gevestigd", "about.statApp": "Eerste app",
    "about.basedValue": "Rome / Web",
    "about.contactLink": "Neem contact op",
    "about.nav.aria": "Over StoicOs.ai",
    "about.nav.why": "Waarom StoicOs.ai",
    "about.nav.founders": "Founder's Speech",
    "about.nav.stoa": "De Digital Stoa",
    "about.nav.disciplines": "Logica · Fysica · Ethica",
    "about.nav.lifelong": "Nooit te oud om te leren",
    "about.stoa.title": "De Digital Stoa",
    "about.stoa.body": "StoicOs.ai is gebouwd als een digitale Stoa: een rustige plaats waar de klassieke leraren — Marcus Aurelius, Epictetus, Seneca en Hipparchia — u begeleiden bij het beoefenen van praktische wijsheid. Geen cursusfabriek, maar een school in de oorspronkelijke betekenis: een plek om te oefenen, te onderzoeken en te groeien, in uw eigen tempo.",
    "about.disc.title": "Logica, Fysica en Ethica",
    "about.disc.logic.title": "Logica",
    "about.disc.logic.body": "Helder leren waarnemen en oordelen. U onderzoekt uw indrukken, herkent overhaaste conclusies en leert wanneer u wel of niet met een eerste indruk instemt.",
    "about.disc.physics.title": "Fysica",
    "about.disc.physics.body": "De werkelijkheid aanvaarden zoals zij is. U leert onderscheiden wat binnen en buiten uw macht ligt, en vindt rust in de orde van de natuur.",
    "about.disc.ethics.title": "Ethica",
    "about.disc.ethics.body": "Wijsheid omzetten in handelen. U oefent de vier deugden — wijsheid, moed, rechtvaardigheid en zelfbeheersing — in de keuzes van iedere dag.",
    "about.lifelong.title": "U bent nooit te oud om te leren.",
    "about.lifelong.body": "Wijsheid kent geen leeftijd. Of u nu aan het begin van uw volwassen leven staat, midden in uw loopbaan bent of terugkijkt op vele jaren ervaring: zolang u bereid bent uzelf eerlijk te onderzoeken, kunt u blijven leren. Niet om iemand anders te worden. Maar om bewuster te worden van wie u bent, hoe u oordeelt en wie u vanaf vandaag kiest te zijn.",
    "about.lifelong.quote": "Verspil geen tijd meer met discussiëren over hoe een goed mens hoort te zijn. Wees er één.",
    "about.lifelong.cite": "— Marcus Aurelius, Meditations 10.16",
    "about.lifelong.p1": "De Stoa is niet slechts iets om over te lezen. Zij vraagt om beoefening.",
    "about.lifelong.line1": "Niet alleen praten over wijsheid, maar wijzer kiezen.",
    "about.lifelong.line2": "Niet alleen spreken over moed, maar moedig handelen.",
    "about.lifelong.line3": "Niet alleen rechtvaardigheid bewonderen, maar rechtvaardig leven.",
    "about.lifelong.line4": "Niet alleen zelfbeheersing begrijpen, maar haar dagelijks oefenen.",
    "about.lifelong.p3": "Uw oefening begint met de eerstvolgende keuze die u maakt.",
    "about.closing": "Innerlijke vrijheid is de bestemming.",
    "join.innerFreedom": "Innerlijke vrijheid is de bestemming",

    // ---------------- PRIVACY ----------------
    "privacy.eyebrow": "Juridisch",
    "privacy.headline1": "Privacy", "privacy.headline2": "Beleid.",
    "privacy.effective": "Ingangsdatum: januari 2026",
    "privacy.s1.title": "1. Onze Toezegging",
    "privacy.s1.body": "StoicOs bouwt software voor het innerlijk leven. Wij behandelen uw gegevens met dezelfde discretie die wij voor onze eigen gegevens zouden verwachten. Wij verzamelen het minimum dat nodig is om Virtue Guide te laten werken, en wij verkopen of verhuren persoonlijke informatie aan niemand. Nooit.",
    "privacy.s2.title": "2. Welke Informatie Wij Verzamelen",
    "privacy.s2.body": "Accountinformatie (e-mail, optionele naam) wanneer u een account aanmaakt. Gebruiksinformatie (sessies, gebruikte functies) om de app te verbeteren. Inhoud van gesprekken die u kiest op te slaan in uw privédagboek, versleuteld opgeslagen en alleen voor u toegankelijk. Wij verzamelen geen contacten, locatie of apparaatkenmerken meer dan strikt nodig.",
    "privacy.s3.title": "3. Hoe Wij Informatie Gebruiken",
    "privacy.s3.body": "Om Virtue Guide te leveren en te onderhouden; om uw supportvragen te beantwoorden; om misbruik te detecteren en voorkomen; en om de app in geaggregeerde, geanonimiseerde vorm te verbeteren. Wij gebruiken uw dagboeknotities niet om AI-modellen te trainen.",
    "privacy.s4.title": "4. AI-Gesprekken",
    "privacy.s4.body": "Gesprekken met de Gids worden geleid via aanbieders van grote taalmodellen onder strikte verwerkersovereenkomsten. Wij bewaren geen gespreksgeschiedenis op onze servers tenzij u dat expliciet opslaat. Opgeslagen inhoud wordt op het apparaat versleuteld en gesynchroniseerd via end-to-end versleutelde kanalen.",
    "privacy.s5.title": "5. Gegevens Delen",
    "privacy.s5.body": "Wij delen gegevens alleen met de dienstverleners die nodig zijn om de app te laten werken (hosting, analyse, AI-verwerking) en alleen onder strikte verwerkersovereenkomsten. Wij verstrekken informatie alleen wanneer dit wettelijk verplicht is, na zorgvuldige beoordeling.",
    "privacy.s6.title": "6. Uw Rechten",
    "privacy.s6.body": "U kunt te allen tijde verzoeken om inzage, correctie of verwijdering van uw gegevens door te mailen naar ek@stoicos.ai. Wij respecteren alle rechten onder de AVG, CCPA en vergelijkbare wetgeving, ongeacht waar u woont.",
    "privacy.s7.title": "7. Kinderen",
    "privacy.s7.body": "Virtue Guide is bedoeld voor gebruikers van 16 jaar en ouder. Wij verzamelen niet bewust informatie van kinderen jonger dan 16.",
    "privacy.s8.title": "8. Wijzigingen",
    "privacy.s8.body": "Wij kunnen dit beleid van tijd tot tijd bijwerken. Belangrijke wijzigingen worden in de app en op stoicos.ai/privacy aangekondigd. Voortgezet gebruik van de app na zo\u2019n wijziging geldt als aanvaarding.",
    "privacy.s9.title": "9. Contact",
    "privacy.s9.body": "Vragen, verzoeken of opmerkingen kunt u sturen naar ek@stoicos.ai. Wij streven ernaar binnen 7 werkdagen te reageren.",
    "privacy.quote": "\u201CBegin nu te leven, en tel elke afzonderlijke dag als een afzonderlijk leven.\u201D",

    // ---------------- SUPPORT ----------------
    "support.eyebrow": "Support",
    "support.headline1": "Hoe kunnen wij", "support.headline2": "helpen?",
    "support.intro": "Wij zijn een klein team en lezen elk bericht zelf. Reken op een antwoord binnen twee werkdagen, meestal veel sneller.",
    "support.emailLabel": "Mail ons", "support.pressLabel": "Pers & Media", "support.readLabel": "Lees ons",
    "support.privacyValue": "Privacybeleid",
    "footer.termsLink": "Algemene Voorwaarden",
    "support.writeTitle": "Schrijf ons.",
    "support.writeSub": "Geen tickets, geen chatbots. Alleen een doordacht antwoord.",
    "support.fieldName": "Uw naam", "support.fieldEmail": "E-mail",
    "support.fieldTopic": "Onderwerp", "support.fieldMessage": "Uw bericht",
    "support.topicGeneral": "Algemene vraag", "support.topicAccount": "Account",
    "support.topicBug": "Een bug melden", "support.topicPress": "Pers & media", "support.topicOther": "Anders",
    "support.placeholderName": "Marcus", "support.placeholderMessage": "Vertel ons wat u bezighoudt...",
    "support.submit": "Verstuur Bericht",
    "support.sentTitle": "Uw bericht is onderweg.",
    "support.sentBody": "Als uw e-mailprogramma niet opende, schrijf dan naar ",
    "support.faqTitle": "Veelgestelde vragen.",
    "support.faq1q": "Wanneer komt Virtue Guide beschikbaar?",
    "support.faq1a": "Virtue Guide is nu beschikbaar in de Apple App Store én op Google Play. Download de gratis basisversie en begin vandaag uw stoïcijnse reis.",
    "support.faq2q": "Is de app gratis?",
    "support.faq2a": "Prijsdetails worden bij de lancering in de app gedeeld.",
    "support.faq3q": "Hoe gaat u om met mijn dagboeknotities?",
    "support.faq3a": "Dagboeknotities worden op uw apparaat versleuteld voordat ze worden gesynchroniseerd. Wij kunnen ze niet lezen, zelfs niet bij supportverzoeken. Zie ons Privacybeleid voor de volledige uitleg.",
    "support.faq4q": "Met welke filosofen kan ik spreken?",
    "support.faq4a": "Bij uitgave: Marcus Aurelius, Seneca en Epictetus. Wij verkennen zorgvuldig aanvullende stemmen: Musonius Rufus, Hierocles en de Hellenistische scholen daaromheen.",

    // ---------------- 404 ----------------
    "404.eyebrow": "404",
    "404.headline1": "Het pad is", "404.headline2": "verloren.",
    "404.body": "\u201CAls u zich druk maakt om iets buiten uzelf, ligt de pijn niet in het ding zelf, maar in uw oordeel erover.\u201D (Marcus Aurelius)",
    "404.return": "Terug naar home",

    // ---------------- LANGUAGE TOGGLE ----------------
    "lang.switchTo": "Wissel van taal",

    // ---------------- HERO (SEO H2 + new subtitle) ----------------
    "hero.h2a": "Ontwikkel wijsheid. Versterk karakter.",
    "hero.h2b": "Bouw uw Innerlijke Citadel.",
    "hero.subtitleSeo": "Virtue Guide is een AI-gedreven Stoïcijnse wijsheidscoach, geïnspireerd door Marcus Aurelius, Seneca en Epictetus, om uw oordeelsvermogen, emotionele veerkracht en zelfkennis te ontwikkelen.",

    // ---------------- MISSION (new closing quote) ----------------
    "mission.quoteLead": "Kennis is goedkoop.",
    "mission.quoteAccent": "Wijsheid is onbetaalbaar.",

    // ---------------- MEET THE SAGES (relabel) ----------------
    "sages.eyebrow": "Ontmoet de Wijzen",
    "sages.introExtra": "Deze app gaat niet over Stoïcisme. Het gaat over zelfkennis. Ken uzelf. De wijzen zijn gidsen.",
    "sages.meetCta": "Ontmoet {name}",

    // ---------------- WHAT IS VIRTUE GUIDE ----------------
    "wivg.eyebrow": "Wat is Virtue Guide?",
    "wivg.h2a": "Een AI-Stoïcijnse coach",
    "wivg.h2b": "voor het onderzochte leven.",
    "wivg.intro": "Virtue Guide is het eerste product van StoicOs. Geen leesapp over Stoïcisme, geen citatenfeed, geen meditatie-app. Het is een kleine, doelbewuste dagelijkse oefening om wijzer, rustiger en meer uzelf te worden, met drie wijzen, uw eigen privé-dagboek en een AI-levenscoach begrensd door deugd.",
    "wivg.col1.title": "Wat het doet",
    "wivg.col1.copy": "Virtue Guide is een AI-gedreven Stoïcijnse wijsheidscoach. U voert gesprekken met gidsen geïnspireerd door Marcus Aurelius, Seneca en Epictetus, wijzen die u helpen reflecteren op de dag, een beslissing te onderzoeken en te zien waar u wijzer kunt kiezen.",
    "wivg.col2.title": "Hoe het werkt",
    "wivg.col2.copy": "Elke dag brengt één korte reflectie en een optionele dialoog. De AI gebruikt ons Common Sense by Virtue™-raamwerk (Indruk, Redenering, Deugdtoets, Instemming, Oordeel, Keuze, Handeling, Reflectie) om indrukken om te zetten in onderzochte oordelen.",
    "wivg.col3.title": "Waarom het anders is",
    "wivg.col3.copy": "De meeste apps leveren content. Virtue Guide is een praktijk. Het doel is niet méér informatie. Het is zelfkennis, beter oordeel, emotionele veerkracht en karakter. Kennis is goedkoop. Wijsheid is onbetaalbaar.",

    // ---------------- COMMON SENSE BY VIRTUE ----------------
    "csbv.eyebrow": "Common Sense by Virtue™",
    "csbv.h2a": "Een raamwerk voor",
    "csbv.h2b": "wijzere beslissingen.",
    "csbv.intro": "De meeste slechte beslissingen komen niet voort uit gebrek aan intelligentie. Ze komen voort uit het overslaan van stappen: we stemmen in met een eerste indruk voordat we deze hebben onderzocht. Common Sense by Virtue™ is het achtstaps Stoïcijnse beslissingsproces dat Virtue Guide u doorloopt, totdat het tweede natuur wordt.",
    "csbv.step1.label": "Indruk",
    "csbv.step1.desc": "Merk de eerste, rauwe gedachte op, voordat u haar waar noemt.",
    "csbv.step2.label": "Redenering",
    "csbv.step2.desc": "Onderzoek hem. Wat zegt hij werkelijk? Klopt het?",
    "csbv.step3.label": "Deugdtoets",
    "csbv.step3.desc": "Wijsheid · Moed · Rechtvaardigheid · Gematigdheid. Houdt het stand?",
    "csbv.step4.label": "Instemming",
    "csbv.step4.desc": "Pas nu stemt u in (of weigert) om het als het uwe te nemen.",
    "csbv.step5.label": "Oordeel",
    "csbv.step5.desc": "Wat is de meest accurate, eerlijke lezing van de situatie?",
    "csbv.step6.label": "Keuze",
    "csbv.step6.desc": "Uit het oordeel wordt de volgende juiste stap zichtbaar.",
    "csbv.step7.label": "Handeling",
    "csbv.step7.desc": "Voer rustig uit. De inspanning is van u; de uitkomst niet.",
    "csbv.step8.label": "Reflectie",
    "csbv.step8.desc": "Aan het einde van de dag, onderzoek wat u gedaan heeft. Pas aan. Herhaal.",
    "csbv.footQuote": "\u201CTussen prikkel en reactie ligt een ruimte. In die ruimte ligt onze macht om onze reactie te kiezen.\u201D",
    "csbv.footAttrib": "Het Stoïcijnse inzicht",

    // ---------------- INNER CITADEL ----------------
    "citadel.eyebrow": "De Innerlijke Citadel",
    "citadel.h2a": "Bouw het deel van u",
    "citadel.h2b": "dat niets van buitenaf kan bereiken.",
    "citadel.intro": "Marcus Aurelius schreef over een innerlijke citadel: het overwogen oordeel van een mens, dat door geen belediging, verlies of fortuin kan worden binnengedrongen zonder toestemming. Virtue Guide is rond het versterken van die citadel opgebouwd. Elke reflectie, elke Stoïcijnse vraag, elk gesprek dient één rustige uitkomst: een onwrikbaar innerlijk leven.",
    "citadel.quote": "\u201CU heeft macht over uw geest, niet over uiterlijke gebeurtenissen. Besef dit, en u zult kracht vinden.\u201D",
    "citadel.quoteAttrib": "Marcus Aurelius",
    "citadel.pillar1.title": "Innerlijke Kracht",
    "citadel.pillar1.copy": "De gevestigde kern die niet wijkt wanneer het leven onaangekondigd arriveert.",
    "citadel.pillar2.title": "Emotionele Veerkracht",
    "citadel.pillar2.copy": "Niet de afwezigheid van gevoel, maar de discipline om er niet door beheerst te worden.",
    "citadel.pillar3.title": "Karakter",
    "citadel.pillar3.copy": "Wie u bent wanneer niemand kijkt, en wanneer iedereen kijkt.",
    "citadel.pillar4.title": "Stoïcijnse Stabiliteit",
    "citadel.pillar4.copy": "Een gelijkmoedigheid die standhoudt of de dag nu uitstekend, gewoon of zwaar is.",
    "citadel.pillar5.title": "Zelfbeheersing",
    "citadel.pillar5.copy": "Heerschappij over uw eigen aandacht, begeerten en instemming: de enige werkelijke macht.",

    // ---------------- HOW DIFFERENT ----------------
    "diff.eyebrow": "Hoe Virtue Guide anders is",
    "diff.h2a": "Geen zoveelste wellness-app.",
    "diff.h2b": "Een wijsheidspraktijk.",
    "diff.intro": "Er bestaan vele uitstekende Stoïcijnse en mindfulness-apps. We respecteren ze. Virtue Guide bevindt zich op een andere plek: een coach voor wijsheid, karakter en oordeel, geen contentbibliotheek, geen stemmingstracker, geen dagboek, geen meditatietimer. De onderstaande vergelijking is eerlijk over dat verschil.",
    "diff.colApp": "App",
    "diff.colThem": "Wat zij doen",
    "diff.colUs": "Wat Virtue Guide doet",
    "diff.row1.cat": "Stoïcijnse dagboek-app",
    "diff.row1.them": "Dagelijkse dagboekprompts gebaseerd op Stoïcijnse thema's.",
    "diff.row1.us": "Dagboek + AI-Stoïcijnse dialoog + Common Sense by Virtue™-beslissingsraamwerk.",
    "diff.row2.cat": "Stoïcijnse content + tracker",
    "diff.row2.them": "Citatenfeeds, stemmingstracking, dagboek, ademwerk.",
    "diff.row2.us": "Een coach, geen contentbibliotheek. AI-geleide reflectie die uw werkelijke beslissingen onderzoekt.",
    "diff.row3.cat": "Stoïcijnse nieuwsbrief & cursussen",
    "diff.row3.them": "Geselecteerde citaten en lessen, lezen en opnemen.",
    "diff.row3.us": "Geen lezen óver Stoïcisme, maar het praktiseren ervan, met drie wijzen als uw conversationele gidsen.",
    "diff.row4.cat": "Meditatie & seculiere filosofie",
    "diff.row4.them": "Geleide meditatie en reflectieve gesprekken.",
    "diff.row4.us": "Minder in stilte zitten, meer helder denken. Gebouwd voor de beslissingen van uw werkelijke dag.",
    "diff.row5.cat": "Mindfulness & welzijn",
    "diff.row5.them": "Rust, slaap, mindfulness-oefeningen.",
    "diff.row5.us": "Geen wellness. Een discipline. Ontworpen om wijsheid en karakter te ontwikkelen, niet alleen ontspanning.",
    "diff.disclaimer": "Apps en merken worden uitsluitend genoemd ter conceptuele vergelijking. Alle merken zijn eigendom van hun respectievelijke eigenaren.",

    // ---------------- FAQ PREVIEW + PAGE chrome ----------------
    "faq.eyebrow": "Veelgestelde vragen",
    "faq.h2a": "Vragen",
    "faq.h2b": "die het stellen waard zijn.",
    "faq.allLink": "Alle vragen",
    "faq.readMore": "Lees meer",
    "faq.pageIntro": "Alles wat u zou willen weten over Virtue Guide: de AI-Stoïcijnse coach, het raamwerk erachter, de Innerlijke Citadel, en hoe een kleine dagelijkse oefening de vorm van een leven verandert.",
    "faq.cta.eyebrow": "Begin de oefening",
    "faq.cta.h2a": "Begin uw oefening met ",
    "faq.cta.h2b": "Virtue Guide.",
    "faq.cta.body": "Virtue Guide is nu beschikbaar in de App Store en Google Play.",
    "faq.cta.aboutLink": "Over StoicOs",

    // ---------------- SUBLANDING (shared chrome) ----------------
    "sub.backAria": "Terug naar StoicOs Virtue Guide-homepage",
    "sub.commonQ": "Veelgestelde vragen",
    "sub.peopleAsk": "Mensen vragen ook.",
    "sub.seeAllFaq": "Bekijk alle vragen",
    "sub.cta.eyebrow": "Begin de oefening",
    "sub.cta.h2a": "Begin uw oefening met ",
    "sub.cta.h2b": "Virtue Guide.",
    "sub.cta.body": "Virtue Guide is nu beschikbaar in de App Store en Google Play.",

    // ---------------- STOIC COMPASS (NL — informal "je/jij" per brand brief) ----------------
    "compass.eyebrow": "Het Stoïcijnse Kompas",
    "compass.headlineLead": "Benieuwd welke Stoïcijnse gids",
    "compass.headlineAccent": "jou vandaag zou adviseren?",
    "compass.subheadline": "Beantwoord vier korte vragen en ontdek welke Stoïcijnse meester het beste aansluit bij de uitdaging waar jij nu voor staat.",
    "compass.startButton": "Start de Stoic Compass",
    "compass.meta": "Vier vragen · Ongeveer één minuut · Privé voor jou",
    "compass.progressLabel": "Vraag {n} van {total}",
    "compass.restartLabel": "Opnieuw beginnen",
    "compass.takeAgain": "Doe het kompas opnieuw",
    "compass.reflectionLabel": "Reflectievraag",
    "compass.downloadEyebrow": "Begin de oefening",
    "compass.downloadHeadline": "Ontmoet jouw gids in StoicOs.",

    // ---- Questions ----
    "compass.q1.title": "Wanneer het leven moeilijk voelt, waar zoek je dan het eerst naar?",
    "compass.q1.a": "Richting",
    "compass.q1.b": "Rust",
    "compass.q1.c": "Kracht",

    "compass.q2.title": "Wat frustreert jou het meest?",
    "compass.q2.a": "Gebrek aan controle",
    "compass.q2.b": "Gebrek aan helderheid",
    "compass.q2.c": "Handelen onder mijn waarden",

    "compass.q3.title": "Welke eigenschap bewonder je het meest?",
    "compass.q3.a": "Zelfdiscipline",
    "compass.q3.b": "Wijsheid",
    "compass.q3.c": "Moed",

    "compass.q4.title": "Waar heb je vandaag het meeste behoefte aan?",
    "compass.q4.a": "Perspectief",
    "compass.q4.b": "Discipline",
    "compass.q4.c": "Innerlijke rust",

    // ---- Sage bust labels ----
    "compass.bust.marcus": "Marcus Aurelius",
    "compass.bust.epictetus": "Epictetus",
    "compass.bust.seneca": "Seneca",
    "compass.quoteLabel": "Een Stoïcijnse raad",

    // ---- Result chrome ----
    "compass.result.eyebrow": "Jouw Stoïcijnse gids",

    // ---- Result: Marcus ----
    "compass.result.marcus.name": "Marcus Aurelius",
    "compass.result.marcus.body": "Jouw kompas wijst naar Marcus Aurelius, de filosoof-keizer. Je zoekt perspectief en kalme kracht; de gezette geest die standhoudt wanneer verantwoordelijkheid drukt.",
    "compass.result.marcus.quote": "U heeft macht over uw geest, niet over uiterlijke gebeurtenissen. Besef dit, en u zult kracht vinden.",
    "compass.result.marcus.quoteAttrib": "Marcus Aurelius, Overpeinzingen",
    "compass.result.marcus.cta": "Begin je oefening met Marcus",

    // ---- Result: Seneca ----
    "compass.result.seneca.name": "Seneca",
    "compass.result.seneca.body": "Jouw kompas wijst naar Seneca, de staatsman en briefschrijver. Je zoekt richting, discipline en de wijsheid om intentie om te zetten in oprechte handeling.",
    "compass.result.seneca.quote": "Het is niet dat we kort te leven hebben, maar dat we er veel van verspillen.",
    "compass.result.seneca.quoteAttrib": "Seneca, Brieven aan Lucilius",
    "compass.result.seneca.cta": "Begin je oefening met Seneca",

    // ---- Result: Epictetus ----
    "compass.result.epictetus.name": "Epictetus",
    "compass.result.epictetus.body": "Jouw kompas wijst naar Epictetus, de vrijgemaakte slaaf en leermeester. Je zoekt de innerlijke vrijheid die komt door je alleen te richten op wat werkelijk binnen jouw macht ligt.",
    "compass.result.epictetus.quote": "Sommige dingen zijn in onze macht, andere niet.",
    "compass.result.epictetus.quoteAttrib": "Epictetus, Enchiridion",
    "compass.result.epictetus.cta": "Begin je oefening met Epictetus",

    // ---------------- GETTING STARTED (collapsible homepage card) ----------------
    "gs.title": "Aan de slag",
    "gs.eyebrow": "Een korte rondleiding",
    "gs.intro": "Zes deuren naar de digitale Stoa. Begin waar je je toe aangetrokken voelt.",
    "gs.open": "Open gids",
    "gs.close": "Sluit gids",
    "gs.virtueguide.title": "Virtue Guide",
    "gs.virtueguide.desc": "Spreek met Marcus, Seneca en Epictetus.",
    "gs.journal.title": "Stoic Journal",
    "gs.journal.desc": "Lees artikelen, reflecties en praktische wijsheid.",
    "gs.cards.title": "Stoïcijnse Oefenkaarten",
    "gs.cards.desc": "Beoefen Stoïcijnse oefeningen voor het dagelijks leven.",
    "gs.insights.title": "Mijn Inzichten",
    "gs.insights.desc": "Ontdek persoonlijke reflecties en geleerde lessen.",
    "gs.books.title": "Mijn Boeken",
    "gs.books.desc": "Ontdek de boeken achter de reis.",
    "gs.about.title": "Over StoicOs.ai",
    "gs.about.desc": "Lees over de missie en de oprichter.",

    // ---------------- THE STOIC JOURNAL ----------------
    "journal.eyebrow": "Stoic Journal",
    "journal.title": "Stoic Journal",
    "journal.subA": "Oude Teksten.",
    "journal.subB": "Moderne Reflecties.",
    "journal.subC": "Tijdloze Vragen.",
    "journal.intro": "Een kleine redactionele bibliotheek met artikelen, essays en reflecties. Nieuwe bijdragen worden toegevoegd naarmate de oefening verdiept.",
    "journal.comingSoon": "De eerste artikelen worden geschreven. Kom binnenkort terug, of schrijf u in voor een rustig bericht zodra de eerste essays verschijnen.",
    "journal.categoriesLabel": "Categorieën",
    "journal.cat.all": "Alle",
    "journal.cat.marcus": "Marcus Aurelius",
    "journal.cat.epictetus": "Epictetus",
    "journal.cat.seneca": "Seneca",
    "journal.cat.stoicPractice": "Stoïcijnse Praktijk",
    "journal.cat.virtues": "Deugden",
    "journal.cat.modernLife": "Het moderne leven",
    "journal.cat.stoicismSociety": "Stoïcisme en samenleving",
    "journal.cat.virtueGuide": "Virtue Guide",
    // legacy keys
    "journal.cat.stoicism": "Stoïcisme",
    "journal.cat.selfKnowledge": "Zelfkennis",
    "journal.cat.reflections": "Reflecties",
    "journal.featuredLabel": "Uitgelicht",
    "journal.featuredTitle": "Hoe Oude Wijsheid Mij Door Een Moeilijke Periode Hielp",
    "journal.featuredAuthor": "Eduard Korporaal",
    "journal.featuredRole": "Oprichter, StoicOs.ai",
    "journal.featuredTeaser": "Een persoonlijk verslag van een terugkeer naar de Stoïcijnen in een seizoen waarin weinig anders houvast bood. Een essay over het langzame, ongehaaste werk van wijzer worden.",
    "journal.featuredReadCta": "Lees het artikel",
    "journal.browseLabel": "Bladeren",
    "journal.search.placeholder": "Doorzoek de Stoic Library…",
    "journal.hero.label": "Stoic Journal",
    "journal.hero.title": "Oude Teksten. Moderne Reflecties. Tijdloze Vragen.",
    "journal.hero.tagline": "Oude Teksten. Moderne Reflecties. Tijdloze Vragen.",
    "journal.hero.lede": "Een redactionele bibliotheek met artikelen, essays en reflecties voor het moderne leven.",
    "journal.hero.cta": "Ontdek het Journal",
    "journal.search.results": "resultaten",
    "journal.tags.philosophers": "Filosofen",
    "journal.tags.themes": "Thema's",
    "journal.tags.virtues": "Deugden",
    "journal.tags.practices": "Praktijken",
    "journal.readingTime": "min lezen",
    "journal.readMore": "Verder lezen",
    "journal.empty.title": "Nog geen artikelen in deze categorie.",
    "journal.empty.body": "Er is meer schrijfwerk onderweg. Blader door een andere categorie, of wacht rustig. Het werk gaat door.",
    "journal.backToIndex": "Terug naar Stoic Journal",
    "journal.article.publishedOn": "Gepubliceerd",
    "journal.article.readOther": "Andere artikelen",
    "journal.article.relatedTitle": "Gerelateerde artikelen",
    "journal.article.tagsLabel": "Onderwerpen",
    "journal.cta.eyebrow": "Zet je oefening voort",
    "journal.cta.title": "Zet je oefening voort in Virtue Guide.",
    "journal.cta.body": "De Basisversie is gratis te gebruiken. Begin vandaag je Stoïcijnse reis.",
    "journal.cta.download": "Gratis downloaden",

    // ---------------- STOIC TRAINING CARDS ----------------
    "cards.eyebrow": "Stoïcijnse Oefenkaarten",
    "cards.title": "Stoïcijnse Oefenkaarten",
    "cards.subtitle": "Voor het dagelijks leven",
    "cards.lede": "Wijsheid om in je zak mee te dragen.",
    "cards.intro": "Korte Stoïcijnse oefeningen, ontleend aan de klassieken en aangepast aan hoe we vandaag leven. Praktisch, herhaalbaar, om mee de dag in te dragen.",
    "cards.comingSoon": "De eerste 23 kaarten worden afgewerkt. Kom binnenkort terug om ze te lezen, downloaden en delen.",
    "cards.browseLabel": "Bladeren op",
    "cards.shown": "kaarten getoond",
    "cards.cat.all": "Alle",
    "cards.cat.virtue": "Deugd",
    "cards.cat.emotion": "Emotie",
    "cards.cat.philosophy": "Filosofie",
    "cards.cat.wisdom": "Wisdom",
    "cards.cat.courage": "Courage",
    "cards.cat.justice": "Justice",
    "cards.cat.temperance": "Temperance",
    "cards.cat.relationships": "Relaties",
    "cards.cat.work": "Werk",
    "cards.cat.selfKnowledge": "Zelfkennis",
    "cards.cat.difficultTimes": "Moeilijke tijden",
    "cards.downloadCard": "Kaart downloaden",
    "cards.shareCard": "Kaart delen",
    "cards.empty.title": "Nog geen kaarten in deze categorie.",
    "cards.empty.body": "De bibliotheek groeit met de seizoenen. Probeer een andere categorie, of kom binnenkort terug.",
    "cards.modal.close": "Sluiten",
    "cards.share.download": "PNG downloaden",
    "cards.share.downloaded": "Gedownload",
    "cards.share.native": "Delen…",
    "cards.share.shareTo": "Delen naar",
    "cards.share.copyLink": "Link kopiëren",
    "cards.share.linkCopied": "Link gekopieerd",
    "cards.share.instagramHint": "Kaart opgeslagen. Open Instagram en plaats de afbeelding vanuit je foto's.",

    // ---------------- MY INSIGHTS ----------------
    "insights.eyebrow": "Mijn Inzichten",
    "insights.title": "Mijn Inzichten",
    "insights.subtitle": "Reflecties van een moderne Stoïcijn.",
    "insights.intro": "Persoonlijke reflecties en geleerde lessen. Geen Stoïcijnse citaten, maar kleine, eerlijke notities uit een leven dat probeert ze te leven.",
    "insights.byline": "Door Ed Korporaal",
    "insights.comingSoon": "De eerste inzichten worden met de hand geschreven. Nieuwe bijdragen verschijnen rustig zodra ze komen.",
    "insights.shareLabel": "Delen",

    // ---------------- MY BOOKS ----------------
    "books.eyebrow": "Mijn Boeken",
    "books.title": "Mijn Boeken",
    "books.subtitle": "Een persoonlijke reis door oude wijsheid.",
    "books.intro": "De bibliotheek achter de oefening: oude edities in de hand gehouden, moderne vertalingen die laat in de avond gelezen worden, vrij beschikbare teksten voor iedereen, en enkele zeldzame boeken die nog op de zoektocht staan.",
    "books.comingSoon": "Elk boek wordt gefotografeerd en van een korte notitie voorzien. De eerste planken verschijnen hier binnenkort.",
    "books.s1.title": "Oude Werken",
    "books.s1.desc": "Oude edities in de hand gehouden, kleine vensters op hoe eerdere lezers deze teksten ontmoetten.",
    "books.s1.b1.title": "Enchiridion",
    "books.s1.b1.year": "1670 / 1680",
    "books.s1.b1.note": "Een vroege gedrukte Enchiridion van Epictetus, het kleine handboekje dat lezers bijna twee millennia heeft geleid.",
    "books.s1.b2.title": "Stoicos Apatheias",
    "books.s1.b2.year": "Historische editie",
    "books.s1.b2.note": "Een historische editie over de Stoïcijnse leer van de apatheia: vrijheid van overheerst te worden door hartstochten.",
    "books.s2.title": "Modern Stoïcisme",
    "books.s2.desc": "De boeken die nu in bezit zijn en bestudeerd worden. Een groeiende verzameling.",
    "books.s2.b1": "Overpeinzingen · Marcus Aurelius",
    "books.s2.b2": "Brieven aan Lucilius · Seneca",
    "books.s2.b3": "The Practicing Stoic · Ward Farnsworth",
    "books.s2.b4": "How to Think Like a Roman Emperor · Donald Robertson",
    "books.s2.b5": "Andere titels uit Ed's persoonlijke collectie",
    "books.s3.title": "Essentiële Stoïcijnse Teksten",
    "books.s3.desc": "Werken uit het publieke domein, voor iedereen toegankelijk. Een goed beginpunt voor de oefening.",
    "books.s3.whyMatters": "Waarom dit boek ertoe doet",
    "books.s3.readOnline": "Lees online",
    "books.s3.b1.title": "Overpeinzingen",
    "books.s3.b1.author": "Marcus Aurelius",
    "books.s3.b1.why": "Het privédagboek van een keizer, voor zichzelf geschreven, niet voor ons. De helderste toegang tot geleefd Stoïcisme.",
    "books.s3.b1.url": "https://www.gutenberg.org/ebooks/2680",
    "books.s3.b2.title": "Enchiridion",
    "books.s3.b2.author": "Epictetus",
    "books.s3.b2.why": "Een compact handboek over wat in onze macht ligt en wat niet. De kortste weg naar Stoïcijnse discipline.",
    "books.s3.b2.url": "https://www.gutenberg.org/ebooks/45109",
    "books.s3.b3.title": "Brieven aan Lucilius",
    "books.s3.b3.author": "Seneca",
    "books.s3.b3.why": "Een lange briefwisseling over hoe te leven: warm, welbespraakt, en zo dicht bij een wijze oudere vriend als een boek kan komen.",
    "books.s3.b3.url": "https://www.gutenberg.org/ebooks/56075",
    "books.s4.title": "Op Mijn Zoektocht",
    "books.s4.desc": "Boeken die Ed nog hoopt te verwerven, een levenslange zoektocht naar edities die de weg van het Stoïcisme door de geschiedenis markeren.",
    "books.s4.b1": "Zeldzame Marcus Aurelius-edities",
    "books.s4.b2": "Zeldzame Seneca-edities",
    "books.s4.b3": "Werken van Justus Lipsius",
    "books.s4.b4": "Andere historische Stoïcijnse teksten",

    // ---------------- SHARED PLACEHOLDER ----------------
    "placeholder.notifyTitle": "Word stil op de hoogte gehouden",
    "placeholder.notifyBody": "Laat uw e-mail achter en wij schrijven u eenmalig zodra deze sectie opent. Geen ruis.",
    "placeholder.notifyButton": "Houd mij op de hoogte",
    "placeholder.exploreOther": "Verken de rest van de Stoa",

    // ================ DE AGORA ================
    "agora.hero.eyebrow": "Stoic Os · De Agora",
    "agora.hero.title": "Ga de Stoa Binnen",
    "agora.hero.subtitle": "Voor je de Stoa leert, moet je haar voelen.",
    "agora.hero.line1": "Leer Wijsheid.",
    "agora.hero.line2": "Beoefen Deugd.",
    "agora.hero.line3": "Leef Goed.",
    "agora.hero.cta": "Begin de Reis",
    "agora.hero.scroll": "Loop binnen",
    "agora.hero.alt": "Zonovergoten panorama van het oude Athene: marmeren zuilengang, burgers op de agora, de Akropolis bekroont de stad in gouden licht.",

    "agora.intro": "Voor je de Stoa leert, moet je haar voelen. Loop langzaam. Er is hier geen leerplan. Er is alleen een lange zaal van licht, en een vraag die op je wacht sinds Athene: hoe zou ik moeten leven?",
    "agora.continueJourney": "Vervolg de Reis",
    "agora.readMore": "Lees Verder",

    "agora.placeholder": "Binnenkort. De plek wordt gebouwd. De wijsheid volgt.",

    // I — De Griekse Dageraad
    "agora.s1.numeral": "II",
    "agora.s1.title": "De Griekse Dageraad",
    "agora.s1.dates": "334 – 206 v.Chr.",
    "agora.s1.figures": "Zeno van Citium · Cleanthes · Chrysippus",
    "agora.s1.atmosphere": "Ochtendlicht boven Athene. Olijfbomen. Marmeren zuilen. Het begin van de Stoa.",
    "agora.s1.body": "Een schipbreukelinge koopman loopt een boekwinkel in Athene binnen en vertrekt nooit meer. De school die hij sticht draagt de naam van de beschilderde zuilengang waar hij in de open lucht onderwijst, de stoa poikilē. Iedereen mag luisteren. De eerste filosofie van het alledaagse wordt geboren onder een dak van olijflicht.",
    "agora.s1.alt": "Zeno van Citium onderwijst zittende studenten op marmeren tegels onder een zuilengang. De Akropolis verrijst bij zonsondergang, ingelijst door olijftakken.",

    // II — De Stoa Poikile (de beschilderde zuilengang, school van de rede)
    "agora.stoa.numeral": "III",
    "agora.stoa.title": "De Stoa Poikile",
    "agora.stoa.subtitle": "De School van de Rede",
    "agora.stoa.atmosphere": "Onder de beschilderde zuilengang. Fresco's warm in het middaglicht. Stemmen, dan stillere stemmen, dan een vraag.",
    "agora.stoa.q1": "Geen heilige mysteriën.",
    "agora.stoa.q2": "Geen verborgen leerstellingen.",
    "agora.stoa.q3": "Alleen waarneming.",
    "agora.stoa.q4": "Alleen dialoog.",
    "agora.stoa.q5": "Alleen rede.",
    "agora.stoa.p1": "Onder de beschilderde zuilengang van Athene kwamen mensen niet bijeen om te aanbidden, maar om te denken. Kooplieden zaten naast studenten. Burgers debatteerden met reizigers. Vragen waren welkom. Argumenten werden beproefd. Ideeën werden uitgedaagd.",
    "agora.stoa.p2": "De Stoa was niet gebouwd op openbaring of gezag. Zij was gebouwd op rede.",
    "agora.stoa.p3": "Hier werd wijsheid niet doorgegeven. Zij werd samen ontdekt.",
    "agora.stoa.alt": "De beschilderde zuilengang van het oude Athene tijdens het gouden uur. Burgers, studenten, reizigers en een oudere filosoof in levendige dialoog onder fresco-wanden en marmeren zuilen; de Akropolis zichtbaar in de verte.",

    // III — Cleanthes (discipline en toewijding)
    "agora.cleanthes.numeral": "IV",
    "agora.cleanthes.dates": "331 – 232 v.Chr.",
    "agora.cleanthes.title": "Cleanthes",
    "agora.cleanthes.subtitle": "Discipline en Toewijding",
    "agora.cleanthes.virtues": "Discipline · Volharding · Nederigheid · Toewijding",
    "agora.cleanthes.q1": "Waar Zeno de deur opende,",
    "agora.cleanthes.q2": "bleef Cleanthes toen anderen vertrokken.",
    "agora.cleanthes.p1": "Na Zeno kwam de toets van de volharding.",
    "agora.cleanthes.p2": "Cleanthes werd niet geboren in rijkdom. 's Nachts droeg hij water door de straten van Athene; overdag studeerde hij filosofie.",
    "agora.cleanthes.p3": "Anderen bewonderden wijsheid. Cleanthes beoefende haar. Hij geloofde dat karakter wordt gebouwd door discipline, inspanning en trouw aan wat juist is.",
    "agora.cleanthes.p4": "Onder zijn leiding werd de Stoa meer dan een idee. Zij werd een manier van leven.",
    "agora.cleanthes.alt": "Cleanthes spreekt zijn leerlingen toe onder de zuilengang van de Stoa. Aardetint toga, marmeren zuilen, fresco-wand, de Akropolis zichtbaar tussen de zuilen in gouden middaglicht.",

    // V — Chrysippus (de architect van de Stoïcijnse logica)
    "agora.chrysippus.numeral": "V",
    "agora.chrysippus.dates": "279 – 206 v.Chr.",
    "agora.chrysippus.title": "Chrysippus",
    "agora.chrysippus.subtitle": "Het Systeem van de Rede",
    "agora.chrysippus.note": "Chrysippus van Soli · Derde hoofd van de Stoïcijnse school · Vaak genoemd \u201CDe Tweede Stichter van het Stoïcisme\u201D",
    "agora.chrysippus.concepts": "Indruk · Redeneren · Instemmen · Oordelen · Handelen",
    "agora.chrysippus.q1": "Om goed te leven,",
    "agora.chrysippus.q2": "moet men eerst leren helder te denken.",
    "agora.chrysippus.p1": "Athene zat vol meningen. De haven zat vol kooplieden, zeelieden, politici en filosofen. Iedereen had een standpunt. Niet elk standpunt was waar.",
    "agora.chrysippus.p2": "Chrysippus geloofde dat wijsheid begint waar indrukken worden onderzocht in plaats van blindelings aanvaard. Aan drukke tafels en onder het rumoer van de stad werden argumenten beproefd. Vragen werden uitgedaagd. Aannames werden blootgelegd.",
    "agora.chrysippus.p3": "De rede werd een instrument van vrijheid. Wat ertoe doet, is niet wat waar lijkt te zijn. Wat ertoe doet, is wat het onderzoek doorstaat.",
    "agora.chrysippus.p4": "Door Chrysippus kreeg het Stoïcisme zijn structuur. Door de rede werd filosofie praktisch.",
    "agora.chrysippus.alt": "Een oudere filosoof in lichte toga gebaart midden in een argument aan een houten tafel bij de haven. Drie metgezellen luisteren aandachtig. Achter hen: aangemeerde schepen, een drukke markt en de Akropolis die de stad bekroont in het gouden uur.",

    // VI — De Brug naar Rome (Panaetius)
    "agora.bridge.numeral": "VI",
    "agora.bridge.dates": "ca. 185 – 110 v.Chr.",
    "agora.bridge.title": "De Brug naar Rome",
    "agora.bridge.subtitle": "Toen Griekse Wijsheid de Romeinse Macht Ontmoette",
    "agora.bridge.note": "Panaetius van Rhodos · ca. 185 – 110 v.Chr. · Stoïcijns filosoof · Leraar en raadsman van invloedrijke Romeinse staatslieden · Algemeen beschouwd als de filosoof die het Stoïcisme tot de Romeinse elite bracht",
    "agora.bridge.concepts": "Wijsheid · Karakter · Plicht · Cultuur · Beschaving",
    "agora.bridge.q1": "Rome veroverde Griekenland met legioenen.",
    "agora.bridge.q2": "Griekenland veroverde Rome met ideeën.",
    "agora.bridge.p1": "De Middellandse Zee behoorde nu Rome toe. Zijn legioenen bewaakten de havens. Zijn senatoren bestuurden een groeiende republiek. Zijn vaandels wapperden over de bekende wereld. De macht had haar hoofdstad gekozen.",
    "agora.bridge.p2": "Toch gebeurde er in deze tijd van verovering iets onverwachts. De veroveraars werden studenten. Romeinse staatslieden keken voorbij rijkdom, voorbij militaire overwinning en voorbij politiek. Zij keken naar Griekenland, niet voor land, niet voor goud, maar voor wijsheid.",
    "agora.bridge.p3": "Panaetius van Rhodos stond tussen twee beschavingen. Achter hem lag Athene. Voor hem stond Rome. Hij droeg geen leger. Geen vloot. Geen gezag. Alleen ideeën: over karakter, over plicht, over rede, over deugd.",
    "agora.bridge.p4": "Door Panaetius stak het Stoïcisme de zee over. De filosofie die geboren werd onder de beschilderde zuilengang van Athene begon haar reis naar het hart van Rome. Onder de waakzame ogen van soldaten en senatoren ontstond een ander soort macht: de macht van een onderzocht leven. Het zwaard had gebieden veroverd. Ideeën zouden eeuwen veroveren.",
    "agora.bridge.alt": "Een Griekse filosoof in lichte toga spreekt een kring Romeinse senatoren en een centurion toe aan een stenen tafel bij de haven in de avondzon. Romeinse oorlogsschepen voeren een adelaarsvaandel; zuilen en standbeelden omzomen de kade; de stad Rome verrijst in de verte.",

    // VII — Seneca (wijsheid te midden van macht)
    "agora.seneca.numeral": "VII",
    "agora.seneca.dates": "4 v.Chr. – 65 n.Chr.",
    "agora.seneca.title": "Seneca",
    "agora.seneca.subtitle": "Wijsheid Te Midden van Macht",
    "agora.seneca.note": "Lucius Annaeus Seneca · 4 v.Chr. – 65 n.Chr. · Romeins Stoïcijns filosoof · Staatsman · Auteur van de Brieven aan Lucilius · Raadsman van keizer Nero · Een van de meest invloedrijke stemmen van het Stoïcisme",
    "agora.seneca.concepts": "Deugd · Lot · Karakter · Sterfelijkheid · Innerlijke Vrijheid",
    "agora.seneca.q1": "Niet de mens die te weinig heeft,",
    "agora.seneca.q2": "maar de mens die naar meer hunkert,",
    "agora.seneca.q3": "is arm.",
    "agora.seneca.qattr": "Seneca",
    "agora.seneca.p1": "Rome was nu het middelpunt van de wereld. Zijn wegen doorkruisten continenten. Zijn legers bewaakten een rijk. Zijn heersers beschikten over immense macht. Toch ontvouwde zich onder de marmeren grandeur van Rome een andere strijd: de strijd van binnen.",
    "agora.seneca.p2": "Seneca leefde dicht bij de macht. Dichter dan de meeste filosofen ooit durfden. Hij adviseerde keizers. Hij verzamelde rijkdom. Hij bewoog zich door paleizen en hoven. Toch keerden zijn geschriften steeds terug naar dezelfde vraag: hoe kan een mens vrij blijven?",
    "agora.seneca.p3": "In zijn Brieven aan Lucilius sprak Seneca niet als een verre leraar, maar als een metgezel. Hij schreef over angst. Over woede. Over lotgeval. Over de dood. Over de dagelijkse oefening van de filosofie. Zijn wijsheid was niet bedoeld voor scholen. Zij was bedoeld voor het leven.",
    "agora.seneca.p4": "Toen Nero's bewind donkerder werd, trok Seneca zich geleidelijk terug uit het openbare leven. Na de Pisonische samenzwering van 65 n.Chr. werd hij van betrokkenheid beschuldigd. Of hij schuldig of onschuldig was, blijft onzeker. Nero gebood hem zelf zijn leven te beëindigen. Seneca gehoorzaamde met kalmte. De filosoof die zo vaak over sterfelijkheid had geschreven, stond er nu zelf voor.",
    "agora.seneca.p5": "Zijn laatste les werd niet geschreven. Zij werd geleefd.",
    "agora.seneca.alt": "Seneca schrijvend aan een stenen tafel in zijn villatuin bij zonsondergang. Cipressen, met klimop begroeide zuilengang, marmeren beelden, een spiegelvijver en de stad Rome die in het gouden uur in de verte oprijst.",

    // VIII — Epictetus (de vrijheid van binnen)
    "agora.epictetus.numeral": "VIII",
    "agora.epictetus.dates": "ca. 50 – 135 n.Chr.",
    "agora.epictetus.title": "Epictetus",
    "agora.epictetus.subtitle": "De Vrijheid Van Binnen",
    "agora.epictetus.note": "Epictetus · ca. 50 – 135 n.Chr. · Geboren als slaaf · Later een van de meest invloedrijke Stoïcijnse leraren in de geschiedenis · Zijn leer is overgeleverd in de Discourses en de Enchiridion, opgetekend door Arrianus",
    "agora.epictetus.concepts": "Vrijheid · Zelfbeheersing · Discipline · Aanvaarding · Karakter",
    "agora.epictetus.q1": "Geen mens is vrij die geen meester over zichzelf is.",
    "agora.epictetus.qattr": "Epictetus",
    "agora.epictetus.p1": "Epictetus werd geboren als slaaf. Hij bezat niets. Zijn toekomst behoorde aan anderen. Zijn lichaam kon worden gecommandeerd. Zijn bewegingen werden beheerst. Zijn leven werd gestuurd door de wil van een ander. En toch ontdekte hij, zelfs als slaaf, iets wat geen meester hem kon afnemen: de vrijheid van de geest.",
    "agora.epictetus.p2": "Nadat hij zijn vrijheid had verkregen, werd Epictetus leraar. Niet in paleizen. Niet in de senaat. Niet onder de rijken. Maar onder gewone mensen die een betere manier van leven zochten.",
    "agora.epictetus.p3": "Zijn boodschap was eenvoudig. Sommige dingen liggen in onze macht. Sommige dingen niet. Verwarring begint waar wij het ene voor het andere aanzien. Vrede begint waar wij het verschil leren kennen.",
    "agora.epictetus.p4": "Leerlingen reisden grote afstanden om hem te horen spreken. Zij kwamen niet voor theorieën. Zij kwamen voor helderheid. Voor moed. Voor praktische wijsheid. Voor een manier om standvastig te blijven in een onzekere wereld.",
    "agora.epictetus.p5": "Epictetus leerde dat vrijheid niet voortkomt uit rijkdom. Noch uit status. Noch uit macht. Zij komt voort uit zelfbeheersing. Wie zijn oordelen beheerst, wordt onoverwinnelijk.",
    "agora.epictetus.p6": "Zijn leerling Arrianus tekende zijn lessen op. Door die geschriften zou de stem van een voormalige slaaf door de eeuwen weerklinken, en uiteindelijk een keizer bereiken met de naam Marcus Aurelius.",
    "agora.epictetus.alt": "Epictetus, getekend door de jaren en grijsgebaard, zit voor een lage houten tafel vol boekrollen en kleine houten tabletten, één vinger geheven terwijl hij onderwijst. Zijn leerlingen leunen naar voren in aardetint toga's. IJzeren ketenen hangen aan de gebarsten muur achter hem; door de open boog gloeit de tempel van een oud bergstadje in het gouden uur.",

    // IX — Marcus Aurelius (de jonge filosoof)
    "agora.marcus.numeral": "IX",
    "agora.marcus.dates": "121 – 180 n.Chr.",
    "agora.marcus.title": "Marcus Aurelius",
    "agora.marcus.subtitle": "De Filosoof",
    "agora.marcus.note": "Marcus Aurelius · 121 – 180 n.Chr. · Romeins keizer · Leerling van het Stoïcisme · Geadopteerde erfgenaam van Antoninus Pius · Auteur van de Overpeinzingen · Algemeen beschouwd als een van de wijste heersers uit de geschiedenis",
    "agora.marcus.concepts": "Karakter · Plicht · Nederigheid · Filosofie · Dienstbaarheid",
    "agora.marcus.q1": "Verspil geen tijd meer met betogen hoe een goed mens moet zijn.",
    "agora.marcus.q2": "Wees er een.",
    "agora.marcus.qattr": "Marcus Aurelius",
    "agora.marcus.p1": "Marcus Aurelius werd in 121 n.Chr. geboren als Marcus Annius Verus. Van jongs af aan merkten zij die hem omringden iets ongewoons op. Een ernst. Een oprechtheid. Een stille toewijding aan plicht.",
    "agora.marcus.p2": "Keizer Hadrianus zag deze eigenschappen eveneens. Hij gaf de jongen, naar verluidt, een bijnaam: Verissimus, \u201CDe Meest Waarachtige,\u201D \u201CDe Meest Oprechte.\u201D Niet vanwege ambitie. Niet vanwege status. Maar vanwege karakter.",
    "agora.marcus.p3": "De toekomst van Marcus veranderde toen Hadrianus Antoninus Pius adopteerde en van hem eiste dat hij twee jonge erfgenamen zou adopteren: Marcus Aurelius en Lucius Verus. De weg naar de troon werd voor hem gekozen. Toch zag Marcus het keizerlijke ambt nooit als een beloning. Hij zag het als een verantwoordelijkheid.",
    "agora.marcus.p4": "Als jongeman verdiepte hij zich in de filosofie. Van al zijn leraren stond er één boven de rest: Junius Rusticus. Marcus zou Rusticus later in zijn Overpeinzingen bedanken omdat hij hem kennis liet maken met de leer van Epictetus, en omdat hij hem de waarde van eenvoud, nederigheid en zelfonderzoek liet zien.",
    "agora.marcus.p5": "Andere leermeesters volgden. Apollonius van Chalcedon. Sextus van Chaeronea. Fronto, de grote redenaar. Elk vormde zijn geest. Maar het was het Stoïcisme dat zijn karakter vormde.",
    "agora.marcus.p6": "Boven Rome uitkijkend, aanschouwde Marcus de grootste stad ter wereld. Toch werd zijn aandacht elders heen getrokken. Niet naar macht. Niet naar roem. Niet naar rijkdom. Maar naar een vraag die zijn hele leven zou leiden: hoe behoort een mens te leven?",
    "agora.marcus.alt": "Een jonge Marcus Aurelius, donker krullend haar, in roomwitte tuniek en diep paarse mantel met gouden borduursels, staat op een marmeren balkon en kijkt uit over de stad Rome bij zonsondergang. Koepels, zuilen en daken strekken zich uit tot de horizon onder een weidse gouden hemel.",

    "agora.marcusWar.numeral": "X",
    "agora.marcusWar.dates": "161 – 180 n.Chr. · De Marcomannische Oorlogen",
    "agora.marcusWar.title": "Marcus Aurelius",
    "agora.marcusWar.subtitle": "De Filosoof in Oorlog",
    "agora.marcusWar.note": "Marcus Aurelius · 121 – 180 n.Chr. · Romeins keizer · Stoïcijns filosoof · Bevelhebber van de Romeinse legioenen aan de Donaugrens · Auteur van de Overpeinzingen · Algemeen beschouwd als een van de wijste heersers uit de geschiedenis.",
    "agora.marcusWar.concepts": "Oorlog · Reflectie · Rede · Discipline · Deugd",
    "agora.marcusWar.q1": "De belemmering van het handelen brengt het handelen voort.",
    "agora.marcusWar.q2": "Wat in de weg staat, wordt de weg.",
    "agora.marcusWar.qattr": "Marcus Aurelius",
    "agora.marcusWar.p1": "Het keizerrijk was in oorlog. Aan de Donaugrens stonden de Romeinse legioenen onder onophoudelijke druk van stammen aan de overkant van de rivier. De winter was hard. De aanvoer onzeker. Duizenden waren afhankelijk van de beslissingen van één man. Die man was Marcus Aurelius.",
    "agora.marcusWar.p2": "Overdag voerde hij legers aan. ’s Nachts voerde hij zichzelf aan. In de stilte van zijn commandotent, slechts verlicht door olielampen en de gloed van verre wachtvuren, schreef Marcus persoonlijke overpeinzingen. Niet om gepubliceerd te worden. Niet voor de geschiedenis. Niet voor roem. Alleen voor zichzelf.",
    "agora.marcusWar.p3": "Deze aantekeningen zouden bekend worden als de Overpeinzingen. Een van de meest opmerkelijke boeken ooit geschreven. Niet omdat het leert hoe je een keizerrijk regeert, maar omdat het leert hoe je jezelf regeert.",
    "agora.marcusWar.p4": "Buiten stonden de legers van Rome. Binnen zat een man die probeerde goed te blijven.",
    "agora.marcusWar.p5": "De grootste strijd van Marcus Aurelius werd niet uitgevochten tegen de Marcomannen. Hij werd vanbinnen uitgevochten.",
    "agora.marcusWar.alt": "Marcus Aurelius, in bronzen harnas en karmozijnrode mantel, schrijft bij lamplicht in zijn commandotent aan de Donaugrens bij schemering. Door de open tentflap staan Romeinse legioenen in formatie langs de rivier onder een brandende oranje hemel; achter hem hangen kaarten en veldtekens aan de tentwand.",

    "agora.marcusEnd.numeral": "XI",
    "agora.marcusEnd.dates": "180 n.Chr. · De Laatste Dag",
    "agora.marcusEnd.title": "Marcus Aurelius",
    "agora.marcusEnd.subtitle": "De Laatste Zonsondergang",
    "agora.marcusEnd.note": "Marcus Aurelius · 121 – 180 n.Chr. · Romeins keizer · Stoïcijns filosoof · Auteur van de Overpeinzingen. Hij stierf tijdens de Donau-veldtochten in 180 n.Chr. De hier weergegeven laatste woorden behoren tot een latere historische overlevering en weerspiegelen de Stoïcijnse geest waarom Marcus Aurelius bekend werd.",
    "agora.marcusEnd.concepts": "Amor Fati · Aanvaarding · Natuur · Logos · Sterfelijkheid",
    "agora.marcusEnd.q1": "Handel niet alsof u tienduizend jaar zou leven.",
    "agora.marcusEnd.q2": "De dood hangt boven u.",
    "agora.marcusEnd.q3": "Wees, zolang u leeft en zolang het in uw macht ligt, goed.",
    "agora.marcusEnd.qattr": "Marcus Aurelius",
    "agora.marcusEnd.p1": "De oorlogen liepen ten einde. De veldtochten waren bijna voltooid. De last van het keizerrijk was vele jaren gedragen. Marcus Aurelius had een leven lang iets gediend dat groter was dan hijzelf. Nu wachtte hem een andere plicht.",
    "agora.marcusEnd.p2": "De jaren hadden zijn lichaam verzwakt. Verlies had hem vaak bezocht. Hij had pest doorstaan, hongersnood, oorlog, politieke intriges en de dood van velen die hij liefhad. Toch keerde hij door dit alles heen telkens terug naar dezelfde beginselen. Rede. Plicht. Deugd. Aanvaarding.",
    "agora.marcusEnd.p3": "Toen de dageraad naderde, rustte Marcus in zijn vertrek. Voorbij het balkon raakte het eerste licht de horizon. Het keizerrijk ontwaakte. Een nieuwe dag begon.",
    "agora.marcusEnd.p4": "Volgens latere overlevering riep Marcus een Praetoriaan tot zich. De jonge soldaat trad binnen. Marcus keek naar de opkomende zon, en daarna naar de man wiens leven nog vóór hem lag. En hij sprak woorden die door de eeuwen zijn blijven klinken.",
    "agora.marcusEnd.p5": "\u201CGa nu. Voor jou rijst de zon. Voor mij zinkt zij.\u201D",
    "agora.marcusEnd.p6": "Er was geen bitterheid. Geen vrees. Geen verzet. Slechts aanvaarding: de aanvaarding van een mens die begreep dat alles wat van de Natuur geleend is, op een dag moet worden teruggegeven.",
    "agora.marcusEnd.p7": "Voor Marcus was de dood geen vijand. Zij was deel van dezelfde orde die hem het leven had gegeven. Dezelfde Logos. Hetzelfde heelal. Hetzelfde eeuwige proces van worden en vergaan.",
    "agora.marcusEnd.p8": "De filosoof die een leven lang dit moment had voorbereid, ontmoette het nu kalm. Zoals hij geleefd had, zo ging hij heen.",
    "agora.marcusEnd.closing": "De zon rijst. De zon zinkt. De Logos blijft.",
    "agora.marcusEnd.alt": "Een oude Marcus Aurelius rust op een met zijde bekleed keizerlijk rustbed in een marmeren vertrek bij zonsondergang. Met open hand gebaart hij naar een Praetoriaanse wacht die bij het open balkon staat. Voorbij de zuilen strekt Rome zich uit tot de horizon onder een weidse gouden hemel; op een lage tafel staan een brandende olielamp en een lederen codex met de inscriptie \u2018Meditationes · Marcus Aurelius\u2019.",

    // ORPHAN — oude typografische hoofdstuk (behouden om sleutelverloop te voorkomen)
    "agora.s2.numeral": "IV",
    "agora.s2.title": "Het Systeem van de Rede",
    "agora.s2.figures": "Chrysippus",
    "agora.s2.themes": "Indrukken · Redeneren · Oordelen · Logica",
    "agora.s2.atmosphere": "Schriftrollen. Kaarten. Oude teksten.",
    "agora.s2.body": "Als Zeno de deur opende, bouwde Chrysippus het huis. Honderden verhandelingen, bijna alle verloren, over hoe een indruk een gedachte wordt, en een gedachte een daad. Het Stoïcisme wordt een complete manier van zien, een logica van het levend zijn.",

    // V — De Brug naar Rome
    "agora.s3.numeral": "VI",
    "agora.s3.title": "De Brug naar Rome",
    "agora.s3.dates": "185 – 51 v.Chr.",
    "agora.s3.figures": "Panaetius · Posidonius",
    "agora.s3.atmosphere": "Zeilen op de Middellandse Zee. Een vracht van ideeën zwaarder dan graan.",
    "agora.s3.body": "Het Stoïcisme steekt het water over. In Rhodos en Rome ontmoet het Romeins recht, Romeinse plicht, Romeins temperament. Het verzacht, verbreedt, wordt praktisch. Tegen de tijd dat het de senaatsvloer bereikt, is het niet langer alleen Grieks. Het is een manier om burger te zijn.",

    // VI — Seneca's Binnenplaats
    "agora.s4.numeral": "VII",
    "agora.s4.title": "Seneca's Binnenplaats",
    "agora.s4.dates": "4 v.Chr. – 65 n.Chr.",
    "agora.s4.themes": "Tijd · Rijkdom · Vriendschap · Sterfelijkheid",
    "agora.s4.atmosphere": "De tuin van een Romeinse villa. Een fontein. Olijfbomen. Een halfgeschreven brief op de tafel.",
    "agora.s4.body": "Leraar van een keizer, raadsman in het hart van de macht, en uiteindelijk door haar veroordeeld. Seneca schrijft als een man die weet dat hem gevraagd zal worden te sterven nog vóór het jaar voorbij is. Zijn brieven aan Lucilius zijn warm, bijna huiselijk, en onverschrokken. Ze blijven het dichtst dat de filosofie komt bij een wijze oudere vriend.",

    // VII — Epictetus
    "agora.s5.numeral": "VIII",
    "agora.s5.title": "Epictetus",
    "agora.s5.dates": "50 – 135 n.Chr.",
    "agora.s5.themes": "Vrijheid · Beheersing · Prohairesis · Het Innerlijke Citadel",
    "agora.s5.atmosphere": "Een eenvoudige kamer. Een lamp. Eén bank. Meer is niet nodig.",
    "agora.s5.body": "Geboren als slaaf, levenslang kreupel, verbannen door een keizer, en toch de vrijste geest van zijn tijd. Epictetus leert dat niemand buiten ons beheerst wat het meest van ons is: het gebruik dat wij maken van onze indrukken. De leer van prohairesis, het innerlijke vermogen tot kiezen, wordt het hart van de oefening.",

    // VIII — Marcus aan de Donau
    "agora.s6.numeral": "IX",
    "agora.s6.title": "Marcus aan de Donau",
    "agora.s6.dates": "121 – 180 n.Chr.",
    "agora.s6.subtitle": "De Keizer Die Voor Zichzelf Schreef",
    "agora.s6.atmosphere": "Een militaire tent aan de Donau. Het gouden uur. Een kampvuur buiten. Een olielamp binnen. Het gewicht van het rijk, en één enkel schriftje open op een houten tafel.",
    "agora.s6.body": "Hij bedoelde nooit dat wij deze bladzijden zouden lezen. De Overpeinzingen zijn private oefeningen: een man die bij kaarslicht oefent rechtvaardig te blijven onder onrechtvaardige druk, een open hand te houden als de wereld een gesloten vuist eist. De machtigste man ter wereld probeerde de meeste nachten eenvoudigweg een beter mens te zijn.",
    "agora.s6.pull": "Begin de ochtend door tot uzelf te zeggen: ik zal vandaag de bemoeizuchtige tegenkomen, de ondankbare…",

    // IX — De Bibliotheek
    "agora.s7.numeral": "XII",
    "agora.s7.title": "De Bibliotheek",
    "agora.s7.atmosphere": "Een lange klassieke hal. Boekenkasten tot het gewelf. Leestafels van donker hout. Stilte.",
    "agora.s7.body": "Een toekomstig thuis voor teksten, cursussen, oefeningen, reflecties, en de boeken die de oefening hebben gevormd. Voor nu worden de planken gevuld.",
    "agora.s7.shelf1": "Teksten",
    "agora.s7.shelf2": "Cursussen",
    "agora.s7.shelf3": "Oefeningen",
    "agora.s7.shelf4": "Reflecties",
    "agora.s7.shelf5": "Boekaanbevelingen",

    // X — De Zaal van het Licht
    "agora.s8.numeral": "XIII",
    "agora.s8.title": "De Zaal van het Licht",
    "agora.s8.atmosphere": "Een open ruimte. Witte steen. Hoge ramen. Lange banen gouden middaglicht die over de vloer vallen.",
    "agora.s8.line1": "Kennis kan worden opgeslagen.",
    "agora.s8.line2": "Informatie kan worden overgedragen.",
    "agora.s8.line3": "Intelligentie kan worden gesimuleerd.",
    "agora.s8.line4": "Maar wijsheid moet geleefd worden.",
    "agora.s8.line5": "Karakter heeft ons staande gehouden.",
    "agora.s8.line6": "Nu is het tijd voor wijsheid.",
    "agora.s8.line7": "Kennis is goedkoop.",
    "agora.s8.line8": "Wijsheid is van onschatbare waarde.",

    // XI — De Moderne Stoa
    "agora.s9.numeral": "XIV",
    "agora.s9.title": "De Moderne Stoa",
    "agora.s9.atmosphere": "Glas. Steen. Linnen. Stil licht. De architectuur van een kalm leven.",
    "agora.s9.body": "Een oude weg, ontmoet door een modern leven. De vragen worden niet langer gesteld op een markt in Athene. Ze worden gesteld in een keuken om zes uur 's ochtends, in een vergadering om twee uur, in een ziekenhuisgang, in de trein naar huis. Het Stoïcisme wordt teruggebracht naar kamers die het nooit verliet.",
    "agora.s9.topicsLabel": "Onderwerpen",
    "agora.s9.t1": "Relaties",
    "agora.s9.t2": "Gezondheid",
    "agora.s9.t3": "Werk",
    "agora.s9.t4": "Leiderschap",
    "agora.s9.t5": "Politiek",
    "agora.s9.t6": "Sociale media",
    "agora.s9.t7": "Kunstmatige intelligentie",
    "agora.s9.t8": "Betekenis",

    // XII — Virtue Guide
    "agora.s10.numeral": "XV",
    "agora.s10.title": "Virtue Guide",
    "agora.s10.body": "De les eindigt. De oefening begint.",
    "agora.s10.cta": "Open Virtue Guide",
    "agora.s10.note": "De Gidsen Zijn Altijd Bij U.",

    // XIII — De Uitgang
    "agora.s11.numeral": "XVI",
    "agora.s11.title": "De Uitgang",
    "agora.s11.atmosphere": "Een stenen pad dat de zuilengang verlaat. De zon laag. De dag stiller dan hij begon.",
    "agora.s11.line1": "De Gidsen Zijn Altijd Bij U.",
    "agora.s11.line2": "Tot U Terugkeert tot de Natuur en de Logos.",

    // expandable figures
    "agora.figures.label": "Toekomstige hoofdstukken",
    "agora.figures.note": "Elke figuur hieronder krijgt een eigen kamer in de Agora. De kamers bestaan nu; de woorden komen met de tijd.",
    "agora.fig.zeno": "Zeno van Citium",
    "agora.fig.cleanthes": "Cleanthes",
    "agora.fig.chrysippus": "Chrysippus",
    "agora.fig.panaetius": "Panaetius",
    "agora.fig.posidonius": "Posidonius",
    "agora.fig.seneca": "Seneca",
    "agora.fig.musonius": "Musonius Rufus",
    "agora.fig.epictetus": "Epictetus",
    "agora.fig.marcus": "Marcus Aurelius",
    "agora.fig.modern": "De Moderne Stoa",

    "agora.philosophy.title": "Waarom de Agora bestaat",
    "agora.philosophy.body": "Om wijsheid te bewaren, te cultiveren en door te geven. Technologie dient wijsheid, niet andersom. AI zou ooit de grootste bibliotheek van de mensheid kunnen worden, maar wijsheid blijft de hoogste verworvenheid van de mens.",
  },
};
